// Typed client for the Commerce Ops API. Every shape here mirrors the Go
// handlers in internal/httpapi.

import { progress } from './components/loadingProgress';
import { clearAll, markAllStale } from './queryCache';

export type DeploymentMode = 'existing_atomic' | 'managed_docker';
export type RollbackPolicy = 'manual_recovery' | 'automatic_code_rollback_when_safe';
export type MigrationOutcome = 'not_run' | 'backward_compatible' | 'incompatible' | 'unknown';

export type DeploymentStatus =
  | 'queued'
  | 'preparing'
  | 'building'
  | 'awaiting_db_approval'
  | 'critical_window'
  | 'verifying'
  | 'rolling_back'
  | 'succeeded'
  | 'rolled_back'
  | 'recovery_required'
  | 'failed'
  | 'cancelled';

/** Capability is one granular, server-enforced action an owner-managed
 * Role can grant to a non-owner member. An owner role always has every
 * capability implicitly and never needs one assigned. */
export type PermissionCapability =
  | 'manage_team'
  | 'manage_projects'
  | 'manage_deployments'
  | 'manage_backups'
  | 'manage_monitoring'
  | 'manage_terminal'
  | 'manage_distribution'
  | 'manage_erp_integrations';

export const ALL_CAPABILITIES: PermissionCapability[] = [
  'manage_team',
  'manage_projects',
  'manage_deployments',
  'manage_backups',
  'manage_monitoring',
  'manage_terminal',
  'manage_distribution',
  'manage_erp_integrations',
];

export const CAPABILITY_LABELS: Record<PermissionCapability, string> = {
  manage_team: 'Manage team (invite, remove members)',
  manage_projects: 'Manage projects (create, connections, atomic template)',
  manage_deployments: 'Manage deployments (start, transition, recovery)',
  manage_backups: 'Manage backups (create, policy)',
  manage_monitoring: 'Manage monitoring (smoke checks, alerts, webhook)',
  manage_terminal: 'Access web SSH terminal',
  manage_distribution: 'Manage product distribution (channels, credentials, mappings, publishing)',
  manage_erp_integrations: 'Manage ERP integrations (connections, credentials, product matching, prices)',
};

/** A capability's name where a list of them is read at a glance (the roles
 * table); `CAPABILITY_LABELS` says what each one allows, in role editing. */
export const CAPABILITY_SHORT_LABELS: Record<PermissionCapability, string> = {
  manage_team: 'Team',
  manage_projects: 'Projects',
  manage_deployments: 'Deployments',
  manage_backups: 'Backups',
  manage_monitoring: 'Monitoring',
  manage_terminal: 'Web terminal',
  manage_distribution: 'Product distribution',
  manage_erp_integrations: 'ERP integrations',
};

/** Role is a named, owner-managed bundle of capabilities. Owner is the
 * only built-in role (is_owner_role, always full access regardless of its
 * own stored capabilities, never editable or deletable) — every other
 * role is entirely owner-created and owner-configured. There is no
 * separate role-label-plus-permission-set layering: a role's own
 * capabilities are the complete, real grant. */
export interface Role {
  id: string;
  name: string;
  capabilities: PermissionCapability[];
  is_owner_role: boolean;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface Identity {
  kind: string;
  authenticated: boolean;
  label: string;
  notice: string;
  /** Set only for a real, authenticated session (kind === 'session'). */
  email?: string;
  role?: Role;
  /** Every capability this identity currently has — always every
   * Capability for an owner role, otherwise exactly what their role
   * grants. Informational for the UI only; every action is re-checked
   * server-side regardless. */
  capabilities?: PermissionCapability[];
  /** Set only for a real, authenticated session (kind === 'session'). */
  email_verified?: boolean;
}

export type SubscriptionStatus = 'none' | 'trialing' | 'active' | 'past_due' | 'canceled' | 'incomplete';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  is_local_development: boolean;
  /** The Role a new invitation gets when the inviter does not choose one
   * explicitly, and the only one a non-owner inviter may ever choose. */
  default_role_id: string;
  subscription_status?: SubscriptionStatus;
  subscription_current_period_end?: string | null;
  /** Set while Hestaworks has suspended the organization: every request
   * but the workspace summary is refused with `organization_suspended`. */
  suspended_at?: string | null;
  created_at: string;
  updated_at: string;
}

export type InvitationStatus = 'pending' | 'accepted' | 'revoked' | 'expired';

export interface Invitation {
  id: string;
  email: string;
  role_id: string;
  role_name: string;
  status: InvitationStatus;
  created_at: string;
  expires_at: string;
  accepted_at?: string;
  invite_url?: string;
}

export interface InvitationLookup {
  organization_name: string;
  email: string;
  role_name: string;
  account_exists: boolean;
}

export interface Member {
  user_id: string;
  name: string;
  email: string;
  role_id: string;
  role_name: string;
  created_at: string;
}

/** How much a personal access token may do through MCP. */
export type AccessTokenAccess = 'read' | 'operate' | 'admin';

/** A personal access token. The secret is never part of it. */
export interface AccessToken {
  id: string;
  name: string;
  display_prefix: string;
  access: AccessTokenAccess;
  capabilities: PermissionCapability[];
  expires_at: string;
  last_used_at?: string;
  created_at: string;
  /** Only on the organization-wide list (owner). */
  owner_name?: string;
  owner_email?: string;
}

export interface CreatedAccessToken {
  token: AccessToken;
  /** Shown once. */
  secret: string;
}

/** Store licence (CO-143). */
export type DataEntity = 'order' | 'invoice' | 'credit_memo' | 'customer' | 'search_query';
export type LicenceStatus = 'active' | 'grace' | 'expired' | 'none' | 'domain_not_licensed';
export type LicensedDomainStatus = 'active' | 'pending' | 'released';

export interface LicencePlan {
  code: string;
  name: string;
  entities: DataEntity[];
  production_domains: number;
  backfill_days: number;
  pii_retention_months: number;
  search_retention_months: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface LicenceOverrides {
  production_domains?: number;
  entities_add?: DataEntity[];
  entities_remove?: DataEntity[];
  backfill_days?: number;
}

export interface OrganizationLicence {
  organization_id: string;
  plan_code: string;
  source: 'manual' | 'stripe';
  manual_valid_until?: string;
  grace_days: number;
  lapsed_at?: string;
  revoked_at?: string;
  revoke_mode?: 'grace' | 'immediate';
  overrides: LicenceOverrides;
  created_at: string;
  updated_at: string;
}

export interface LicensedDomain {
  id: string;
  organization_id: string;
  installation_id: string;
  project_id: string;
  environment_id: string;
  /** The website of the store this slot is ('' for a store without websites). */
  website_code: string;
  /** The storefront hostname; '' when the store reports nothing public. */
  host: string;
  status: LicensedDomainStatus;
  first_seen_at: string;
  last_seen_at: string;
  decided_by?: string;
  decided_at?: string;
  project_name?: string;
  environment_name?: string;
}

export interface OrganizationLicenceView {
  licence: OrganizationLicence | null;
  plan: LicencePlan | null;
  status: LicenceStatus;
  reason?: string;
  grace_until?: string;
  domain_limit: number;
  entities: DataEntity[];
  domains: LicensedDomain[];
}

export interface BillingStatus {
  subscription_status: SubscriptionStatus;
  entitled: boolean;
  subscription_current_period_end: string | null;
  real: boolean;
}

export type ProjectArchitecture = 'traditional' | 'headless';

export type ApplicationType = 'magento_backend' | 'frontend' | 'wordpress';
/** The commerce platform a project runs (CO-60). Absent means Magento. */
export type ProjectPlatform = 'magento' | 'wordpress';
export type FrontendFramework = 'nextjs' | 'react_vite' | 'nuxt' | 'vue' | 'pwa_studio' | 'custom';
export type PackageManager = 'composer' | 'pnpm' | 'npm' | 'yarn';

export type FrontendEnvVarScope = 'build' | 'runtime' | 'build_and_runtime';
export type FrontendEnvVarVisibility = 'public' | 'secret';

/** Reference to a version of a secret in the secret store. Plaintext never travels with it. */
export interface SecretRef {
  secret_id: string;
  version: number;
}

/**
 * Where a variable's value comes from: "literal" (typed by the operator or
 * held in the secret store) or a semantic Commerce Ops value the deployment
 * resolves. The list is open — the API reports what this installation
 * provides — so new sources need no dashboard change.
 */
export type ValueSource = string;

export interface FrontendEnvironmentVariable {
  id: string;
  organization_id?: string;
  application_id: string;
  environment_id: string;
  name: string;
  /** Empty for secrets and for variables that reference a value source. */
  value: string;
  secret?: SecretRef;
  source: ValueSource;
  scope: FrontendEnvVarScope;
  visibility: FrontendEnvVarVisibility;
  required: boolean;
  created_at: string;
  updated_at: string;
}

/** One value source a variable can reference, with what it resolves to now. */
export interface AvailableValueSource {
  source: ValueSource;
  group: 'magento' | 'frontend' | 'environment' | 'component';
  label: string;
  description: string;
  requires_magento_binding: boolean;
  example: string;
  current_value?: string;
  resolved: boolean;
  reason?: string;
}

/** One variable of a component in one environment, with its value resolved. */
export interface EffectiveVariable {
  id: string;
  name: string;
  source: ValueSource;
  source_label: string;
  value: string;
  scope: FrontendEnvVarScope;
  visibility: FrontendEnvVarVisibility;
  build_time: boolean;
  runtime: boolean;
  required: boolean;
  resolved: boolean;
  reason?: string;
}

export interface ConfigIssue {
  severity: 'blocking' | 'warning';
  code: string;
  variable?: string;
  message: string;
}

export interface EffectiveEnvironment {
  environment_id: string;
  environment_name: string;
  application_id: string;
  component_name: string;
  framework: string;
  framework_label: string;
  framework_note?: string;
  variables: EffectiveVariable[];
  issues: ConfigIssue[];
  deployable: boolean;
}

export interface SourceRecommendation {
  source?: ValueSource;
  confidence?: 'high' | 'low';
  reason?: string;
}

/** One variable a repository declares, as detection found it. */
export interface DetectedVariable {
  name: string;
  found_in: string[];
  example_value?: string;
  scope: FrontendEnvVarScope;
  visibility: FrontendEnvVarVisibility;
  recommendation: SourceRecommendation;
  configured: boolean;
  current_source?: ValueSource;
}

export interface DetectedEnvironment {
  framework: string;
  framework_label: string;
  framework_note?: string;
  variables: DetectedVariable[];
  files_read: string[];
  reason?: string;
}

export type MagentoInstallationType = 'new_installation' | 'existing_magento_migration' | 'existing_magento_in_place';
export type MagentoDatabaseStrategy = 'create_managed' | 'reuse_existing_on_server' | 'external_database' | 'import_into_managed' | 'import_into_existing';
export type MagentoDatabaseSource = 'upload_dump' | 'existing_file_on_server' | 'commerceops_recovery_point' | 'object_storage';
export type MagentoCryptKeyStrategy = 'generate_new' | 'use_existing';
export type MagentoCacheStrategy = 'commerceops_managed' | 'reuse_existing' | 'external' | 'disabled';
export type MagentoSearchEngine = 'opensearch' | 'elasticsearch';
export type MagentoMediaStrategy = 'new_empty' | 'existing_server' | 'uploaded_archive' | 'object_storage_source' | 'recovery_point';

export interface MagentoDatabaseConfig {
  strategy: MagentoDatabaseStrategy;
  host?: string;
  port?: number;
  database_name?: string;
  username?: string;
  password_secret?: SecretRef;
  table_prefix?: string;
  tls_mode?: string;
  import_source?: MagentoDatabaseSource;
  import_source_metadata?: Record<string, string>;
  resource_id?: string;
}

export interface MagentoCryptKeyConfig {
  strategy: MagentoCryptKeyStrategy;
  secret?: SecretRef;
  verified: boolean;
}

export interface MagentoURLConfig {
  base_url: string;
  secure_base_url: string;
  backend_api_url?: string;
  /** Release file the nginx vhost includes; empty means Magento's nginx.conf.sample. */
  web_server_include?: string;
}

export interface MagentoAdminConfig {
  front_name: string;
  mode: string;
}

/** GraphQL settings Magento reads from env.php (CO-124): in production mode introspection is off unless allowed. */
export interface MagentoGraphQLConfig {
  allow_introspection: boolean;
}

export interface MagentoCacheConfig {
  strategy: MagentoCacheStrategy;
  same_instance_for_page: boolean;
  same_instance_for_session: boolean;
  default_cache_db: number;
  page_cache_db: number;
  session_db: number;
  host?: string;
  port?: number;
  password_secret?: SecretRef;
  tls: boolean;
  resource_id?: string;
}

/** How the catalog search configuration is decided: "discovered" (the
 * default) lets the analysed repository decide whether a search service
 * exists at all; "override" applies the operator's engine/strategy as
 * written. */
export type MagentoSearchMode = 'discovered' | 'override';
/** "application_provided": the engine lives inside the application (a MySQL
 * engine provided by a module), no external service. "paused": the
 * capability is unresolved or in conflict, nothing is provisioned. */
export type MagentoSearchStrategy = MagentoCacheStrategy | 'application_provided' | 'paused';

export interface MagentoSearchConfig {
  mode?: MagentoSearchMode;
  /** Discovered technology: opensearch, elasticsearch, mysql, custom, unknown. */
  provider?: string;
  /** Magento's catalog/search/engine value the deployment configures. */
  engine_identifier?: string;
  providing_module?: string;
  external_service_required?: boolean;
  engine: MagentoSearchEngine;
  strategy: MagentoSearchStrategy;
  host?: string;
  port?: number;
  scheme?: string;
  username?: string;
  password_secret?: SecretRef;
  index_prefix: string;
  resource_id?: string;
}

export interface MagentoQueueConfig {
  enabled: boolean;
  strategy: MagentoCacheStrategy;
  host?: string;
  port?: number;
  username?: string;
  password_secret?: SecretRef;
  virtual_host?: string;
  tls: boolean;
  consumers?: string[];
  resource_id?: string;
}

export interface MagentoMediaConfig {
  strategy: MagentoMediaStrategy;
  source_reference?: string;
}

export interface MagentoLocaleConfig {
  default_locale: string;
  additional_locales?: string[];
  timezone: string;
  base_currency?: string;
  default_display_currency?: string;
  allowed_currencies?: string[];
}

export interface MagentoCustomEnvVar {
  name: string;
  /** Plain variables carry their value; secret ones carry a reference. */
  value?: string;
  secret?: SecretRef;
  scope: string;
  visibility: string;
}

/** Status of one secret slot of a configuration, as reported by the API. */
export interface SecretStatus {
  configured: boolean;
  version?: number;
  updated_at?: string;
}

/** Write-only secret values sent with a configuration save; never echoed back. */
export interface MagentoRuntimeSecretValues {
  crypt_key?: string;
  database_password?: string;
  redis_password?: string;
  search_password?: string;
  queue_password?: string;
  custom_env_vars?: Record<string, string>;
}

export interface MagentoRuntimeConfigView {
  config: MagentoRuntimeConfiguration;
  secrets: Record<string, SecretStatus>;
}

export interface MagentoRuntimeConfiguration {
  id?: string;
  organization_id?: string;
  environment_id?: string;
  application_id?: string;
  version?: number;
  fingerprint?: string;
  installation_type: MagentoInstallationType;
  mage_mode: string;
  database: MagentoDatabaseConfig;
  crypt_key: MagentoCryptKeyConfig;
  urls: MagentoURLConfig;
  admin: MagentoAdminConfig;
  /** Absent on configurations saved before CO-124: introspection then follows the production default (off). */
  graphql?: MagentoGraphQLConfig;
  cache: MagentoCacheConfig;
  search: MagentoSearchConfig;
  queue: MagentoQueueConfig;
  media: MagentoMediaConfig;
  locale: MagentoLocaleConfig;
  custom_env_vars?: MagentoCustomEnvVar[];
  created_at?: string;
  updated_at?: string;
}

export interface ProjectApplication {
  id: string;
  organization_id: string;
  project_id: string;
  type: ApplicationType;
  application_type?: string;
  name: string;
  slug: string;
  repository_url: string;
  default_branch: string;
  framework: string;
  runtime: string;
  package_manager: string;
  deployment_strategy: string;
  build_script?: string;
  install_command?: string;
  output_runtime_type?: string;
  working_directory?: string;
  server_id?: string;
  backend_application_id?: string;
  /** Components this one needs at runtime (CO-48); never a deployment trigger. */
  depends_on?: string[];
  /** A disabled component keeps its configuration and history but is left out of deployments. */
  disabled?: boolean;
  /** package.json script that serves the built frontend (default: start). */
  start_script?: string;
  health_check?: ComponentHealthCheck | null;
  /** How this component's pages are invalidated and warmed (CO-49). */
  cache?: ComponentCacheConfig;
  /** Where the component is in its removal lifecycle (CO-49). */
  lifecycle?: ComponentLifecycle;
  removal_error?: string;
  removed_at?: string | null;
  environment_variables?: Record<string, string>;
  created_at: string;
  updated_at: string;
}

export interface ComponentHealthCheck {
  path: string;
  expected_status?: number;
  expected_content?: string;
  /** The storefront's own backend-dependent route; blue-green readiness requests it on the candidate. */
  backend_path?: string;
  /** Content the backend route must contain (defaults to the bound store view code). */
  backend_expected_content?: string;
}

// Component cache lifecycle and removal lifecycle (CO-49).
export type CacheProvider = 'none' | 'varnish' | 'cloudflare' | 'custom_endpoint';
export type CachePurgeStrategy = 'none' | 'changed_urls' | 'full_component';
export type DependentCachePolicy = 'never' | 'always';
export type ComponentLifecycle = 'active' | 'removing' | 'removal_failed' | 'removed';

export interface ComponentCacheConfig {
  provider: CacheProvider;
  purge_strategy: CachePurgeStrategy;
  automatic_purge: boolean;
  purge_after_backend_deploy: DependentCachePolicy;
  purge_aliases?: boolean;
  varnish_endpoint?: string;
  varnish_method?: string;
  cloudflare_zone_id?: string;
  /** The zone's name, recorded when the zone is picked or checked. When set, a purge names only hostnames inside it. */
  cloudflare_zone_name?: string;
  /** Request each purged hostname once through its public address after a Cloudflare purge and record cf-cache-status (CO-201). */
  verify_edge?: boolean;
  custom_endpoint_url?: string;
  custom_endpoint_header?: string;
  warmup_enabled: boolean;
  warmup_paths?: string[];
  warmup_sitemap?: boolean;
  warmup_sitemap_path?: string;
  warmup_max_urls?: number;
  /** How much slower than before a release the storefront may answer (CO-198). */
  latency_budget?: LatencyBudget;
}

export interface LatencyBudget {
  /** Turns the speed check off for the component. */
  disabled?: boolean;
  /** Slower means both limits are passed; absent means the defaults (50 %, 300 ms). */
  max_increase_percent?: number;
  min_increase_ms?: number;
}

export type DeploymentLatencyState = 'measuring' | 'ok' | 'slower' | 'unknown';

export interface DeploymentLatency {
  state: DeploymentLatencyState;
  before: { samples: number; median_ms: number };
  after: { samples: number; median_ms: number };
  budget: LatencyBudget;
  increase_ms: number;
  increase_percent: number;
  /** When the after side is complete (measuring only). */
  ready_at?: string;
}

export interface CacheTarget {
  hostname: string;
  canonical: boolean;
  urls?: string[];
}

export interface CachePlan {
  application_id: string;
  component_name: string;
  provider: CacheProvider;
  purge_strategy: CachePurgeStrategy;
  purge: boolean;
  warm: boolean;
  targets: CacheTarget[];
  /** Hostnames of the component that lie outside its recorded Cloudflare zone. Never purged. */
  outside_zone?: string[];
  warmup_urls?: string[];
  reason?: string;
}

export interface CacheInvalidationResult {
  application_id: string;
  component_name: string;
  provider: string;
  strategy?: string;
  hostnames: string[];
  target_count: number;
  urls?: string[];
  warmed?: number;
  skipped: boolean;
  reason?: string;
  duration_ms: number;
  status: string;
  /** Requests the provider needed; only present when more than one. */
  attempts?: number;
}

/** A query string from the parameters that are set. */
function queryOf(params: Record<string, string | undefined>): string {
  const q = Object.entries(params)
    .filter(([, v]) => v)
    .map(([k, v]) => `${k}=${encodeURIComponent(v as string)}`);
  return q.length ? `?${q.join('&')}` : '';
}

export type CloudflareTokenState = 'not_configured' | 'invalid' | 'insufficient_scope' | 'zone_not_found' | 'valid' | 'unknown';

export type CloudflarePermissionKey = 'zone_read' | 'cache_purge' | 'zone_settings_read' | 'dns_read';

/** One permission the token is expected to hold, in the words of Cloudflare's token editor, and what the check found. */
export interface CloudflarePermission {
  key: CloudflarePermissionKey;
  name: string;
  required: boolean;
  /** Cache Purge is always untestable: Cloudflare has no way to ask without purging. */
  state: 'granted' | 'missing' | 'unknown' | 'untestable';
}

/** What verifying a component's stored Cloudflare token found (CO-201). The token itself never travels. */
export interface CloudflareTokenCheck {
  application_id: string;
  component_name: string;
  state: CloudflareTokenState;
  zone?: { id: string; name: string; status: string; paused: boolean; plan?: string };
  /** Cloudflare's word for the token: active, expired, disabled. */
  token_status?: string;
  expires_at?: string;
  zone_read: 'ok' | 'denied' | 'not_found' | 'unknown';
  /** Always unverified: Cloudflare cannot test purge permission without purging. */
  purge_scope: 'unverified';
  permissions?: CloudflarePermission[];
  message?: string;
  checked_at: string;
}

/** One zone the component's stored token can read. */
export interface CloudflareZoneChoice {
  id: string;
  name: string;
  status: string;
  account?: string;
  /** The component's own hostnames inside this zone. */
  covers: string[];
}

export interface CloudflareZoneList {
  application_id: string;
  component_name: string;
  zones: CloudflareZoneChoice[];
  /** The zone that covers the most of the component's hostnames. */
  suggested_zone_id?: string;
  truncated: boolean;
  /** Why the list is empty when the token may not list zones. */
  unreadable?: string;
  checked_at: string;
}

export type CacheSafetyVerdict = 'safe' | 'cacheable' | 'cached' | 'unknown' | 'absent';

export interface CacheSafetyProbe {
  label: string;
  status?: number;
  cache_status?: string;
  age_seconds?: number;
  cache_control?: string;
  vary?: string;
  set_cookie?: boolean;
  error?: string;
}

export interface CacheSafetyRoute {
  hostname: string;
  path: string;
  class: 'checkout' | 'account' | 'session' | 'graphql' | 'admin';
  verdict: CacheSafetyVerdict;
  detail?: string;
  probes: CacheSafetyProbe[];
}

/** Whether the edge would cache the component's personalised routes. Read-only: no rule is created or changed. */
export interface CacheSafetyReport {
  application_id: string;
  component_name: string;
  verdict: 'safe' | 'warning' | 'unsafe' | 'unknown';
  routes: CacheSafetyRoute[];
  checked_at: string;
}

export interface ComponentRemovalResult {
  component: ProjectApplication;
  lifecycle: ComponentLifecycle;
  executions: string[];
  environments: string[];
  immediate: boolean;
  message: string;
}

export interface DetectedEnvVarTemplate {
  name: string;
  default_scope?: 'build' | 'runtime' | 'build_and_runtime';
  default_visibility?: 'public' | 'secret';
  example_value?: string;
  required?: boolean;
  description?: string;
  /** The Commerce Ops value recommended for this name, when one fits. The
   * repository owns the name; this only says what can fill it (CO-52). */
  source?: ValueSource;
}

export interface DetectedRequirementItem {
  name: string;
  status: 'passed' | 'warning' | 'info';
  message: string;
}

export interface FrontendAnalysis {
  framework: string;
  package_manager: string;
  node_version: string;
  router_type?: string;
  build_script?: string;
  start_script?: string;
  dev_script?: string;
  install_command?: string;
  available_scripts?: Record<string, string>;
  detected_scripts?: string[];
  has_dockerfile?: boolean;
  port?: number;
  deployment_supported?: boolean;
  working_directory?: string;
  detected_working_directories?: string[];
  nextjs_version?: string;
  output_mode?: string;
  detected_env_vars?: DetectedEnvVarTemplate[];
  detected_requirements?: DetectedRequirementItem[];
  identified_files?: string[];
  raw_warnings?: string[];
}

export type DeploymentScope = 'magento_only' | 'frontend_only' | 'combined' | 'selected' | 'all';

// Deployable components (CO-48).
export type ComponentHealth = 'healthy' | 'warning' | 'critical' | 'unknown' | 'disabled' | 'not_monitored';

export interface ComponentDomainView {
  id: string;
  hostname: string;
  role: string;
  canonical: boolean;
  tls_mode: string;
  certificate_status: string;
  dns_status: string;
  tls_observed_status: string;
  website_code?: string;
  store_view_code?: string;
}

export interface ComponentView {
  application: ProjectApplication;
  depends_on: string[];
  dependency_names: string[];
  dependents: string[];
  placed: boolean;
  server_id?: string;
  server_name?: string;
  deployment_strategy?: string;
  domains: ComponentDomainView[];
  website_code?: string;
  website_name?: string;
  store_view_code?: string;
  graphql_endpoint?: string;
  public_base_url?: string;
  active_release?: DeploymentRelease | null;
  last_deployment?: ProjectDeployment | null;
  deploying: boolean;
  cache: ComponentCacheConfig;
  cache_plan: CachePlan;
  lifecycle: ComponentLifecycle;
  removal_error?: string;
  health: ComponentHealth;
  monitors: number;
  issues: string[];
}

export interface ComponentsOverview {
  project_id: string;
  environment_id: string;
  environment: string;
  components: ComponentView[];
  backend_id?: string;
  websites: string[];
  issues: string[];
}

export type DeploymentGroupStatus = 'pending' | 'running' | 'completed' | 'completed_with_failures' | 'failed' | 'cancelled';

export interface DeploymentGroup {
  id: string;
  organization_id: string;
  project_id: string;
  environment_id: string;
  scope: DeploymentScope;
  status: DeploymentGroupStatus;
  /** Deployment IDs in the order they run. Optional: before CO-73 the API
   * sent this list as `order`. */
  deployment_order?: string[];
  failure_reason?: string;
  created_by: string;
  created_at: string;
  started_at?: string | null;
  completed_at?: string | null;
  /** Computed for finished groups: what each member component serves now. */
  current?: DeploymentGroupCurrent;
}

export interface DeploymentGroupCurrent {
  all_live: boolean;
  superseded: boolean;
  members: {
    application_id: string;
    application_name: string;
    group_outcome: string;
    live_now: 'this_release' | 'newer_release' | 'previous_release' | 'nothing';
    live_from?: string;
  }[];
}

export interface DeploymentGroupMember {
  deployment: ProjectDeployment;
  sequence: number;
  application_name: string;
  application_type: ApplicationType;
}

/**
 * One frontend framework Commerce Ops has an adapter for. The list comes
 * from the adapters themselves (CO-52 §20): the dashboard never keeps its
 * own table of frameworks, prefixes or exposure notes.
 */
export interface FrontendFrameworkAdapterView {
  framework: string;
  label: string;
  public_prefixes?: string[];
  public_at_build: boolean;
  public_note: string;
}

export interface FrontendRepositoryInspection {
  analysis: FrontendAnalysis;
  provenance: { fetch_method: string; provider?: string; repository?: string; branch?: string; commit_sha?: string; files_read?: string[]; files_absent?: string[] };
  fallback?: { reason: string; message: string } | null;
}

/** One frontend of a new headless project (CreateProjectFrontendInput). */
export interface CreateProjectFrontendInput {
  name: string;
  slug?: string;
  repository_url: string;
  /** Empty asks the provider for the repository's default branch. */
  default_branch?: string;
  framework?: string;
  package_manager?: string;
  runtime?: string;
  build_script?: string;
  start_script?: string;
  working_directory?: string;
  output_runtime_type?: string;
  install_command?: string;
}

export interface AddFrontendComponentInput {
  name: string;
  slug?: string;
  repository_url: string;
  default_branch?: string;
  framework?: string;
  runtime?: string;
  package_manager?: string;
  build_script?: string;
  install_command?: string;
  start_script?: string;
  output_runtime_type?: string;
  working_directory?: string;
  health_check?: ComponentHealthCheck | null;
  depends_on?: string[];
  environment_id?: string;
  server_id?: string;
  website_id?: string;
  store_view_id?: string;
  graphql_endpoint?: string;
  public_base_url?: string;
  primary_domain?: string;
  alias_domains?: string[];
  tls_mode?: string;
  acme_email?: string;
  env_vars?: { name: string; value: string; source?: ValueSource; scope?: string; visibility?: string; required?: boolean }[];
}

export interface AddFrontendComponentResult {
  application: ProjectApplication;
  placement?: EnvironmentPlacement | null;
  binding?: FrontendMagentoBinding | null;
  domains: EnvironmentDomain[];
  env_vars: FrontendEnvironmentVariable[];
  warnings: string[];
}

export interface UpdateComponentSettingsInput {
  name?: string;
  repository_url?: string;
  default_branch?: string;
  framework?: string;
  runtime?: string;
  package_manager?: string;
  build_script?: string;
  install_command?: string;
  start_script?: string;
  output_runtime_type?: string;
  working_directory?: string;
  health_check?: ComponentHealthCheck | null;
  clear_health_check?: boolean;
  depends_on?: string[];
  disabled?: boolean;
  cache?: ComponentCacheConfig;
}

export interface CoordinatedComponentInput {
  applicationId: string;
  branch?: string;
  commitSha?: string;
  commitMessage?: string;
  commitAuthor?: string;
}

export interface DeploymentComponentRelease {
  application_id?: string;
  application_type: ApplicationType;
  application_name: string;
  framework: string;
  release_id: string;
  release_tag: string;
  commit_sha: string;
  commit_ref: string;
  commit_message: string;
  commit_author: string;
  previous_release_id?: string;
  previous_commit_sha?: string;
  status: string;
  can_rollback: boolean;
  rollback_block_reason?: string;
}

export interface ResolvedBranch {
  application_id: string;
  repository_url: string;
  branch: string;
  commit_sha: string;
}

export interface Project {
  id: string;
  organization_id: string;
  name: string;
  slug: string;
  repository_url: string;
  architecture?: ProjectArchitecture;
  platform?: ProjectPlatform;
  frontend_repository_url?: string;
  frontend_framework?: string;
  frontend_package_manager?: string;
  frontend_runtime?: string;
  frontend_build_script?: string;
  frontend_working_directory?: string;
  frontend_output_runtime_type?: string;
  frontend_install_command?: string;
  created_at: string;
  updated_at: string;
}

export type PlanSafetyBoundary = 'before_critical_window' | 'critical_window' | 'post_deploy';
export type PlanStepState = 'pending' | 'running' | 'skipped' | 'succeeded' | 'failed';

export interface AtomicDeploymentConfig {
  application_root: string;
  release_root: string;
  shared_paths: string[] | null;
  php_binary: string;
  smoke_test_paths: string[] | null;
}

export type EnvironmentKind = 'production' | 'staging' | 'development' | 'preview';

export interface Environment {
  id: string;
  organization_id: string;
  project_id: string;
  name: string;
  slug: string;
  kind: EnvironmentKind;
  deployment_mode: DeploymentMode;
  rollback_policy: RollbackPolicy;
  atomic_config?: AtomicDeploymentConfig | null;
  backup_retention_days: number;
  /** Off by default. Only an owner can turn this on, and only after a fresh
   * verification of both connections at that moment. Nothing yet reads it
   * to change what a deployment does. */
  live_execution_enabled: boolean;
  /** True while a generation-1 managed_docker environment's credentials still
   * have to be imported into the secret store before it can deploy. */
  credential_import_pending?: boolean;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Environment hub: placements, resources, bindings, secrets
// ---------------------------------------------------------------------------

/** Who owns a resource: Commerce Ops created it, it is external, it was
 * detected on the host, or nobody knows. Only commerceops-owned resources
 * are ever mutated automatically. */
export type ResourceOwnership = 'commerceops' | 'external' | 'detected' | 'unknown';
export type ResourceKind =
  | 'mysql' | 'mariadb' | 'redis' | 'opensearch' | 'elasticsearch' | 'rabbitmq'
  | 'object_storage' | 'reverse_proxy' | 'docker_runtime' | 'php_runtime' | 'frontend_runtime' | 'varnish';
export type ResourceProvider = 'docker_compose_service' | 'host_service' | 'external_endpoint' | 'hetzner_managed_docker' | 'manual';
export type ResourceStatus = 'pending' | 'reachable' | 'unreachable' | 'unknown';
export type BindingRole = 'database' | 'cache_default' | 'cache_page' | 'session' | 'search' | 'queue' | 'media_storage' | 'http_cache';

export interface ResourceLocator {
  host?: string;
  port?: number;
  scheme?: string;
  socket?: string;
  service_name?: string;
  container_name?: string;
  network?: string;
  image?: string;
  username?: string;
  database_name?: string;
  virtual_host?: string;
  bucket?: string;
  endpoint?: string;
  compose_file?: string;
  compose_project?: string;
  tls?: boolean;
}

export interface ManagedResource {
  id: string;
  organization_id: string;
  environment_id: string;
  server_id?: string | null;
  kind: ResourceKind;
  name: string;
  ownership: ResourceOwnership;
  provider: ResourceProvider;
  status: ResourceStatus;
  locator: ResourceLocator;
  observed_locator?: ResourceLocator | null;
  credential_secret_id?: string | null;
  observed_version?: string;
  last_observed_at?: string | null;
  created_from: string;
  created_at: string;
  updated_at: string;
}

export interface ResourceDrift {
  resource_id: string;
  kind: ResourceKind;
  field: string;
  expected: string;
  observed: string;
}

export interface ManagedResourceView extends ManagedResource {
  drift?: ResourceDrift[];
}

export interface BindingOptions {
  database_name?: string;
  table_prefix?: string;
  redis_db?: number;
  index_prefix?: string;
  virtual_host?: string;
  prefix?: string;
}

export interface ResourceBinding {
  id: string;
  organization_id: string;
  environment_id: string;
  application_id: string;
  role: BindingRole;
  resource_id: string;
  options: BindingOptions;
  created_at: string;
  updated_at: string;
}

export interface EnvironmentPlacement {
  id: string;
  organization_id: string;
  environment_id: string;
  application_id: string;
  server_id: string;
  deployment_strategy: DeploymentStrategy;
  created_at: string;
  updated_at: string;
}

export interface EnvironmentActiveRelease {
  organization_id: string;
  environment_id: string;
  application_id: string;
  release_id: string;
  previous_release_id?: string | null;
  activated_by_deployment_id?: string | null;
  activated_at: string;
}

export interface ConvergenceMigrationNote {
  id: string;
  project_id?: string | null;
  environment_id?: string | null;
  subject: string;
  note: string;
  resolved_at?: string | null;
  created_at: string;
}

export interface SecretMetadata {
  id: string;
  organization_id: string;
  environment_id?: string | null;
  application_id?: string | null;
  name: string;
  kind: string;
  scope: string;
  current_version: number;
  created_by: string;
  created_at: string;
  updated_at: string;
}

// ── Environment readiness (CO-43): alerts, domains, backups, capacity, drift ──

export type AlertSeverity = 'warning' | 'critical';

export interface AlertEvent {
  id: string;
  organization_id: string;
  environment_id?: string;
  project_id?: string;
  kind: string;
  severity: AlertSeverity;
  dedupe_key: string;
  title: string;
  message: string;
  status: 'open' | 'resolved';
  occurrences: number;
  first_seen_at: string;
  last_seen_at: string;
  notified_at?: string;
  resolved_at?: string;
  resolved_by?: string;
  metadata?: Record<string, unknown>;
}

export type TLSMode = 'none' | 'acme' | 'external';

/** Where an environment's automatic first issuance stands (GAP-14). */
export type AutoIssueStatus = 'off' | 'idle' | 'waiting_dns' | 'issuing' | 'backoff' | 'rate_limited' | 'paused' | 'not_applicable';

export interface EnvironmentTLSSettings {
  environment_id: string;
  auto_issue: boolean;
  /** Certificates come from Let's Encrypt's staging CA and are never valid for browsers. */
  staging: boolean;
  status: AutoIssueStatus;
  attempts: number;
  dns_checks: number;
  failures: number;
  last_attempt_at?: string;
  next_attempt_at?: string;
  last_error?: string;
  /** Let's Encrypt refused: nothing is requested before this time. */
  blocked_until?: string;
  blocked_class?: string;
  /** The control plane's COMMERCE_OPS_TLS_AUTO_ISSUE. */
  global_enabled: boolean;
  /** Automatic issuance will act: the switch is on and so is the control plane's. */
  effective: boolean;
}

/** One DNS record an environment's hostname needs, with what DNS answers now. */
export interface DNSRequirement {
  hostname: string;
  type: string;
  value: string;
  purpose: string;
  observed?: string;
  status: 'ok' | 'missing' | 'mismatch' | 'unknown';
}
export type CertificateStatus = 'none' | 'placeholder' | 'issued' | 'issue_failed' | 'renewal_failed' | 'expired';

export type DomainPurpose = 'magento_storefront' | 'headless_storefront' | 'backend_api' | 'admin' | 'media' | 'alias';
export type DomainRoutingMode = 'website' | 'store' | 'none';

export interface EnvironmentDomain {
  id: string;
  organization_id: string;
  environment_id: string;
  application_id?: string;
  hostname: string;
  role: 'primary' | 'alias' | 'api' | 'media' | 'frontend' | 'admin';
  tls_mode: TLSMode;
  redirect_to_https: boolean;
  acme_email?: string;
  certificate_status: CertificateStatus;
  certificate_issuer?: string;
  certificate_not_after?: string;
  certificate_checked_at?: string;
  last_certificate_error?: string;
  /** The installed certificate comes from Let's Encrypt's staging CA: browsers reject it. */
  certificate_staging?: boolean;
  /** What the server itself holds (read through the agent), apart from `tls_observed_*`, which is what a visitor's connection shows. */
  origin_cert_status?: 'valid' | 'expired' | 'placeholder' | 'staging' | 'missing' | 'hostname_mismatch' | 'unknown';
  origin_cert_not_after?: string;
  origin_cert_issuer?: string;
  origin_cert_checked_at?: string;
  // Multi-site (CO-46).
  website_id?: string;
  store_view_id?: string;
  purpose: DomainPurpose | '';
  canonical: boolean;
  redirect_to_domain_id?: string;
  routing_mode: DomainRoutingMode | '';
  dns_status: 'unknown' | 'ok' | 'missing' | 'mismatch' | 'proxied';
  dns_observed?: string;
  dns_checked_at?: string;
  tls_observed_status: 'unknown' | 'valid' | 'invalid' | 'expired' | 'hostname_mismatch' | 'missing' | 'not_applicable';
  tls_observed_not_after?: string;
  tls_hostname_match?: boolean;
  verification_status: 'unknown' | 'passed' | 'failed' | 'warning';
  verification_checked_at?: string;
  verification_error?: string;
  created_at: string;
  updated_at: string;
}

// ── Magento multi-site topology (CO-46) ──────────────────────────────────────

export type TopologyStatus = 'active' | 'removed';

export interface MagentoWebsite {
  id: string;
  organization_id: string;
  environment_id: string;
  application_id: string;
  magento_website_id?: number;
  code: string;
  name: string;
  is_default: boolean;
  default_group_magento_id?: number;
  sort_order: number;
  status: TopologyStatus;
  source: 'discovered' | 'manual' | 'migrated';
  discovered_at?: string;
  last_seen_at?: string;
  created_at: string;
  updated_at: string;
}

export interface MagentoStore {
  id: string;
  organization_id: string;
  environment_id: string;
  website_id: string;
  magento_group_id?: number;
  code: string;
  name: string;
  root_category_id?: number;
  default_store_view_magento_id?: number;
  status: TopologyStatus;
  created_at: string;
  updated_at: string;
}

export interface MagentoStoreView {
  id: string;
  organization_id: string;
  environment_id: string;
  website_id: string;
  store_id: string;
  magento_store_id?: number;
  code: string;
  name: string;
  locale: string;
  is_active: boolean;
  is_default: boolean;
  sort_order: number;
  base_url: string;
  secure_base_url: string;
  base_url_scope: string;
  base_url_scope_code: string;
  base_currency: string;
  display_currency: string;
  use_store_in_url: boolean;
  status: TopologyStatus;
  created_at: string;
  updated_at: string;
}

export interface MagentoTopology {
  environment_id: string;
  application_id: string;
  websites: MagentoWebsite[];
  stores: MagentoStore[];
  store_views: MagentoStoreView[];
}

export interface FrontendMagentoBinding {
  id: string;
  organization_id: string;
  environment_id: string;
  application_id: string;
  website_id?: string;
  store_view_id?: string;
  graphql_endpoint: string;
  rest_endpoint?: string;
  public_base_url: string;
  media_url_strategy: 'magento' | 'frontend' | 'cdn';
  locale?: string;
  currency?: string;
  store_code_var?: string;
  graphql_endpoint_var?: string;
  public_url_var?: string;
  created_at: string;
  updated_at: string;
}

export type RouteDestination = 'magento' | 'frontend' | 'redirect' | 'backend';

export interface StorefrontRoute {
  hostname: string;
  purpose: DomainPurpose;
  destination: RouteDestination;
  run_type?: 'website' | 'store';
  run_code?: string;
  website_id?: string;
  website_code?: string;
  store_view_code?: string;
  frontend_application_id?: string;
  frontend_service?: string;
  redirect_to?: string;
  tls: boolean;
  canonical: boolean;
  domain_id: string;
}

export interface RoutingIssue {
  code: string;
  severity: 'blocking' | 'warning';
  hostname?: string;
  message: string;
}

export interface RoutingPlan {
  routes: StorefrontRoute[];
  issues: RoutingIssue[];
  strict_hosts: boolean;
}

export type TopologyChangeKind =
  | 'website_added' | 'website_removed' | 'website_code_changed' | 'website_renamed' | 'default_website_changed'
  | 'store_added' | 'store_removed' | 'store_changed'
  | 'store_view_added' | 'store_view_removed' | 'store_view_disabled' | 'store_view_enabled' | 'store_view_code_changed' | 'store_view_renamed' | 'store_view_moved'
  | 'base_url_changed' | 'locale_changed' | 'frontend_binding_broken' | 'domain_binding_broken';

export interface TopologyChange {
  kind: TopologyChangeKind;
  subject: string;
  expected?: string;
  observed?: string;
  severity: 'critical' | 'warning' | 'info';
  advice?: string;
  website_id?: string;
  store_id?: string;
  store_view_id?: string;
}

export interface TopologyDiff {
  changes: TopologyChange[];
  in_sync: boolean;
  level: 'ok' | 'warning' | 'critical';
  websites_discovered: number;
}

export interface TopologySnapshotSummary {
  id: string;
  discovered_at: string;
  discovered_by: string;
  fingerprint: string;
  imported_at?: string;
  partial: boolean;
  warnings?: string[];
  websites: number;
  store_views: number;
  source: string;
}

export interface DiscoveredTopology {
  discovered_at: string;
  websites: { id: number; default_group_id: number; name: string; code: string; sort_order: number; is_default: boolean }[];
  stores: { id: number; website_id: number; code?: string; name?: string; root_category_id?: number; default_store_view_id?: number }[];
  store_views: { id: number; website_id: number; group_id: number; name: string; code: string; sort_order: number; is_active: boolean; locale?: string; base_url?: string; secure_base_url?: string; base_url_scope?: string; base_url_scope_code?: string }[];
  partial: boolean;
  warnings?: string[];
  source: string;
}

export interface TopologyDiscovery {
  snapshot: TopologySnapshotSummary;
  discovered: DiscoveredTopology;
  diff: TopologyDiff;
}

export type VerificationScopeKind = 'installation' | 'website' | 'store_view' | 'frontend' | 'domain';

export interface VerificationScope {
  kind: VerificationScopeKind;
  label: string;
  check: string;
  website_id?: string;
  website_code?: string;
  store_view_id?: string;
  store_view_code?: string;
  hostname?: string;
  frontend_application_id?: string;
  frontend_name?: string;
}

export interface SiteHealthEntry {
  kind: VerificationScopeKind;
  label: string;
  status: 'healthy' | 'warning' | 'critical' | 'unknown' | 'disabled';
  detail?: string;
  website_id?: string;
  website_code?: string;
  hostname?: string;
  monitor_ids?: string[];
  checked_at?: string;
}

export interface WebsiteHealth {
  website_id: string;
  code: string;
  name: string;
  status: SiteHealthEntry['status'];
  sites: SiteHealthEntry[];
}

export interface SiteHealth {
  backend: SiteHealthEntry;
  websites: WebsiteHealth[];
  overall: SiteHealthEntry['status'];
  entries: SiteHealthEntry[];
}

export interface TopologyFrontendView {
  application: ProjectApplication;
  binding?: FrontendMagentoBinding | null;
  domains: EnvironmentDomain[];
  service_name?: string;
}

export interface TopologyWebsiteView {
  website: MagentoWebsite;
  stores: MagentoStore[];
  store_views: MagentoStoreView[];
  domains: EnvironmentDomain[];
  frontends: TopologyFrontendView[];
  health?: WebsiteHealth | null;
  canonical_hostname?: string;
  routes: StorefrontRoute[];
}

export interface MagentoTopologyView {
  environment_id: string;
  application_id?: string;
  installation: Record<string, unknown>;
  websites: TopologyWebsiteView[];
  unbound_domains: EnvironmentDomain[];
  unbound_frontends: TopologyFrontendView[];
  routing: RoutingPlan;
  drift?: TopologyDiff | null;
  last_discovery?: TopologySnapshotSummary | null;
  health?: SiteHealth | null;
  resource_levels: Record<string, 'installation' | 'website' | 'domain'>;
  multi_website: boolean;
  /** Only when Commerce Ops runs this Magento (CO-123). */
  internal_network?: TopologyInternalNetwork;
}

/** Addresses on the environment's Docker network: containers only, never browsers (CO-123). */
export interface TopologyInternalNetwork {
  network: string;
  magento: TopologyInternalService;
  graphql_url: string;
  rest_url: string;
  frontends: TopologyInternalService[];
}

export interface TopologyInternalService {
  application_id: string;
  name: string;
  service: string;
  port: number;
  url: string;
}

export interface WebsiteImpact {
  website_id: string;
  code: string;
  name: string;
  domains: string[];
  store_views: string[];
  frontends: string[];
}

export interface FrontendImpact {
  application_id: string;
  name: string;
  slug: string;
  domains: string[];
  website_code?: string;
  store_view_code?: string;
}

export interface CacheLayerImpact {
  layer: 'magento_application' | 'full_page_cache' | 'cdn' | 'frontend';
  scope: 'installation' | 'domain';
  targets?: string[];
  action: 'flushed_by_deployment' | 'operator_purge_required' | 'purged_by_deployment' | 'recreated_by_deployment';
  /** The cache provider that purges the layer, when the deployment does. */
  provider?: CacheProvider;
}

export interface DeploymentImpact {
  scope: DeploymentScope;
  installation: boolean;
  websites: WebsiteImpact[];
  frontends: FrontendImpact[];
  cache_layers: CacheLayerImpact[];
  shared_resources: string[];
  summary: string;
}

export interface BaseURLChange {
  path: string;
  scope: string;
  scope_code: string;
  target: string;
  website_id?: string;
  store_view_id?: string;
  current_value: string;
  current_scope: string;
  current_scope_code?: string;
  proposed_value: string;
  reason: string;
}

export interface BaseURLPlan {
  changes: BaseURLChange[];
  in_sync: boolean;
  warnings?: string[];
}

export interface DomainObservation {
  dns_status: EnvironmentDomain['dns_status'];
  dns_observed?: string;
  tls_status: EnvironmentDomain['tls_observed_status'];
  tls_not_after?: string;
  tls_hostname_match?: boolean;
  verification_status: EnvironmentDomain['verification_status'];
  verification_error?: string;
  https_redirect?: string;
  canonical_redirect?: string;
  canonical_resolved_to?: string;
  checked_at: string;
}

export interface DomainValidationResult {
  domain: EnvironmentDomain;
  observation: DomainObservation;
  details?: Record<string, unknown>;
  error?: string;
}

export interface RoutingInspection {
  owner: string;
  owned_by_commerceops: boolean;
  can_apply: boolean;
  plan: RoutingPlan;
  proposed_nginx_map: string;
  proposed_apache: string;
  gate_config?: string;
  magento_vhost?: string;
  server: Record<string, unknown>;
  note: string;
}

export interface SetEnvironmentDomainInput {
  hostname: string;
  role?: string;
  tls_mode?: TLSMode;
  redirect_to_https?: boolean;
  acme_email?: string;
  application_id?: string | null;
  purpose?: DomainPurpose;
  website_id?: string | null;
  store_view_id?: string | null;
  canonical?: boolean;
  redirect_to_domain_id?: string | null;
  routing_mode?: DomainRoutingMode;
}

export interface SetFrontendBindingInput {
  application_id: string;
  website_id?: string | null;
  store_view_id?: string | null;
  graphql_endpoint?: string;
  rest_endpoint?: string;
  public_base_url?: string;
  media_url_strategy?: FrontendMagentoBinding['media_url_strategy'];
  locale?: string;
  currency?: string;
  store_code_var?: string;
  graphql_endpoint_var?: string;
  public_url_var?: string;
}

/**
 * EnvironmentBackupStatus mirrors service.EnvironmentBackupStatus. The
 * policy has no on/off switch (a manual_only frequency means no schedule);
 * the attempt is a recovery point, which carries no error message.
 */
export interface EnvironmentBackupStatus {
  policy?: { id: string; frequency: string; preferred_hour_utc?: number; include_media?: boolean; restore_verification?: string } | null;
  last_successful_backup?: RecoveryPoint | null;
  last_backup_attempt?: RecoveryPoint | null;
  next_scheduled_at?: string | null;
  last_restore_verification_at?: string | null;
  last_restore_verified_point_id?: string | null;
  restore_verification_running: boolean;
  last_backup_verified: boolean;
  open_alerts: number;
}

export interface EnvironmentCapacity {
  checked_at: string;
  filesystems: Record<string, { freeBytes?: number; totalBytes?: number; usedBytes?: number; freePercent?: number }>;
  directories: Record<string, { bytes?: number; files?: number; error?: string }>;
  memory?: Record<string, unknown>;
  load?: Record<string, unknown>;
  thresholds: Record<string, number>;
  level: 'ok' | 'warning' | 'critical';
  advice: string[] | null;
  reclaimable: { path: string; reason: string; bytes?: number }[];
  reclaimable_bytes: number;
}

export interface DriftFinding {
  kind: 'release' | 'service' | 'volume' | 'file' | 'agent';
  subject: string;
  expected: string;
  observed: string;
  severity: 'critical' | 'warning' | 'info';
  advice: string;
}

export interface EnvironmentDrift {
  inspected_at: string;
  agent_version: string;
  latest_agent_version?: string;
  agent_outdated: boolean;
  active_release_tag?: string;
  observed_release_tag?: string;
  services: Record<string, { state?: string; health?: string; image?: string }>;
  findings: DriftFinding[];
  level: 'ok' | 'warning' | 'critical';
}

export interface EnvironmentOverview {
  environment: Environment;
  applications: ProjectApplication[];
  placements: EnvironmentPlacement[];
  active_releases: EnvironmentActiveRelease[];
  resources: ManagedResource[];
  bindings: ResourceBinding[];
  migration_notes: ConvergenceMigrationNote[];
}

/** AtomicPlanStep is one step of the fixed, versioned atomic template — either
 * a live preview (not persisted) or copied verbatim into a persisted plan. */
export interface AtomicPlanStep {
  sequence: number;
  key: string;
  label: string;
  safety_boundary: PlanSafetyBoundary;
}

/** AtomicTemplateResponse mirrors internal/httpapi.atomicTemplateResponse. It
 * is always a 200: an unavailable mode or a missing configuration are normal
 * states, never an error and never a fabricated plan. */
export interface AtomicTemplateResponse {
  available: boolean;
  reason?: string;
  configured: boolean;
  template_version?: string;
  config?: AtomicDeploymentConfig;
  steps?: AtomicPlanStep[];
}

export interface ConfigureAtomicTemplateInput {
  application_root: string;
  release_root: string;
  shared_paths: string[];
  php_binary: string;
  smoke_test_paths: string[];
}

/** DeploymentPlan is the immutable snapshot one deployment attempt started
 * with; DeploymentPlanStep additionally carries the durable per-step state
 * (always "pending" in this milestone: nothing executes yet). */
export interface DeploymentPlan {
  id: string;
  organization_id: string;
  project_id: string;
  environment_id: string;
  deployment_id: string;
  template_version: string;
  config: AtomicDeploymentConfig;
  created_at: string;
}

export interface DeploymentPlanStep extends AtomicPlanStep {
  id: string;
  organization_id: string;
  plan_id: string;
  state: PlanStepState;
  created_at: string;
}

export interface DeploymentPlanResponse {
  plan: DeploymentPlan;
  steps: DeploymentPlanStep[];
}

export type JobStatus = 'queued' | 'leased' | 'succeeded' | 'failed';

/** DeploymentJob is the durable worker queue's rehearsal status for one
 * deployment attempt. It only ever moves through validated storage methods
 * driven by internal/worker's dry-run engine — nothing here executes a real
 * command. Absent for a managed_docker attempt, which never gets one. */
export interface DeploymentJob {
  id: string;
  organization_id: string;
  deployment_id: string;
  status: JobStatus;
  attempts: number;
  lease_owner?: string;
  lease_expires_at?: string | null;
  last_error?: string;
  created_at: string;
  updated_at: string;
}

export type PreflightStatus =
  | 'pending'
  | 'passed'
  | 'warning'
  | 'awaiting_approval'
  | 'approved'
  | 'rejected'
  | 'failed'
  | 'no_upgrade_required';

export type RiskLevel = 'info' | 'low' | 'medium' | 'high' | 'critical' | 'unknown';

export type DatabaseOperationType =
  | 'create_table'
  | 'drop_table'
  | 'add_column'
  | 'drop_column'
  | 'modify_column'
  | 'add_index'
  | 'drop_index'
  | 'add_foreign_key'
  | 'drop_foreign_key'
  | 'raw_sql';

export type PatchType =
  | 'data_patch'
  | 'schema_patch'
  | 'patch_version'
  | 'legacy_upgrade_data'
  | 'legacy_upgrade_schema'
  | 'legacy_install_data'
  | 'legacy_install_schema';

export type PatchGitStatus = 'added' | 'modified' | 'untracked' | 'unknown';

export interface DatabaseChange {
  id: string;
  preflight_id: string;
  sequence: number;
  operation: DatabaseOperationType;
  table_name: string;
  column_name: string;
  risk: RiskLevel;
  description: string;
  raw_sql: string;
}

export interface MagentoUpgradeCode {
  id: string;
  preflight_id: string;
  module_name: string;
  patch_type: PatchType;
  file_path: string;
  git_status: PatchGitStatus;
}

export interface DatabasePreflight {
  id: string;
  organization_id: string;
  environment_id: string;
  deployment_id: string;
  status: PreflightStatus;
  upgrade_required: boolean;
  highest_risk: RiskLevel;
  approval_required: boolean;
  approved_at?: string | null;
  approved_by?: string;
  risk_summary: string;
  raw_dry_run_log?: string;
  execution_duration_ms: number;
  started_at: string;
  completed_at?: string | null;
  changes?: DatabaseChange[];
  patches?: MagentoUpgradeCode[];
}

export type BackupStatus = 'completed' | 'failed';
export type BackupSource = 'control_plane_postgres' | 'magento_mysql';

/** DatabaseBackup is a durable record of one real pg_dump (control_plane_postgres)
 * or one real mysqldump captured live over SSH (magento_mysql), encrypted and
 * uploaded to object storage. size_bytes and checksum describe the real
 * plaintext dump; both are zero/empty for a failed backup. */
export interface DatabaseBackup {
  id: string;
  organization_id: string;
  project_id: string;
  environment_id: string;
  label: string;
  status: BackupStatus;
  source: BackupSource;
  size_bytes: number;
  checksum: string;
  storage_key?: string;
  triggered_by: string;
  created_at: string;
  expires_at: string;
}

/** The redacted, non-secret evidence one restore verification produced: a
 * real pg_restore into a freshly created, throwaway database — never the
 * live one — with its real table count as proof. */
export interface RestoreVerifyResult {
  table_count: number;
}

/** The redacted, non-secret evidence one Magento MySQL restore
 * verification produced: a real restore into a freshly created, uniquely
 * named throwaway database on the same real remote MySQL server — never
 * the environment's own live database — with its real table count and
 * exact real total row count as proof. */
export interface MagentoRestoreVerifyResult {
  table_count: number;
  row_count: number;
}

/** MediaBackup is a durable record of one real capture of an
 * existing_atomic environment's own pub/media directory, encrypted and
 * uploaded to object storage. size_bytes and checksum describe the real
 * plaintext tar.gz archive; both are zero/empty for a failed backup. */
export interface MediaBackup {
  id: string;
  organization_id: string;
  project_id: string;
  environment_id: string;
  label: string;
  status: BackupStatus;
  size_bytes: number;
  checksum: string;
  storage_key?: string;
  triggered_by: string;
  created_at: string;
  expires_at: string;
}

/** The redacted, non-secret evidence one media restore verification
 * produced: a real, bounded, Zip Slip-safe local extraction of the
 * encrypted archive into a throwaway temporary directory, with the real
 * file and byte counts the extraction actually wrote. */
export interface MediaRestoreVerifyResult {
  file_count: number;
  total_bytes: number;
}

export interface SmokeCheck {
  id: string;
  organization_id: string;
  project_id: string;
  environment_id: string;
  path: string;
  label: string;
  expected_status: number;
  interval_minutes: number;
  next_run_at?: string | null;
  created_at: string;
}

export type MonitoringAlertStatus = 'open' | 'resolved';

export interface MonitoringAlert {
  id: string;
  organization_id: string;
  environment_id: string;
  smoke_check_id: string;
  status: MonitoringAlertStatus;
  message: string;
  opened_at: string;
  resolved_at?: string | null;
  resolved_by?: string;
}

export type SmokeCheckResultStatus = 'passed' | 'failed';

/** SmokeCheckResult is one run of a SmokeCheck. real is true only when
 * observed_status_code came from an actual HTTP request (a check whose
 * path is a full https:// URL) — see
 * docs/adr/0007-real-monitoring-and-webhooks.md. deployment_id is absent
 * for an ad hoc run. */
export interface SmokeCheckResult {
  id: string;
  organization_id: string;
  environment_id: string;
  smoke_check_id: string;
  deployment_id?: string | null;
  status: SmokeCheckResultStatus;
  real: boolean;
  observed_status_code: number;
  message: string;
  checked_at: string;
}

export type RecoveryAction = 'restored_from_backup' | 'verified_manual_fix' | 'accepted_data_loss';

/** RecoveryReconciliation is the durable record of how a human resolved one
 * attempt stuck in recovery_required. Recording it is what releases the
 * environment it was blocking. */
export interface RecoveryReconciliation {
  id: string;
  organization_id: string;
  deployment_id: string;
  action: RecoveryAction;
  backup_id?: string | null;
  notes: string;
  resolved_by: string;
  resolved_at: string;
}

/** RollbackDecision is a generated (never hand-authored) explanation of why
 * one deployment transition landed on a failure route. */
export interface RollbackDecision {
  id: string;
  organization_id: string;
  deployment_id: string;
  from_status: DeploymentStatus;
  to_status: DeploymentStatus;
  rollback_policy: RollbackPolicy;
  migration_outcome: MigrationOutcome;
  reason: string;
  decided_at: string;
}

export interface Release {
  id: string;
  organization_id: string;
  project_id: string;
  commit_sha: string;
  commit_ref: string;
  commit_message: string;
  created_at: string;
}

export interface Deployment {
  id: string;
  organization_id: string;
  project_id: string;
  environment_id: string;
  release_id: string;
  attempt_number: number;
  status: DeploymentStatus;
  deployment_mode: DeploymentMode;
  rollback_policy: RollbackPolicy;
  migration_outcome: MigrationOutcome;
  triggered_by: string;
  failure_reason: string;
  created_at: string;
  updated_at: string;
  finished_at: string | null;
}

export interface DeploymentEvent {
  id: string;
  deployment_id: string;
  sequence: number;
  from_status: DeploymentStatus | null;
  to_status: DeploymentStatus;
  migration_outcome: MigrationOutcome;
  message: string;
  occurred_at: string;
}

export interface DeploymentDetail {
  deployment: Deployment;
  release: Release;
  events: DeploymentEvent[];
  allowed_next_statuses: DeploymentStatus[];
  failure_target_now?: DeploymentStatus;
  /** The immutable atomic template snapshot this attempt started with. Absent
   * for a managed_docker attempt: this milestone never fabricates one. */
  plan?: DeploymentPlan;
  plan_steps?: DeploymentPlanStep[];
  /** The worker queue's rehearsal status for this attempt. Absent for a
   * managed_docker attempt, or an attempt started before the worker queue
   * existed. */
  job?: DeploymentJob;
  /** Explains, in order, why this attempt landed on any failure route it
   * reached. Empty for an attempt that never failed. */
  rollback_decisions?: RollbackDecision[];
  /** Set once a human has recorded how a recovery_required attempt was
   * resolved. Absent while the attempt is still blocking its environment. */
  reconciliation?: RecoveryReconciliation | null;
  /** The simulated checks run against this specific attempt, in order. */
  smoke_check_results?: SmokeCheckResult[];
}

export interface DeploymentSummary {
  deployment: Deployment;
  release: Release;
}

export type AuthKind = 'ssh_password' | 'ssh_private_key';

/** ServerConnection is the non-secret record of how an existing_atomic
 * environment would eventually be reached over SSH. There is no field here
 * that could hold the secret — the API never returns one. */
export interface ServerConnection {
  id: string;
  organization_id: string;
  project_id: string;
  environment_id: string;
  host: string;
  port: number;
  username: string;
  auth_kind: AuthKind;
  /** Always empty: a server connection's secret is never previewed, even by
   * its last four characters (see internal/domain/server_connection.go). */
  secret_last_four?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

/** ServerConnectionResponse mirrors internal/httpapi.serverConnectionResponse:
 * always a 200, an unavailable mode or a missing connection are normal
 * states, never an error and never a fabricated connection. */
export interface ServerConnectionResponse {
  available: boolean;
  reason?: string;
  configured: boolean;
  connection?: ServerConnection;
}

/** A read-only picture of the Cloudflare zone behind a component (CO-192). */
export interface CloudflareView {
  application_id: string;
  component_name: string;
  zone?: { id: string; name: string; status: string; paused: boolean; plan?: string };
  /** off, flexible, full or strict. */
  ssl_mode?: string;
  hostnames: { hostname: string; in_zone: boolean; proxied?: boolean; record_type?: string; covered?: boolean; purged: boolean }[];
  /** Parts the token may not read, by part, with the reason. */
  unreadable?: Record<string, string>;
  checked_at: string;
}

/** What the organization's alerts can reach (CO-196). The webhook URL itself is never returned. */
export interface AlertChannels {
  webhook_configured: boolean;
  /** Where the webhook points: the host only. */
  webhook_host?: string;
  email_configured: boolean;
  environment_webhooks: number;
  /** Open alerts that no channel has taken. */
  undelivered_open: number;
  last_delivered_at?: string;
  /** An alert raised now would reach someone. */
  reachable: boolean;
}

export interface AlertChannelTestResult {
  webhook: string;
  email: string;
}

/** Where the browser terminal of a server connects (CO-190). The key is never returned. */
export interface ServerTerminalAccess {
  server_id: string;
  host: string;
  port: number;
  username: string;
  auth_kind: 'ssh_private_key';
  /** The host key pinned when the key was saved. */
  host_key_fingerprint?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

/** Where a component placed on a server lives, for the terminal's shortcuts. */
export interface TerminalLocation {
  project_name: string;
  environment_name: string;
  component_name: string;
  component_type: string;
  /** The release serving now (the "current" link). */
  path: string;
  /** What survives releases: media, var, env files. */
  shared_path: string;
}

export interface ServerTerminalAccessResponse {
  configured: boolean;
  access?: ServerTerminalAccess;
}

export interface ConfigureServerTerminalInput {
  host: string;
  port: number;
  username: string;
  private_key: string;
}

export interface ConfigureServerConnectionInput {
  host: string;
  port: number;
  username: string;
  auth_kind: AuthKind;
  secret: string;
}

export type ServerConnectionVerificationReason = 'unreachable' | 'auth_failed' | 'unexpected_response' | 'host_key_changed';

/** The redacted, non-secret result of one real SSH verification attempt. A
 * failed verification is a normal outcome (reason set), not a thrown error. */
export interface ServerConnectionVerification {
  verified: boolean;
  reason?: ServerConnectionVerificationReason;
}

export type ManagedServerStatus = 'provisioning' | 'running' | 'failed' | 'deleted';

/** ManagedServer mirrors internal/domain.ManagedServer's real, dedicated
 * Hetzner Cloud server for a managed_docker environment — one active
 * server per organization (see docs/adr/0008-managed-server-provisioning.md).
 * GET returns a 404 (translated to ApiError "not_found") when the
 * environment has never had one provisioned; that is a normal state, not
 * an error, for a fresh managed_docker environment. */
export interface ManagedServer {
  id: string;
  organization_id: string;
  environment_id: string;
  hetzner_server_id: number;
  public_ipv4?: string;
  status: ManagedServerStatus;
  docker_version?: string;
  failure_reason?: string;
  magento_base_url?: string;
  magento_admin_username?: string;
  magento_admin_email?: string;
  magento_admin_created: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export type GitProvider = 'github' | 'gitlab' | 'bitbucket';

/** GitConnection is the non-secret record of a project's Git provider
 * connection. There is no field here that could hold the token. */
export interface GitConnection {
  id: string;
  organization_id: string;
  project_id: string;
  provider: GitProvider;
  account: string;
  /** The token's last four characters — a personal access token is
   * high-entropy, so this is a low-risk convenience, unlike a password. */
  secret_last_four?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface GitConnectionResponse {
  configured: boolean;
  connection?: GitConnection;
}

/** How a new project's repository is read: the organization's GitHub App,
 * or a token stored encrypted as the project's Git connection. The token
 * is sent once, when the project is created, and never returned. */
export type RepositoryAccessInput =
  | { method: 'github_app' }
  | { method: 'token'; provider: GitProvider; account: string; token: string };

export interface ConfigureGitConnectionInput {
  provider: GitProvider;
  account: string;
  token: string;
}

export type GitConnectionVerificationReason =
  | 'invalid_token'
  | 'repository_not_accessible'
  | 'rate_limited'
  | 'unexpected_error';

/** The redacted, non-secret result of one real, read-only verification call
 * against the connected Git provider. A failed verification is a normal
 * outcome (reason set), not a thrown error. */
export interface GitConnectionVerification {
  verified: boolean;
  reason?: GitConnectionVerificationReason;
  default_branch?: string;
  private?: boolean;
}

export interface TestServerConnectionResult {
  verified: boolean;
  reason?: string;
  message?: string;
  latency_ms?: number;
}

export interface InspectServerInput {
  host: string;
  port: number;
  username: string;
  auth_kind: AuthKind;
  secret: string;
  php_binary?: string;
  application_root?: string;
}

export interface VerifyGitProviderInput {
  provider: GitProvider;
  account: string;
  token: string;
  repository_url?: string;
}

export interface VerifyGitProviderResult {
  verified: boolean;
  reason?: string;
  default_branch?: string;
  private?: boolean;
  magento?: boolean;
}

export interface RemoteRepository {
  full_name: string;
  default_branch: string;
  private: boolean;
  updated: string;
  magento: boolean;
  branches: string[];
}

export interface GitHubApp {
  id: string;
  organization_id: string;
  app_id: number;
  app_slug: string;
  app_name: string;
  client_id: string;
  installation_id?: number;
  account: string;
  created_at: string;
  updated_at: string;
}

export interface GitHubAppStatusResponse {
  configured: boolean;
  installed: boolean;
  app?: GitHubApp;
}

export interface GitHubAppManifest {
  name: string;
  url: string;
  hook_attributes: { url: string; active: boolean };
  redirect_url: string;
  callback_urls: string[];
  setup_url: string;
  public: boolean;
  default_permissions: Record<string, string>;
  default_events: string[];
}

export interface GetGitHubAppManifestResponse {
  manifest: GitHubAppManifest;
  github_url: string;
}

export interface SaveManualGitHubAppInput {
  app_id: number;
  app_name?: string;
  app_slug?: string;
  client_id?: string;
  client_secret: string;
  webhook_secret: string;
  private_key: string;
  installation_id: number;
  account: string;
}


/** AuditEvent is a durable record of one connection-credential lifecycle
 * change. metadata never carries a secret value. */
export interface AuditEvent {
  id: string;
  organization_id: string;
  project_id?: string;
  actor: string;
  action: string;
  resource_type: string;
  resource_id: string;
  metadata: Record<string, unknown>;
  occurred_at: string;
}

export interface AuditEventListResponse {
  events: AuditEvent[];
}

export interface Overview {
  organization: Organization;
  project_count: number;
  environment_count: number;
  active_deployments: number;
  needing_recovery: number;
  /** How many rehearsal jobs are queued or currently leased by a worker —
   * zero whenever nothing is waiting to be dry-run. */
  worker_queue_depth: number;
  recent_deployments: DeploymentSummary[];
  mode_distribution: Record<string, number>;
  policy_distribution: Record<string, number>;
  /** Agent fleet: registered servers and how many have a connected agent. */
  server_count: number;
  servers_online: number;
  /** Agent-driven deployments (the current path) not yet in a terminal
   * status; `active_deployments` above counts older SSH-path attempts. */
  project_deployments_in_progress: number;
  recent_project_deployments: ProjectDeploymentSummary[];
}

export interface ProjectDeploymentSummary {
  deployment: ProjectDeployment;
  project_name: string;
  environment_name: string;
  environment_kind: string;
}

/** What a capability is for: Settings ▸ Platform lists them by group (CO-160). */
export type CapabilityGroup = 'projects' | 'servers' | 'deployments' | 'operations';

export interface Capability {
  key: string;
  name: string;
  group: CapabilityGroup;
  status: 'implemented' | 'not_implemented' | 'disabled';
  /** One sentence for the list. */
  summary: string;
  /** The short boundary a person should know, when there is one. */
  limitation?: string;
  /** The technical description, shown on request. */
  detail: string;
  /** Internal documents behind it (repository paths), shown on request. */
  references?: string[];
  milestone: number;
}

export interface CapabilitiesResponse {
  identity: Identity;
  environment: string;
  capabilities: Capability[];
}

export interface StatusOption {
  status: DeploymentStatus;
  terminal: boolean;
  owns_environment: boolean;
  allowed_next: DeploymentStatus[];
}

export interface DeploymentOptions {
  deployment_modes: DeploymentMode[];
  rollback_policies: RollbackPolicy[];
  migration_outcomes: MigrationOutcome[];
  statuses: StatusOption[];
}

export interface WorkspaceResponse {
  identity: Identity;
  organization: Organization;
}

/** AuthResponse is returned by register and login: the same shape as
 * WorkspaceResponse, since both answer "who am I and which organization". */
export type AuthResponse = WorkspaceResponse;

export interface RegisterInput {
  name: string;
  email: string;
  password: string;
  organization_name: string;
  organization_slug: string;
}

export interface LoginInput {
  email: string;
  password: string;
}

/** ApiError carries the machine-readable error shape of the API. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly field?: string;
  /** Structured, error-specific data for the few errors the UI acts on
   * (for example `server_in_use` carries the environments that still use
   * the server). Undefined for most errors. */
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, field?: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.field = field;
    this.details = details;
  }
}

/** The environments that keep a server from being removed
 * (`server_in_use`, HTTP 409). */
export interface ServerInUseDetails {
  server_id: string;
  server_name: string;
  environments: ServerDependentEnvironment[];
}

export interface ServerDependentEnvironment {
  environment_id: string;
  environment_name: string;
  environment_kind: string;
  project_id: string;
  project_name: string;
  /** "placed": an application of the environment is placed on the server;
   * "run_in_progress": a run targeting the server has not finished. */
  reasons: string[];
}

/** serverInUseDetails returns the structured details of a `server_in_use`
 * error, or null for any other error. */
export function serverInUseDetails(error: unknown): ServerInUseDetails | null {
  if (!(error instanceof ApiError) || error.code !== 'server_in_use') return null;
  const d = error.details as Partial<ServerInUseDetails> | undefined;
  if (!d || !Array.isArray(d.environments)) return null;
  return { server_id: d.server_id ?? '', server_name: d.server_name ?? '', environments: d.environments };
}

const csrfCookieName = 'commerce_ops_csrf';
const csrfHeaderName = 'X-CSRF-Token';

/** readCookie reads one cookie by name from document.cookie. The CSRF cookie
 * is deliberately not HttpOnly so this can read it back and echo it as a
 * header — the double-submit pattern the API's CSRF check relies on. */
function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }
  return null;
}

/** requestWithCsrfCookie is the one fetch wrapper every API call goes
 * through. The member dashboard and the staff console each have their own
 * CSRF cookie (the staff console's session is separate end to end), so the
 * cookie echoed back is a parameter. */
export async function requestWithCsrfCookie<T>(csrfCookie: string, path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? 'GET').toUpperCase();
  const mutating = method !== 'GET' && method !== 'HEAD';
  const endRequest = progress.requestStarted(mutating);
  try {
    const result = await sendRequest<T>(csrfCookie, path, init);
    // A change was made: whatever is cached may be out of date now. Nothing
    // on screen is removed; the next visit to any page asks the API again.
    if (mutating) markAllStale();
    return result;
  } finally {
    endRequest();
  }
}

async function sendRequest<T>(csrfCookie: string, path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? 'GET').toUpperCase();
  const isMutating = method !== 'GET' && method !== 'HEAD';
  const csrfToken = isMutating ? readCookie(csrfCookie) : null;

  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
        ...(csrfToken ? { [csrfHeaderName]: csrfToken } : {}),
        ...init?.headers,
      },
    });
  } catch {
    throw new ApiError(0, 'network_error', 'Commerce Ops could not be reached. Check your connection and try again.');
  }

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      // A proxy error page ("Bad gateway") or an HTML fallback: never show a
      // JSON parser error to people; keep the raw body for developers.
      console.error(`Commerce Ops API ${method} ${path} answered ${response.status} with a body that is not JSON:`, text.slice(0, 500));
      throw nonJsonResponseError(response.status);
    }
  }

  if (!response.ok) {
    const body = payload as { error?: { code?: string; message?: string; field?: string; details?: unknown } } | null;
    throw new ApiError(
      response.status,
      body?.error?.code ?? 'unexpected_error',
      body?.error?.message ?? `Request failed with status ${response.status}`,
      body?.error?.field,
      body?.error?.details,
    );
  }

  return payload as T;
}

/** The error for a response whose body is not JSON (CO-87). 502–504 mean a
 * proxy could not reach the API; anything else is an unexpected answer. */
export function nonJsonResponseError(status: number): ApiError {
  if (status === 502 || status === 503 || status === 504) {
    return new ApiError(status, 'api_unavailable', `Commerce Ops could not reach its API (HTTP ${status}). Try again in a moment.`);
  }
  return new ApiError(status, 'bad_response', `Commerce Ops received an answer it could not read (HTTP ${status}). Try again in a moment.`);
}

function request<T>(path: string, init?: RequestInit): Promise<T> {
  return requestWithCsrfCookie<T>(csrfCookieName, path, init);
}

/** A file the API generated from a POST: the bytes and the name it suggests. */
async function requestFile(path: string, body: unknown): Promise<{ blob: Blob; filename: string }> {
  const csrfToken = readCookie(csrfCookieName);
  let response: Response;
  try {
    response = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(csrfToken ? { [csrfHeaderName]: csrfToken } : {}) },
      body: JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'network_error', 'Commerce Ops could not be reached. Check your connection and try again.');
  }
  if (!response.ok) {
    type ErrorBody = { error?: { code?: string; message?: string; field?: string } } | null;
    let body = null as ErrorBody;
    try {
      body = (await response.json()) as ErrorBody;
    } catch {
      body = null;
    }
    throw new ApiError(response.status, body?.error?.code ?? 'unexpected_error', body?.error?.message ?? `Request failed with status ${response.status}`, body?.error?.field);
  }
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const filename = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? 'download';
  return { blob: await response.blob(), filename };
}

function post<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'POST', body: JSON.stringify(body) });
}

function put<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'PUT', body: JSON.stringify(body) });
}

function del<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'DELETE' });
}

/** Whether Commerce Ops runs in safe mode after a recovery (RES-006). */
export interface PlatformStatus {
  safe_mode: boolean;
  since?: string | null;
}

export const api = {
  getPlatform: () => request<PlatformStatus>('/api/v1/platform'),
  workspace: () => request<WorkspaceResponse>('/api/v1/workspace'),
  register: (input: RegisterInput) => post<AuthResponse>('/api/v1/auth/register', input),
  login: (input: LoginInput) => post<AuthResponse>('/api/v1/auth/login', input),
  // Signing out ends the session: nothing it cached may reach the next one.
  logout: () => post<{ signed_out: boolean }>('/api/v1/auth/logout', {}).finally(clearAll),
  capabilities: () => request<CapabilitiesResponse>('/api/v1/capabilities'),
  deploymentOptions: () => request<DeploymentOptions>('/api/v1/deployment-options'),
  overview: () => request<Overview>('/api/v1/overview'),

  listProjects: () => request<{ projects: Project[] }>('/api/v1/projects'),
  createProject: (input: {
    name: string;
    slug: string;
    repository_url: string;
    architecture?: ProjectArchitecture;
    platform?: ProjectPlatform;
    /** The branch the primary application deploys; empty means the
     * repository's own default branch. */
    default_branch?: string;
    /** The connection the repository was selected from (CO-69). The API
     * verifies the selection with it before anything is created. */
    repository_access?: RepositoryAccessInput;
    frontend_repository_url?: string;
    frontend_framework?: string;
    frontend_package_manager?: string;
    frontend_runtime?: string;
    frontend_build_script?: string;
    frontend_working_directory?: string;
    frontend_output_runtime_type?: string;
    frontend_install_command?: string;
    frontends?: CreateProjectFrontendInput[];
  }) => post<Project>('/api/v1/projects', input),
  getProject: (projectId: string) =>
    request<{ project: Project; environments: Environment[] }>(`/api/v1/projects/${projectId}`),
  listProjectApplications: (projectId: string, includeRemoved = false) =>
    request<{ applications: ProjectApplication[] }>(`/api/v1/projects/${projectId}/applications${includeRemoved ? '?include_removed=true' : ''}`),
  /** Which commit a branch of an application currently points at — one
   * read-only call to the Git provider through the project's connection. */
  // Deployable components (CO-48).
  getComponentsOverview: (projectId: string, environmentId?: string) =>
    request<ComponentsOverview>(`/api/v1/projects/${projectId}/components${environmentId ? `?environment_id=${encodeURIComponent(environmentId)}` : ''}`),
  inspectFrontendRepository: (projectId: string, repositoryUrl: string, branch: string) =>
    request<FrontendRepositoryInspection>(`/api/v1/projects/${projectId}/components/inspect-frontend-repository`, {
      method: 'POST',
      body: JSON.stringify({ repository_url: repositoryUrl, branch }),
    }),
  addFrontendComponent: (projectId: string, input: AddFrontendComponentInput) =>
    request<AddFrontendComponentResult>(`/api/v1/projects/${projectId}/components/frontends`, { method: 'POST', body: JSON.stringify(input) }),
  updateComponentSettings: (projectId: string, applicationId: string, input: UpdateComponentSettingsInput) =>
    request<ProjectApplication>(`/api/v1/projects/${projectId}/components/${applicationId}`, { method: 'PATCH', body: JSON.stringify(input) }),
  removeComponent: (projectId: string, applicationId: string) =>
    request<ComponentRemovalResult>(`/api/v1/projects/${projectId}/components/${applicationId}`, { method: 'DELETE' }),
  retryComponentRemoval: (projectId: string, applicationId: string) =>
    request<ComponentRemovalResult>(`/api/v1/projects/${projectId}/components/${applicationId}/removal/retry`, { method: 'POST', body: JSON.stringify({}) }),
  /** zoneId reads another zone with the component's own token, before it is saved. */
  getComponentCloudflare: (projectId: string, applicationId: string, environmentId?: string, zoneId?: string) =>
    request<CloudflareView>(`/api/v1/projects/${projectId}/components/${applicationId}/cloudflare${queryOf({ environment_id: environmentId, zone_id: zoneId })}`),
  /** Checks the stored token against Cloudflare (and, with zoneId, a zone that is not saved yet); the request carries no token. */
  verifyComponentCloudflare: (projectId: string, applicationId: string, environmentId?: string, zoneId?: string) =>
    request<CloudflareTokenCheck>(`/api/v1/projects/${projectId}/components/${applicationId}/cloudflare/verify${queryOf({ environment_id: environmentId, zone_id: zoneId })}`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  /** The zones the component's stored token can read. */
  listComponentCloudflareZones: (projectId: string, applicationId: string, environmentId?: string) =>
    request<CloudflareZoneList>(`/api/v1/projects/${projectId}/components/${applicationId}/cloudflare/zones${queryOf({ environment_id: environmentId })}`),
  /** Requests the component's personalised routes through the edge and reports whether it would cache them. */
  checkComponentCacheSafety: (projectId: string, applicationId: string, environmentId?: string, input?: { hostname?: string; paths?: string[] }) =>
    request<CacheSafetyReport>(`/api/v1/projects/${projectId}/components/${applicationId}/cache/safety${queryOf({ environment_id: environmentId })}`, {
      method: 'POST',
      body: JSON.stringify(input ?? {}),
    }),
  purgeComponentCache: (projectId: string, applicationId: string, environmentId?: string) =>
    request<CacheInvalidationResult>(
      `/api/v1/projects/${projectId}/components/${applicationId}/cache/purge${environmentId ? `?environment_id=${encodeURIComponent(environmentId)}` : ''}`,
      { method: 'POST', body: JSON.stringify({}) },
    ),
  listComponentDeployments: (projectId: string, applicationId: string, environmentId?: string) =>
    request<{ deployments: ProjectDeployment[]; component: ProjectApplication }>(
      `/api/v1/projects/${projectId}/components/${applicationId}/deployments${environmentId ? `?environment_id=${encodeURIComponent(environmentId)}` : ''}`,
    ),
  getDeploymentGroup,
  startDeploymentGroup,
  listDeploymentGroups,
  createCoordinatedDeployment,
  resolveApplicationBranch: (projectId: string, applicationId: string, branch: string) =>
    post<ResolvedBranch>(`/api/v1/projects/${projectId}/applications/${applicationId}/resolve-branch`, { branch }),
  getProjectApplication: (projectId: string, applicationId: string) =>
    request<ProjectApplication>(`/api/v1/projects/${projectId}/applications/${applicationId}`),
  createProjectApplication: (projectId: string, input: Partial<ProjectApplication>) =>
    post<ProjectApplication>(`/api/v1/projects/${projectId}/applications`, input),
  updateProjectApplication: (projectId: string, applicationId: string, input: Partial<ProjectApplication>) =>
    put<ProjectApplication>(`/api/v1/projects/${projectId}/applications/${applicationId}`, input),
  deleteProjectApplication: (projectId: string, applicationId: string) =>
    del<{ status: string }>(`/api/v1/projects/${projectId}/applications/${applicationId}`),
  updateFrontendBuildConfig: (
    projectId: string,
    applicationId: string,
    input: {
      packageManager?: string;
      buildScript?: string;
      outputRuntimeType?: string;
      workingDirectory?: string;
      nodeVersion?: string;
    },
  ) =>
    put<ProjectApplication>(
      `/api/v1/projects/${projectId}/applications/${applicationId}/build-config`,
      input,
    ),
  listFrontendEnvVars: (projectId: string, applicationId: string, environmentId?: string) =>
    request<{ variables: FrontendEnvironmentVariable[] }>(
      `/api/v1/projects/${projectId}/applications/${applicationId}/env-vars${
        environmentId ? `?environment_id=${encodeURIComponent(environmentId)}` : ''
      }`,
    ),
  /** The frontend frameworks Commerce Ops has an adapter for. */
  listFrontendFrameworks: () => request<{ frameworks: FrontendFrameworkAdapterView[] }>('/api/v1/frontend-frameworks'),
  setFrontendEnvVar: (
    projectId: string,
    applicationId: string,
    input: {
      environmentId?: string;
      name: string;
      value: string;
      source?: ValueSource;
      scope: FrontendEnvVarScope;
      visibility: FrontendEnvVarVisibility;
      required?: boolean;
    },
  ) =>
    post<FrontendEnvironmentVariable>(
      `/api/v1/projects/${projectId}/applications/${applicationId}/env-vars`,
      input,
    ),
  deleteFrontendEnvVar: (projectId: string, applicationId: string, varId: string) =>
    del<{ status: string }>(
      `/api/v1/projects/${projectId}/applications/${applicationId}/env-vars/${varId}`,
    ),
  /**
   * Analyse a frontend repository. Pass a repository URL to have Commerce
   * Ops read the real repository with the organization's Git credentials,
   * or files to analyse content you already hold. `reason` explains an
   * empty analysis; nothing is ever invented.
   */
  analyzeFrontendRepository: (input: { repositoryUrl?: string; branch?: string; files?: Record<string, string> }) =>
    post<{ analysis: FrontendAnalysis; reason?: string }>('/api/v1/projects/analyze-frontend-repository', {
      repository_url: input.repositoryUrl,
      branch: input.branch,
      files: input.files,
    }),
  /** Existing-store migrations of an environment (docs/EXISTING_STORE_MIGRATION.md). */
  listStoreMigrations: (environmentId: string) =>
    request<{ migrations: StoreMigration[] }>(`/api/v1/environments/${environmentId}/migrations`),
  /** Starts the read-only inspection of files already on the environment's server. */
  assessStoreMigration: (environmentId: string, input: AssessStoreMigrationInput) =>
    post<{ migration: StoreMigration }>(`/api/v1/environments/${environmentId}/migrations`, input),
  approveStoreMigration: (environmentId: string, migrationId: string, input: { crypt_key_confirmed: boolean; replace_existing: boolean }) =>
    post<{ migration: StoreMigration }>(`/api/v1/environments/${environmentId}/migrations/${migrationId}/approve`, input),
  importStoreMigration: (environmentId: string, migrationId: string) =>
    post<{ execution: ExecutionRun }>(`/api/v1/environments/${environmentId}/migrations/${migrationId}/import`, {}),
  getMagentoRuntimeConfig: (projectId: string, applicationId: string, environmentId?: string) =>
    request<MagentoRuntimeConfigView>(
      `/api/v1/projects/${projectId}/applications/${applicationId}/magento-config${
        environmentId ? `?environment_id=${encodeURIComponent(environmentId)}` : ''
      }`,
    ),
  /**
   * Saves the reference-only configuration of one environment. Secret values
   * travel separately, are stored in the secret store and never come back:
   * the response only reports whether each slot is configured.
   */
  saveMagentoRuntimeConfig: (
    projectId: string,
    applicationId: string,
    config: MagentoRuntimeConfiguration,
    secrets: MagentoRuntimeSecretValues = {},
    environmentId?: string,
  ) =>
    put<MagentoRuntimeConfigView>(
      `/api/v1/projects/${projectId}/applications/${applicationId}/magento-config${
        environmentId ? `?environment_id=${encodeURIComponent(environmentId)}` : ''
      }`,
      { config, secrets },
    ),
  validateMagentoRuntimeConfig: (
    projectId: string,
    applicationId: string,
    config: MagentoRuntimeConfiguration,
  ) =>
    post<{ valid: boolean; fingerprint: string }>(
      `/api/v1/projects/${projectId}/applications/${applicationId}/magento-config/validate`,
      { config },
    ),
  listEnvironments: (projectId: string) =>
    request<{ environments: Environment[] }>(`/api/v1/projects/${projectId}/environments`),
  getEnvironmentHealth: (environmentId: string, options: { live?: boolean } = {}) =>
    request<EnvironmentHealthView>(`/api/v1/environments/${environmentId}/health${options.live ? '?live=1' : ''}`),
  getProjectHealth: (projectId: string) => request<ProjectHealthSummary>(`/api/v1/projects/${projectId}/health`),
  /** The Magento storefront: Luma, Hyvä or headless (docs/HYVA_THEMES.md). */
  getMagentoStorefront: (projectId: string) => request<{ storefront: MagentoStorefront }>(`/api/v1/projects/${projectId}/storefront`),
  setMagentoStorefrontMode: (projectId: string, mode: StorefrontMode) =>
    put<{ storefront: MagentoStorefront }>(`/api/v1/projects/${projectId}/storefront`, { mode }),
  /** Varnish full page cache of a traditional Magento environment (CO-127). */
  getEnvironmentVarnish: (environmentId: string, live = false) =>
    request<EnvironmentVarnish>(`/api/v1/environments/${environmentId}/varnish${live ? '?live=1' : ''}`),
  runVarnishOperation: (environmentId: string, operation: VarnishOperation) =>
    request<{ execution: ExecutionRun }>(`/api/v1/environments/${environmentId}/varnish/operations`, {
      method: 'POST',
      body: JSON.stringify({ operation }),
    }),
  getManagedMagentoOperations: (environmentId: string) =>
    request<MagentoOperationsView>(`/api/v1/environments/${environmentId}/magento-operations/managed`),
  runManagedMagentoOperation: (environmentId: string, operation: 'reindex_all' | 'cache_clean' | 'cache_flush') =>
    post<{ execution: ExecutionRun }>(`/api/v1/environments/${environmentId}/magento-operations/managed`, { operation }),
  getWordPressRecovery: (environmentId: string) => request<WordPressRecoveryView>(`/api/v1/environments/${environmentId}/wordpress-recovery`),
  getRepositoryAccess: (projectId: string) => request<RepositoryAccess>(`/api/v1/projects/${projectId}/repository-access`),
  getChangesSinceLive: (projectId: string, applicationId: string, environmentId: string, commit: string) =>
    request<ChangesSinceLive>(
      `/api/v1/projects/${projectId}/applications/${applicationId}/changes?environment_id=${encodeURIComponent(environmentId)}&commit=${encodeURIComponent(commit)}`,
    ),
  previewDatabaseChanges: (environmentId: string, commitSha: string, branch: string) =>
    post<DatabaseChangePreview>(`/api/v1/environments/${environmentId}/database-change-previews`, { commit_sha: commitSha, branch }),
  listDatabaseChangePreviews: (environmentId: string) =>
    request<{ previews: DatabaseChangePreview[] }>(`/api/v1/environments/${environmentId}/database-change-previews`),
  getDatabaseChangePreview: (previewId: string) => request<DatabaseChangePreview>(`/api/v1/database-change-previews/${previewId}`),
  getOrganizationHealth: () => request<OrganizationHealth>('/api/v1/operational-health'),
  listActiveDeployments: () => request<{ deployments: ActiveDeployment[]; generated_at: string }>('/api/v1/active-deployments'),
  dismissAttention: (environmentId: string, items: { id: string; fingerprint?: string }[]) =>
    post<AttentionDismissResult>(`/api/v1/environments/${environmentId}/attention/dismissals`, { items }),
  restoreAttention: (environmentId: string, ids: string[] = []) =>
    post<{ restored: number }>(`/api/v1/environments/${environmentId}/attention/dismissals/restore`, { ids }),
  getEnvironmentOverview: (environmentId: string) =>
    request<EnvironmentOverview>(`/api/v1/environments/${environmentId}/overview`),
  listEnvironmentPlacements: (environmentId: string) =>
    request<{ placements: EnvironmentPlacement[] }>(`/api/v1/environments/${environmentId}/placements`),
  setEnvironmentPlacement: (environmentId: string, input: { applicationId: string; serverId: string; deploymentStrategy?: string }) =>
    put<EnvironmentPlacement>(`/api/v1/environments/${environmentId}/placements`, input),
  listEnvironmentResources: (environmentId: string) =>
    request<{ resources: ManagedResourceView[] }>(`/api/v1/environments/${environmentId}/resources`),
  registerEnvironmentResource: (
    environmentId: string,
    input: { kind: ResourceKind; name: string; ownership: ResourceOwnership; provider: ResourceProvider; locator: ResourceLocator; serverId?: string; credentialValue?: string },
  ) => post<ManagedResource>(`/api/v1/environments/${environmentId}/resources`, input),
  listApplicationBindings: (environmentId: string, applicationId: string) =>
    request<{ bindings: ResourceBinding[] }>(`/api/v1/environments/${environmentId}/applications/${applicationId}/bindings`),
  bindApplicationResource: (environmentId: string, applicationId: string, input: { role: BindingRole; resourceId: string; options?: BindingOptions }) =>
    put<ResourceBinding>(`/api/v1/environments/${environmentId}/applications/${applicationId}/bindings`, input),
  listEnvironmentSecrets: (environmentId: string) =>
    request<{ secrets: SecretMetadata[] }>(`/api/v1/environments/${environmentId}/secrets`),
  /** Stores or replaces a secret of one component. The value is write-only: the answer is metadata. */
  writeComponentSecret: (environmentId: string, applicationId: string, name: string, value: string) =>
    post<{ secret: SecretMetadata }>(`/api/v1/environments/${environmentId}/secrets`, { applicationId, name, value, kind: 'generic', scope: 'runtime' }),
  deleteSecret: (secretId: string) => del<{ deleted: boolean }>(`/api/v1/secrets/${secretId}`),
  listEnvironmentExecutions: (environmentId: string) =>
    request<{ executions: ExecutionRun[] }>(`/api/v1/environments/${environmentId}/executions`),
  bootstrapEnvironmentMonitors: (environmentId: string) =>
    post<{ monitors: unknown[] }>(`/api/v1/environments/${environmentId}/monitors/bootstrap`, {}),

  // Environment readiness (CO-43).
  listEnvironmentAlerts: (environmentId: string, openOnly = true) =>
    request<{ alerts: AlertEvent[] }>(`/api/v1/alert-events?environment_id=${encodeURIComponent(environmentId)}${openOnly ? '&status=open' : ''}&limit=50`),
  resolveAlert: (alertId: string) => post<{ alert: AlertEvent }>(`/api/v1/alert-events/${alertId}/resolve`, {}),
  listEnvironmentDomains: (environmentId: string) =>
    request<{ domains: EnvironmentDomain[] }>(`/api/v1/environments/${environmentId}/domains`),
  setEnvironmentDomain: (environmentId: string, input: SetEnvironmentDomainInput) =>
    put<{ domain: EnvironmentDomain }>(`/api/v1/environments/${environmentId}/domains`, input),
  deleteEnvironmentDomain: (environmentId: string, domainId: string) =>
    del<{ deleted: boolean }>(`/api/v1/environments/${environmentId}/domains/${domainId}`),
  validateEnvironmentDomains: (environmentId: string) =>
    post<{ results: DomainValidationResult[] }>(`/api/v1/environments/${environmentId}/domains/validate`, {}),
  applyEnvironmentIngress: (environmentId: string) =>
    post<{ execution: ExecutionRun }>(`/api/v1/environments/${environmentId}/ingress/apply`, {}),

  // Magento multi-site topology (CO-46).
  getMagentoTopology: (environmentId: string) =>
    request<{ topology: MagentoTopologyView }>(`/api/v1/environments/${environmentId}/magento-topology`),
  discoverMagentoTopology: (environmentId: string) =>
    post<{ discovery: TopologyDiscovery }>(`/api/v1/environments/${environmentId}/magento-topology/discover`, {}),
  importMagentoTopology: (environmentId: string, snapshotId?: string) =>
    post<{ topology: MagentoTopology; applied: TopologyDiff }>(`/api/v1/environments/${environmentId}/magento-topology/import`, snapshotId ? { snapshot_id: snapshotId } : {}),
  getMagentoTopologyDrift: (environmentId: string) =>
    request<{ drift: TopologyDiff | null; snapshot: TopologySnapshotSummary | null }>(`/api/v1/environments/${environmentId}/magento-topology/drift`),
  listFrontendBindings: (environmentId: string) =>
    request<{ bindings: FrontendMagentoBinding[] }>(`/api/v1/environments/${environmentId}/frontend-bindings`),
  setFrontendBinding: (environmentId: string, input: SetFrontendBindingInput) =>
    put<{ binding: FrontendMagentoBinding }>(`/api/v1/environments/${environmentId}/frontend-bindings`, input),
  deleteFrontendBinding: (environmentId: string, applicationId: string) =>
    del<{ deleted: boolean }>(`/api/v1/environments/${environmentId}/frontend-bindings/${applicationId}`),
  /** A component's effective configuration in one environment; secrets stay masked. */
  getComponentEnvironment: (environmentId: string, applicationId: string) =>
    request<{ environment: EffectiveEnvironment }>(
      `/api/v1/environments/${environmentId}/components/${applicationId}/environment`,
    ),
  /** The semantic values a variable of this component can reference. */
  getComponentValueSources: (environmentId: string, applicationId: string) =>
    request<{ sources: AvailableValueSource[] }>(
      `/api/v1/environments/${environmentId}/components/${applicationId}/value-sources`,
    ),
  /** Read the component's repository and report the variables it expects. */
  detectComponentEnvironment: (environmentId: string, applicationId: string) =>
    post<{ detection: DetectedEnvironment }>(
      `/api/v1/environments/${environmentId}/components/${applicationId}/environment/detect`,
      {},
    ),
  /** Create the reviewed variables of a detection. */
  importComponentEnvironment: (
    environmentId: string,
    applicationId: string,
    variables: {
      name: string;
      source?: ValueSource;
      value?: string;
      scope: FrontendEnvVarScope;
      visibility: FrontendEnvVarVisibility;
      required?: boolean;
    }[],
  ) =>
    post<{ imported: FrontendEnvironmentVariable[] }>(
      `/api/v1/environments/${environmentId}/components/${applicationId}/environment/import`,
      { variables },
    ),
  getMagentoBaseURLPlan: (environmentId: string) =>
    request<{ plan: BaseURLPlan }>(`/api/v1/environments/${environmentId}/magento-base-urls/plan`),
  applyMagentoBaseURLs: (environmentId: string, plan: BaseURLPlan) =>
    post<{ execution: ExecutionRun }>(`/api/v1/environments/${environmentId}/magento-base-urls/apply`, { plan }),
  getDeploymentImpact: (environmentId: string, scope: DeploymentScope, frontendApplicationId?: string) =>
    request<{ impact: DeploymentImpact }>(
      `/api/v1/environments/${environmentId}/deployment-impact?scope=${encodeURIComponent(scope)}${frontendApplicationId ? `&frontend_application_id=${encodeURIComponent(frontendApplicationId)}` : ''}`,
    ),
  getRollbackImpact: (deploymentId: string) => request<{ impact: DeploymentImpact }>(`/api/v1/project-deployments/${deploymentId}/rollback-impact`),
  getSiteHealth: (environmentId: string) => request<{ health: SiteHealth }>(`/api/v1/environments/${environmentId}/site-health`),
  inspectStorefrontRouting: (environmentId: string) =>
    request<{ routing: RoutingInspection }>(`/api/v1/environments/${environmentId}/storefront-routing`),
  applyStorefrontRouting: (environmentId: string) =>
    post<{ execution: ExecutionRun }>(`/api/v1/environments/${environmentId}/storefront-routing/apply`, {}),
  issueEnvironmentCertificate: (environmentId: string, options: { staging?: boolean } = {}) =>
    post<{ execution: ExecutionRun }>(`/api/v1/environments/${environmentId}/certificate/issue`, options),
  renewEnvironmentCertificate: (environmentId: string) =>
    post<{ execution: ExecutionRun }>(`/api/v1/environments/${environmentId}/certificate/renew`, {}),
  getEnvironmentTLSSettings: (environmentId: string) =>
    request<{ settings: EnvironmentTLSSettings }>(`/api/v1/environments/${environmentId}/tls-settings`),
  setEnvironmentTLSSettings: (environmentId: string, input: { auto_issue: boolean; staging: boolean }) =>
    put<{ settings: EnvironmentTLSSettings }>(`/api/v1/environments/${environmentId}/tls-settings`, input),
  getDNSRequirements: (environmentId: string) =>
    request<{ records: DNSRequirement[]; note: string }>(`/api/v1/environments/${environmentId}/dns-requirements`),
  getEnvironmentBackupStatus: (environmentId: string) =>
    request<{ backup_status: EnvironmentBackupStatus }>(`/api/v1/environments/${environmentId}/backup-status`),
  listProjectCapabilities: (projectId: string) =>
    request<{ capabilities: StoredApplicationCapability[] }>(`/api/v1/projects/${projectId}/capabilities`),
  inspectCatalogSearchRuntime: (environmentId: string) =>
    post<{ inspection: CatalogSearchRuntimeInspection }>(`/api/v1/environments/${environmentId}/catalog-search/inspect`, {}),
  getEnvironmentCapacity: (environmentId: string) =>
    request<{ capacity: EnvironmentCapacity }>(`/api/v1/environments/${environmentId}/capacity`),
  cleanupEnvironment: (environmentId: string) =>
    post<{ execution: ExecutionRun }>(`/api/v1/environments/${environmentId}/cleanup`, {}),
  getEnvironmentDrift: (environmentId: string) =>
    request<{ drift: EnvironmentDrift }>(`/api/v1/environments/${environmentId}/drift`),
  supportBundleURL: (environmentId: string) => `/api/v1/environments/${environmentId}/support-bundle?download=1`,

  createEnvironment: (
    projectId: string,
    input: {
      name: string;
      slug: string;
      /** Stated explicitly: no word list can guess what a customer calls
       *  "Preprod Blue" or "Warehouse EU #17". */
      kind: EnvironmentKind;
      deployment_mode: DeploymentMode;
      rollback_policy: RollbackPolicy;
    },
  ) => post<Environment>(`/api/v1/projects/${projectId}/environments`, input),

  getEnvironment: (environmentId: string) => request<Environment>(`/api/v1/environments/${environmentId}`),
  setLiveExecution: (environmentId: string, enabled: boolean, confirmName?: string) =>
    put<Environment>(`/api/v1/environments/${environmentId}/live-execution`, { enabled, confirm_name: confirmName ?? '' }),
  listDeployments: (environmentId: string) =>
    request<{ deployments: DeploymentSummary[] }>(`/api/v1/environments/${environmentId}/deployments`),
  startDeployment: (
    environmentId: string,
    input: { commit_sha: string; commit_ref: string; commit_message: string },
  ) => post<DeploymentDetail>(`/api/v1/environments/${environmentId}/deployments`, input),

  getAtomicTemplate: (environmentId: string) =>
    request<AtomicTemplateResponse>(`/api/v1/environments/${environmentId}/atomic-template`),
  configureAtomicTemplate: (environmentId: string, input: ConfigureAtomicTemplateInput) =>
    put<Environment>(`/api/v1/environments/${environmentId}/atomic-template`, input),

  getServerConnection: (environmentId: string) =>
    request<ServerConnectionResponse>(`/api/v1/environments/${environmentId}/server-connection`),
  configureServerConnection: (environmentId: string, input: ConfigureServerConnectionInput) =>
    put<ServerConnectionResponse>(`/api/v1/environments/${environmentId}/server-connection`, input),
  verifyServerConnection: (environmentId: string) =>
    post<ServerConnectionVerification>(`/api/v1/environments/${environmentId}/server-connection/verify`, undefined),
  deleteServerConnection: (environmentId: string) =>
    del<{ deleted: boolean }>(`/api/v1/environments/${environmentId}/server-connection`),

  // getManagedServer rejects with ApiError code "not_found" when
  // environmentId has never had one provisioned — callers treat that as
  // the normal "nothing provisioned yet" state, not a failure.
  getManagedServer: (environmentId: string) =>
    request<ManagedServer>(`/api/v1/environments/${environmentId}/managed-server`),
  // Really deletes the real Hetzner server — the only way to free the
  // organization's one-server slot. Owner-only.
  deprovisionManagedServer: (serverId: string) =>
    del<{ deleted: boolean }>(`/api/v1/managed-servers/${serverId}`),

  getGitConnection: (projectId: string) =>
    request<GitConnectionResponse>(`/api/v1/projects/${projectId}/git-connection`),
  configureGitConnection: (projectId: string, input: ConfigureGitConnectionInput) =>
    put<GitConnectionResponse>(`/api/v1/projects/${projectId}/git-connection`, input),
  verifyGitConnection: (projectId: string) =>
    post<GitConnectionVerification>(`/api/v1/projects/${projectId}/git-connection/verify`, undefined),
  deleteGitConnection: (projectId: string) =>
    del<{ deleted: boolean }>(`/api/v1/projects/${projectId}/git-connection`),

  testServerConnection: (input: ConfigureServerConnectionInput) =>
    post<TestServerConnectionResult>('/api/v1/servers/test-connection', input),
  inspectServer: (input: InspectServerInput) =>
    post<ServerAuditReport>('/api/v1/servers/inspect', input),
  verifyGitProvider: (input: VerifyGitProviderInput) =>
    post<VerifyGitProviderResult>('/api/v1/git-providers/verify', input),
  listGitProviderRepositories: (input: { provider: GitProvider; account: string; token: string }) =>
    post<{ repositories: RemoteRepository[] }>('/api/v1/git-providers/repositories', input),

  getGitHubApp: () => request<GitHubAppStatusResponse>('/api/v1/github/app'),
  getGitHubAppManifest: (input?: { app_name?: string; organization_name?: string }) =>
    post<GetGitHubAppManifestResponse>('/api/v1/github/app/manifest', input ?? {}),
  exchangeGitHubAppCode: (code: string) =>
    post<GitHubApp>('/api/v1/github/app/exchange', { code }),
  saveGitHubAppInstallation: (installation_id: number, account: string) =>
    post<GitHubApp>('/api/v1/github/app/installation', { installation_id, account }),
  deleteGitHubApp: () => del<void>('/api/v1/github/app'),
  saveManualGitHubApp: (input: SaveManualGitHubAppInput) =>
    post<GitHubApp>('/api/v1/github/app/manual', input),
  /** What deleting the project would remove and what blocks it (owner only). */
  getProjectDeletion: (projectId: string) => request<ProjectDeletionPreview>(`/api/v1/projects/${projectId}/deletion`),
  /** Deletes the project from Commerce Ops; `confirm` must be its slug. Nothing on any server changes. */
  deleteProject: (projectId: string, confirm: string) =>
    request<null>(`/api/v1/projects/${projectId}`, { method: 'DELETE', body: JSON.stringify({ confirm }) }),
  /** Repositories readable through this project's Git connection, else the organization's GitHub App. */
  listProjectRepositories: (projectId: string) =>
    request<ProjectRepositories>(`/api/v1/projects/${projectId}/repositories`),
  listGitHubAppRepositories: () =>
    request<{ repositories: RemoteRepository[] }>('/api/v1/github/app/repositories'),

  listAuditEvents: () => request<AuditEventListResponse>('/api/v1/audit-events'),

  getDeployment: (deploymentId: string) => request<DeploymentDetail>(`/api/v1/deployments/${deploymentId}`),
  getDeploymentPlan: (deploymentId: string) =>
    request<DeploymentPlanResponse>(`/api/v1/deployments/${deploymentId}/plan`),
  getDatabasePreflight: (deploymentId: string) =>
    request<DatabasePreflight>(`/api/v1/deployments/${deploymentId}/db-preflight`),
  approveDatabasePreflight: (deploymentId: string, input?: { note?: string }) =>
    post<DatabasePreflight>(`/api/v1/deployments/${deploymentId}/db-preflight/approve`, input ?? {}),
  rejectDatabasePreflight: (deploymentId: string, input?: { note?: string; reason?: string }) =>
    post<DatabasePreflight>(`/api/v1/deployments/${deploymentId}/db-preflight/reject`, input ?? {}),
  transitionDeployment: (
    deploymentId: string,
    input: { to_status: DeploymentStatus; migration_outcome?: MigrationOutcome; message?: string },
  ) => post<DeploymentDetail>(`/api/v1/deployments/${deploymentId}/transitions`, input),
  reconcileRecovery: (
    deploymentId: string,
    input: { action: RecoveryAction; backup_id?: string; notes: string },
  ) =>
    post<{ reconciliation: RecoveryReconciliation; deployment: Deployment }>(
      `/api/v1/deployments/${deploymentId}/recovery`,
      input,
    ),

  listDatabaseBackups: (environmentId: string) =>
    request<{ backups: DatabaseBackup[] }>(`/api/v1/environments/${environmentId}/backups`),
  createMagentoDatabaseBackup: (environmentId: string, input: { label: string }) =>
    post<DatabaseBackup>(`/api/v1/environments/${environmentId}/magento-backups`, input),
  verifyMagentoDatabaseBackupRestore: (environmentId: string, backupId: string) =>
    post<MagentoRestoreVerifyResult>(
      `/api/v1/environments/${environmentId}/magento-backups/${backupId}/verify-restore`,
      undefined,
    ),
  setBackupPolicy: (environmentId: string, input: { retention_days: number }) =>
    put<Environment>(`/api/v1/environments/${environmentId}/backup-policy`, input),

  listMediaBackups: (environmentId: string) =>
    request<{ backups: MediaBackup[] }>(`/api/v1/environments/${environmentId}/media-backups`),
  createMediaBackup: (environmentId: string, input: { label: string }) =>
    post<MediaBackup>(`/api/v1/environments/${environmentId}/media-backups`, input),
  verifyMediaBackupRestore: (environmentId: string, backupId: string) =>
    post<MediaRestoreVerifyResult>(
      `/api/v1/environments/${environmentId}/media-backups/${backupId}/verify-restore`,
      undefined,
    ),

  listSmokeChecks: (environmentId: string) =>
    request<{ checks: SmokeCheck[] }>(`/api/v1/environments/${environmentId}/smoke-checks`),
  createSmokeCheck: (environmentId: string, input: { path: string; label: string; expected_status: number }) =>
    post<SmokeCheck>(`/api/v1/environments/${environmentId}/smoke-checks`, input),
  deleteSmokeCheck: (checkId: string) => del<{ deleted: boolean }>(`/api/v1/smoke-checks/${checkId}`),
  runSmokeChecks: (environmentId: string, input: { deployment_id?: string } = {}) =>
    post<{ results: SmokeCheckResult[] }>(`/api/v1/environments/${environmentId}/smoke-checks/run`, input),
  listSmokeCheckResults: (environmentId: string) =>
    request<{ results: SmokeCheckResult[] }>(`/api/v1/environments/${environmentId}/smoke-check-results`),

  // Account lifecycle.
  requestEmailVerification: () => post<{ sent: boolean }>('/api/v1/auth/request-email-verification', undefined),
  verifyEmail: (token: string) => post<{ verified: boolean }>('/api/v1/auth/verify-email', { token }),
  requestPasswordReset: (email: string) =>
    post<{ sent: boolean }>('/api/v1/auth/request-password-reset', { email }),
  resetPassword: (token: string, password: string) =>
    post<AuthResponse>('/api/v1/auth/reset-password', { token, password }),
  changePassword: (input: { current_password: string; new_password: string }) =>
    post<{ changed: boolean }>('/api/v1/auth/change-password', input),
  updateProfile: (input: { name: string }) =>
    put<{ identity: Identity; user: { id: string; email: string; name: string } }>('/api/v1/auth/profile', input),

  // Invitations and member (ACL) management.
  inviteMember: (input: { email: string; role_id?: string }) =>
    post<Invitation>('/api/v1/invitations', input),
  listInvitations: () => request<{ invitations: Invitation[] }>('/api/v1/invitations'),
  revokeInvitation: (invitationId: string) => del<Invitation>(`/api/v1/invitations/${invitationId}`),
  getInvitation: (token: string) => request<InvitationLookup>(`/api/v1/invitations/by-token/${token}`),
  acceptInvitation: (token: string, input: { name?: string; password?: string }) =>
    post<AuthResponse>(`/api/v1/invitations/by-token/${token}/accept`, input),
  listMembers: () => request<{ members: Member[] }>('/api/v1/members'),
  updateMemberRole: (userId: string, roleId: string) =>
    put<{ user_id: string; role_id: string }>(`/api/v1/members/${userId}/role`, { role_id: roleId }),
  removeMember: (userId: string) => del<{ removed: boolean }>(`/api/v1/members/${userId}`),
  listAccessTokens: () => request<{ tokens: AccessToken[] }>('/api/v1/access-tokens'),
  createAccessToken: (input: { name: string; access: AccessTokenAccess; capabilities: PermissionCapability[]; expires_at?: string }) =>
    post<CreatedAccessToken>('/api/v1/access-tokens', input),
  revokeAccessToken: (tokenId: string) => del<AccessToken>(`/api/v1/access-tokens/${tokenId}`),
  revokeAllAccessTokens: () => post<{ revoked: number }>('/api/v1/access-tokens/revoke-all', {}),
  listOrganizationAccessTokens: () => request<{ tokens: AccessToken[] }>('/api/v1/organization/access-tokens'),
  revokeOrganizationAccessToken: (tokenId: string) => del<AccessToken>(`/api/v1/organization/access-tokens/${tokenId}`),

  // Owner-managed roles: Owner is the only built-in role, every other
  // role is owner-created and owner-configured.
  listRoles: () => request<{ roles: Role[] }>('/api/v1/roles'),
  createRole: (input: { name: string; capabilities: PermissionCapability[] }) =>
    post<Role>('/api/v1/roles', input),
  updateRole: (id: string, input: { name: string; capabilities: PermissionCapability[] }) =>
    put<Role>(`/api/v1/roles/${id}`, input),
  deleteRole: (id: string) => del<{ deleted: boolean }>(`/api/v1/roles/${id}`),
  setDefaultRole: (roleId: string) => put<Organization>('/api/v1/default-role', { role_id: roleId }),

  // Billing.
  createCheckoutSession: () =>
    post<{ checkout_url: string; mock: boolean }>('/api/v1/billing/checkout', undefined),
  billingStatus: () => request<BillingStatus>('/api/v1/billing'),
  licence: () => request<OrganizationLicenceView>('/api/v1/licence'),
  releaseLicensedDomain: (domainId: string) =>
    post<OrganizationLicenceView>(`/api/v1/licence/domains/${encodeURIComponent(domainId)}/release`, undefined),
  completeMockCheckout: (customerEmail: string, clientReferenceId?: string) =>
    post<{ completed: boolean }>('/api/v1/billing/mock-checkout/complete', {
      customer_email: customerEmail,
      client_reference_id: clientReferenceId ?? '',
    }),

  // Staging Sync and Operational Health Diagnostics.
  syncMagentoDatabase: (environmentId: string, input: SyncMagentoDatabaseInput) =>
    post<SyncMagentoDatabaseResult>(`/api/v1/environments/${environmentId}/sync-database`, input),
  getMagentoHealth: (environmentId: string) =>
    request<MagentoHealthReport>(`/api/v1/environments/${environmentId}/magento-health`),
  getAlertChannels: () => request<AlertChannels>('/api/v1/alert-channels'),
  setAlertChannels: (webhookUrl: string) => put<AlertChannels>('/api/v1/alert-channels', { webhook_url: webhookUrl }),
  testAlertChannels: () => post<AlertChannelTestResult>('/api/v1/alert-channels/test', undefined),
  getServerTerminalAccess: (serverId: string) => request<ServerTerminalAccessResponse>(`/api/v1/servers/${serverId}/terminal-access`),
  configureServerTerminalAccess: (serverId: string, input: ConfigureServerTerminalInput) =>
    put<ServerTerminalAccessResponse>(`/api/v1/servers/${serverId}/terminal-access`, input),
  deleteServerTerminalAccess: (serverId: string) => del<{ deleted: boolean }>(`/api/v1/servers/${serverId}/terminal-access`),
  getServerTerminalLocations: (serverId: string) => request<{ locations: TerminalLocation[] }>(`/api/v1/servers/${serverId}/terminal-locations`),
  getServerTerminalWebSocketURL: (serverId: string, cols = 80, rows = 24) => {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}/api/v1/servers/${serverId}/terminal/ws?cols=${cols}&rows=${rows}`;
  },
  getTerminalWebSocketURL: (environmentId: string, cols = 80, rows = 24) => {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}/api/v1/environments/${environmentId}/terminal/ws?cols=${cols}&rows=${rows}`;
  },

  // Core / Phase 1 Operations
  getServerAudit: (environmentId: string) =>
    request<ServerAuditReport>(`/api/v1/environments/${environmentId}/server-audit`),
  getSecuritySweep: (environmentId: string) =>
    request<SecurityReport>(`/api/v1/environments/${environmentId}/security-sweep`),
  getMagentoOperations: (environmentId: string) =>
    request<MagentoOperationsSummary>(`/api/v1/environments/${environmentId}/magento-operations`),
  triggerReindex: (environmentId: string, indexerId = 'all') =>
    post<OperationResult>(`/api/v1/environments/${environmentId}/magento-operations/reindex`, { indexer_id: indexerId }),
  triggerCronRun: (environmentId: string) =>
    post<OperationResult>(`/api/v1/environments/${environmentId}/magento-operations/cron-run`, {}),
  runCdnAudit: (environmentId: string, storefrontBaseUrl = '') =>
    post<CdnAuditReport>(`/api/v1/environments/${environmentId}/cdn-audit`, { storefront_base_url: storefrontBaseUrl }),
  runPerfBenchmark: (environmentId: string, storefrontBaseUrl = '', paths: string[] = ['/']) =>
    post<PerformanceBenchmarkReport>(`/api/v1/environments/${environmentId}/perf-benchmark`, { storefront_base_url: storefrontBaseUrl, paths }),
  flushCache: (environmentId: string, cacheType = 'all') =>
    post<CacheFlushResult>(`/api/v1/environments/${environmentId}/cache/flush`, { cache_type: cacheType }),
  purgeCdn: (environmentId: string, targetUrl: string, varnishHost = '') =>
    post<CacheFlushResult>(`/api/v1/environments/${environmentId}/cache/purge-cdn`, { target_url: targetUrl, varnish_host: varnishHost }),
  getMagentoConfigAudit: (environmentId: string) =>
    request<MagentoConfigAuditReport>(`/api/v1/environments/${environmentId}/magento-config-audit`),
  remediateMagentoConfig: (environmentId: string, action: string) =>
    post<ConfigRemediationResult>(`/api/v1/environments/${environmentId}/magento-config-audit/remediate`, { action }),

  // Level 3 Business Integration & ERP Monitoring
  listIntegrationMonitors: (environmentId: string) =>
    request<{ monitors: IntegrationMonitor[] }>(`/api/v1/environments/${environmentId}/integrations`).then((r) => r.monitors),
  createIntegrationMonitor: (environmentId: string, input: CreateIntegrationMonitorInput) =>
    post<IntegrationMonitor>(`/api/v1/environments/${environmentId}/integrations`, input),
  updateIntegrationMonitor: (environmentId: string, monitorId: string, input: CreateIntegrationMonitorInput) =>
    put<IntegrationMonitor>(`/api/v1/environments/${environmentId}/integrations/${monitorId}`, input),
  deleteIntegrationMonitor: (environmentId: string, monitorId: string) =>
    del<void>(`/api/v1/environments/${environmentId}/integrations/${monitorId}`),
  discoverIntegrations: (environmentId: string) =>
    request<{ integrations: DiscoveredIntegration[] }>(`/api/v1/environments/${environmentId}/integrations/discovered`).then((r) => r.integrations),
  syncIntegrationStatuses: (environmentId: string) =>
    post<{ monitors: IntegrationMonitor[] }>(`/api/v1/environments/${environmentId}/integrations/sync`, {}).then((r) => r.monitors),
  getDeploymentRegressions: (environmentId: string) =>
    request<{ alerts: DeploymentRegressionAlert[] }>(`/api/v1/environments/${environmentId}/integrations/regressions`).then((r) => r.alerts),
  listIntegrationRuns: (environmentId: string, monitorId: string, limit = 20) =>
    request<{ runs: IntegrationRun[] }>(`/api/v1/environments/${environmentId}/integrations/${monitorId}/runs?limit=${limit}`).then((r) => r.runs),
  recordIntegrationRun: (environmentId: string, monitorId: string, input: Partial<IntegrationRun>) =>
    post<IntegrationRun>(`/api/v1/environments/${environmentId}/integrations/${monitorId}/runs`, input),
  restartQueueConsumer: (environmentId: string, consumerName: string) =>
    post<OperationResult>(`/api/v1/environments/${environmentId}/integrations/restart-consumer`, { consumer_name: consumerName }),
  listIntegrationTemplates: (environmentId: string) =>
    request<{ templates: IntegrationTemplate[] }>(`/api/v1/environments/${environmentId}/integrations/templates`).then((r) => r.templates),
  createMonitorFromTemplate: (environmentId: string, templateId: string) =>
    post<IntegrationMonitor>(`/api/v1/environments/${environmentId}/integrations/from-template`, { template_id: templateId }),
  listIntegrationWatchers: (environmentId: string, monitorId: string) =>
    request<{ watchers: IntegrationWatcher[] }>(`/api/v1/environments/${environmentId}/integrations/${monitorId}/watchers`).then((r) => r.watchers),
  createIntegrationWatcher: (environmentId: string, monitorId: string, input: CreateIntegrationWatcherInput) =>
    post<IntegrationWatcher>(`/api/v1/environments/${environmentId}/integrations/${monitorId}/watchers`, input),
  getIntegrationWatcher: (environmentId: string, monitorId: string, watcherId: string) =>
    request<IntegrationWatcher>(`/api/v1/environments/${environmentId}/integrations/${monitorId}/watchers/${watcherId}`),
  updateIntegrationWatcher: (environmentId: string, monitorId: string, watcherId: string, input: UpdateIntegrationWatcherInput) =>
    put<IntegrationWatcher>(`/api/v1/environments/${environmentId}/integrations/${monitorId}/watchers/${watcherId}`, input),
  deleteIntegrationWatcher: (environmentId: string, monitorId: string, watcherId: string) =>
    del<void>(`/api/v1/environments/${environmentId}/integrations/${monitorId}/watchers/${watcherId}`),
  evaluateIntegrationMonitor: (environmentId: string, monitorId: string) =>
    post<{ result: CompositeHealthResult; monitor: IntegrationMonitor }>(`/api/v1/environments/${environmentId}/integrations/${monitorId}/evaluate`, {}),
  evaluateAllEnvironmentIntegrations: (environmentId: string) =>
    post<{ monitors: IntegrationMonitor[] }>(`/api/v1/environments/${environmentId}/integrations/evaluate-all`, {}).then((r) => r.monitors),

  // Server Readiness Agent API
  listServers: () => request<{ servers: Server[] }>('/api/v1/servers'),
  createServer: (name: string) => post<CreateServerResponse>('/api/v1/servers', { name }),
  listCloudTokens: () => request<{ tokens: CloudProviderToken[] }>('/api/v1/cloud-tokens'),
  addCloudToken: (input: { provider: 'hetzner'; name: string; token: string }) => post<CloudProviderToken>('/api/v1/cloud-tokens', input),
  deleteCloudToken: (tokenId: string) => del<{ deleted: boolean }>(`/api/v1/cloud-tokens/${tokenId}`),
  getHetznerOptions: (tokenId: string) => request<HetznerServerOptions>(`/api/v1/cloud-tokens/${tokenId}/hetzner-options`),
  createHetznerServer: (input: CreateHetznerServerInput) => post<{ server: Server; instance: ServerCloudInstance }>('/api/v1/servers/hetzner', input),
  getServerCloudInstance: (serverId: string) => request<{ cloud: boolean; instance?: ServerCloudInstance }>(`/api/v1/servers/${serverId}/cloud-instance`),
  deleteServerCloudInstance: (serverId: string) => del<{ deleted: boolean }>(`/api/v1/servers/${serverId}/cloud-instance`),
  getServer: (serverId: string) => request<ServerDetailResponse>(`/api/v1/servers/${serverId}`),
  deleteServer: (serverId: string) => del<{ deleted: boolean }>(`/api/v1/servers/${serverId}`),
  generateServerInstallToken: (serverId: string) => post<{ token: string; installation_command: string }>(`/api/v1/servers/${serverId}/install-token`, {}),
  triggerServerAudit: (serverId: string) => post<{ audit: ServerReadinessAudit }>(`/api/v1/servers/${serverId}/audit`, {}),
  listServerAudits: (serverId: string) => request<{ audits: ServerReadinessAudit[] }>(`/api/v1/servers/${serverId}/audits`),
  getServerEdge: (serverId: string, options: { live?: boolean } = {}) =>
    request<ServerEdge>(`/api/v1/servers/${serverId}/edge${options.live === false ? '?live=false' : ''}`),
  reconcileServerEdge: (serverId: string) => post<EdgeApplyResult>(`/api/v1/servers/${serverId}/edge/reconcile`, {}),
  removeServerEdge: (serverId: string) => del<EdgeApplyResult>(`/api/v1/servers/${serverId}/edge`),

  // Magento Onboarding Planner API
  generateOnboardingPlan: (projectId: string, input: GenerateOnboardingPlanInput) =>
    post<OnboardingPlan>(`/api/v1/projects/${projectId}/onboarding-plan`, input),
  getLatestOnboardingPlan: (projectId: string, environmentId?: string) =>
    request<OnboardingPlan>(
      `/api/v1/projects/${projectId}/onboarding-plan${environmentId ? `?environment_id=${encodeURIComponent(environmentId)}` : ''}`,
    ),
  listOnboardingPlans: (projectId: string) =>
    request<{ plans: OnboardingPlan[] }>(`/api/v1/projects/${projectId}/onboarding-plans`),
  getOnboardingPlan: (projectId: string, planId: string) =>
    request<OnboardingPlan>(`/api/v1/projects/${projectId}/onboarding-plans/${planId}`),
  approveOnboardingPlan: (projectId: string, planId: string) =>
    post<OnboardingPlan>(`/api/v1/projects/${projectId}/onboarding-plans/${planId}/approve`, {}),
};

export interface SyncMagentoDatabaseInput {
  source_environment_id?: string;
  source_backup_id?: string;
  target_base_url?: string;
  anonymize_pii?: boolean;
  disable_emails?: boolean;
  /** The target environment's name, typed by the person; required for production. */
  confirm_target?: string;
}

export interface SyncMagentoDatabaseResult {
  target_environment_id: string;
  table_count: number;
  base_url_updated: boolean;
  pii_anonymized: boolean;
  /** The backup of the target's database taken before it was replaced. */
  checkpoint_backup_id: string;
  duration_ms: number;
}

export interface StuckCronJob {
  schedule_id: number;
  job_code: string;
  status: string;
  executed_at: string;
  age_seconds: number;
}

export interface MagentoHealthReport {
  succeeded: boolean;
  database_healthy: boolean;
  cron_heartbeat_age_sec: number;
  cron_healthy: boolean;
  last_cron_completed_at?: string | null;
  stuck_job_count: number;
  stuck_jobs: StuckCronJob[];
  error_job_count: number;
  queue_pending_count: number;
  queue_error_count: number;
  disk_used_percent: number;
  disk_free_bytes: number;
  disk_total_bytes: number;
  error_output?: string;
}

export interface AuditCheckItem {
  category: string;
  name: string;
  status: 'passed' | 'warning' | 'failed';
  observed_value: string;
  message: string;
  recommendation?: string;
}

export interface ServerAuditReport {
  succeeded: boolean;
  overall_status: 'compatible' | 'warning' | 'incompatible';
  compatibility_score: number;
  magento_version?: string;
  /** The directory an installed Magento was really found in, empty when
   *  none was: Commerce Ops asks rather than assuming a path. */
  magento_root?: string;
  php_version: string;
  mysql_version?: string;
  redis_detected: boolean;
  opensearch_detected: boolean;
  varnish_detected: boolean;
  composer_version?: string;
  git_version?: string;
  disk_free_bytes: number;
  disk_total_bytes: number;
  checks: AuditCheckItem[];
  error_output?: string;
}

export interface SecurityFinding {
  id: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  title: string;
  description: string;
  remediation: string;
  affected_path?: string;
}

export interface SecurityReport {
  succeeded: boolean;
  security_score: number;
  overall_rating: 'secure' | 'needs_attention' | 'at_risk' | 'critical';
  admin_path: string;
  custom_admin_path: boolean;
  exposed_files: string[];
  findings_count: number;
  critical_count: number;
  high_count: number;
  medium_count: number;
  low_count: number;
  findings: SecurityFinding[];
  error_output?: string;
}

export interface IndexerStatus {
  id: string;
  title: string;
  status: 'ready' | 'reindex_required' | 'working' | 'unknown';
  update_mode: string;
  last_runtime: string;
}

export interface QueueConsumerStatus {
  name: string;
  status: 'running' | 'stopped' | 'idle';
  pid?: number;
  memory_bytes?: number;
}

export interface MagentoOperationsSummary {
  succeeded: boolean;
  indexers: IndexerStatus[];
  /** Set when the indexer state could not be read: the list is then empty and the count null (not known, not zero). */
  indexers_unavailable_reason?: string;
  stale_indexer_count: number | null;
  consumers: QueueConsumerStatus[];
  /** Set when the process list could not be read. */
  consumers_unavailable_reason?: string;
  /** Null: the process list cannot say which consumers should be running. */
  stopped_consumer_count: number | null;
  error_output?: string;
}

export interface OperationResult {
  action: string;
  succeeded: boolean;
  output: string;
  duration_ms: number;
  error_output?: string;
}

export interface CdnUrlAuditResult {
  url: string;
  path: string;
  http_status: number;
  cache_control: string;
  has_magento_tags: boolean;
  magento_tags_header?: string;
  sets_cookie: boolean;
  cookies_set?: string[];
  is_cacheable: boolean;
  issue_summary?: string;
}

export interface CdnAuditReport {
  succeeded: boolean;
  readiness_score: number;
  overall_status: 'optimal' | 'needs_tuning' | 'unoptimized';
  tested_urls_count: number;
  cacheable_count: number;
  leak_cookie_count: number;
  missing_tags_count: number;
  results: CdnUrlAuditResult[];
  recommendations: string[];
  error_output?: string;
}

export interface UrlLatencySample {
  path: string;
  http_status: number;
  ttfb_ms: number;
  total_ms: number;
  size_content: number;
}

export interface PerformanceBenchmarkReport {
  succeeded: boolean;
  average_latency_ms: number;
  samples: UrlLatencySample[];
  error_output?: string;
}

export interface CacheFlushResult {
  action: string;
  target?: string;
  succeeded: boolean;
  flushed_tags?: string[];
  output: string;
  duration_ms: number;
  error_output?: string;
}

export interface ConfigCheckItem {
  category: string;
  name: string;
  status: 'passed' | 'warning' | 'critical';
  observed_value: string;
  message: string;
  recommendation?: string;
  remediation_action?: string;
}

export interface ConfigFinding {
  severity: 'critical' | 'warning' | 'optimization';
  title: string;
  description: string;
  impact: string;
  remediation_action?: string;
}

export interface MagentoConfigAuditReport {
  succeeded: boolean;
  overall_score: number;
  caching_score: number;
  infrastructure_score: number;
  performance_score: number;
  magento_version?: string;
  magento_mode?: string;
  session_backend?: string;
  default_cache_backend?: string;
  page_cache_backend?: string;
  static_signing_enabled: boolean;
  realtime_indexer_count: number;
  scheduled_indexer_count: number;
  checks: ConfigCheckItem[];
  findings: ConfigFinding[];
  available_remediations: string[];
  error_output?: string;
}

export interface ConfigRemediationResult {
  action: string;
  succeeded: boolean;
  output: string;
  duration_ms: number;
  error_output?: string;
}

export type IntegrationExecutionType = 'cron' | 'queue_consumer' | 'method_plugin' | 'magento_event' | 'http_api';
export type IntegrationDirection = 'import' | 'export' | 'sync';
export type IntegrationEntity = 'orders' | 'products' | 'stock' | 'customers' | 'shipments' | 'prices' | 'custom';
export type IntegrationMonitorStatus = 'healthy' | 'delayed' | 'failing' | 'idle' | 'stale';
export type IntegrationRunStatus = 'success' | 'warning' | 'failed' | 'running';

export interface IntegrationMonitor {
  id: string;
  organization_id: string;
  environment_id: string;
  name: string;
  execution_type: IntegrationExecutionType;
  target_identifier: string;
  direction: IntegrationDirection;
  entity: IntegrationEntity;
  expected_interval_sec: number;
  warn_after_sec: number;
  critical_after_sec: number;
  status: IntegrationMonitorStatus;
  last_run_at?: string;
  last_success_at?: string;
  last_batch_size: number;
  last_duration_ms: number;
  success_rate_24h: number;
  composite_explanation?: string;
  template_id?: string;
  watchers?: IntegrationWatcher[];
  created_at: string;
  updated_at: string;
}

export interface IntegrationRun {
  id: string;
  monitor_id: string;
  organization_id: string;
  environment_id: string;
  deployment_id?: string;
  status: IntegrationRunStatus;
  started_at: string;
  finished_at?: string;
  duration_ms: number;
  records_total: number;
  records_processed: number;
  records_failed: number;
  error_type?: string;
  error_message?: string;
  correlation_id?: string;
  metadata?: Record<string, any>;
  created_at: string;
}

export interface DiscoveredIntegration {
  module: string;
  name: string;
  execution_type: IntegrationExecutionType;
  target_identifier: string;
  direction: IntegrationDirection;
  entity: IntegrationEntity;
  suggested_interval_sec: number;
  monitored: boolean;
}

export interface DeploymentRegressionAlert {
  monitor_id: string;
  monitor_name: string;
  deployment_id: string;
  deployment_status: string;
  deployment_finished_at: string;
  first_failed_run_at: string;
  time_delta_seconds: number;
  error_message?: string;
}

export interface CreateIntegrationMonitorInput {
  name: string;
  execution_type: IntegrationExecutionType;
  target_identifier: string;
  direction: IntegrationDirection;
  entity: IntegrationEntity;
  expected_interval_sec?: number;
  warn_after_sec?: number;
  critical_after_sec?: number;
}

export type WatcherType = 'file' | 'log' | 'database' | 'process' | 'http' | 'cron';
export type WatcherStatus = 'healthy' | 'warning' | 'critical' | 'unknown';

export interface FileWatcherConfig {
  path: string;
  is_directory: boolean;
  condition: 'modified_within' | 'max_age' | 'file_count_max' | 'file_count_min' | 'exists' | 'not_exists' | 'size_min' | 'size_max';
  threshold_value: number;
}

export interface LogWatcherConfig {
  file_path: string;
  success_pattern?: string;
  failure_pattern?: string;
  max_interval_minutes?: number;
  metric_pattern?: string;
}

export interface DatabaseWatcherConfig {
  table_name: string;
  mode: 'row_count' | 'freshness';
  filter_column?: string;
  filter_value?: string;
  warn_threshold?: number;
  crit_threshold?: number;
  timestamp_column?: string;
  max_age_minutes?: number;
}

export interface ProcessWatcherConfig {
  match_pattern: string;
  max_instances: number;
  max_runtime_minutes: number;
}

export interface HttpWatcherConfig {
  url: string;
  expected_status: number;
  timeout_seconds: number;
}

export interface CronWatcherConfig {
  job_code: string;
  warn_delay_minutes: number;
  crit_delay_minutes: number;
}

export interface WatcherConfiguration {
  file?: FileWatcherConfig;
  log?: LogWatcherConfig;
  database?: DatabaseWatcherConfig;
  process?: ProcessWatcherConfig;
  http?: HttpWatcherConfig;
  cron?: CronWatcherConfig;
}

export interface IntegrationWatcher {
  id: string;
  monitor_id: string;
  organization_id: string;
  environment_id: string;
  name: string;
  watcher_type: WatcherType;
  configuration: WatcherConfiguration;
  status: WatcherStatus;
  last_checked_at?: string;
  last_message: string;
  last_metrics: Record<string, any>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateWatcherFromTemplate {
  name: string;
  watcher_type: WatcherType;
  configuration: WatcherConfiguration;
}

export interface IntegrationTemplate {
  id: string;
  name: string;
  description: string;
  entity: IntegrationEntity;
  direction: IntegrationDirection;
  execution_type: IntegrationExecutionType;
  target_identifier: string;
  expected_interval_sec: number;
  warn_after_sec: number;
  critical_after_sec: number;
  default_watchers: CreateWatcherFromTemplate[];
}

export interface CompositeHealthResult {
  monitor_id: string;
  monitor_name: string;
  status: IntegrationMonitorStatus;
  composite_explanation: string;
  watchers_evaluated: number;
  healthy_count: number;
  warning_count: number;
  critical_count: number;
  evaluated_at: string;
}

export interface CreateIntegrationWatcherInput {
  name: string;
  watcher_type: WatcherType;
  config: WatcherConfiguration;
  is_active?: boolean;
}

export interface UpdateIntegrationWatcherInput {
  name: string;
  config: WatcherConfiguration;
  is_active: boolean;
}

// Server Readiness Agent Types
export type ServerStatus = 'waiting_for_agent' | 'online' | 'offline' | 'error';
export type ServerReadinessStatus = 'not_run' | 'running' | 'ready' | 'warning' | 'failed';
export type ReadinessCheckStatus = 'pass' | 'warning' | 'fail' | 'info';

export interface ReadinessCheck {
  id: string;
  category: string;
  title: string;
  status: ReadinessCheckStatus;
  message: string;
  data?: Record<string, any>;
}

export interface ReadinessSummary {
  status: string;
  passed: number;
  warnings: number;
  failed: number;
}

export interface ServerReadinessAudit {
  id: string;
  server_id: string;
  organization_id: string;
  audit_id: string;
  status: string;
  summary: ReadinessSummary;
  checks: ReadinessCheck[];
  created_at: string;
}

// Server Capability Types
export type CapabilityStatus = 'recommended' | 'available' | 'supported' | 'supported_with_warning' | 'blocked' | 'unknown';

export interface ServerCapability {
  id: string;
  category: string;
  name: string;
  status: CapabilityStatus;
  ownership?: ResourceOwnership;
  reason: string;
  /** Whether a warning or refusal stops anything; empty for a plain "supported". */
  impact?: 'blocking' | 'not_blocking';
  /** What the operator can do about the verdict. Every warning and refusal carries one. */
  remedy?: string;
  details?: Record<string, any>;
  /** When the evidence behind the verdict was taken (the server check's time, or the agent's connection). */
  observed_at?: string;
}

export interface Server {
  id: string;
  organization_id: string;
  name: string;
  hostname: string;
  agent_id?: string;
  /** How Commerce Ops reaches the server: its agent, or SSH for hosting
   * where no agent can be installed (CO-60). Absent means agent. */
  access_kind?: 'agent' | 'ssh';
  agent_version?: string;
  os?: string;
  architecture?: string;
  kernel?: string;
  status: ServerStatus;
  readiness_status: ServerReadinessStatus;
  capabilities?: ServerCapability[];
  last_seen_at?: string;
  connected_at?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface CreateServerResponse {
  server: Server;
  token: string;
  installation_command: string;
}

/** A cloud provider token an organization saved (CO-200); the secret is never returned. */
export interface CloudProviderToken {
  id: string;
  provider: 'hetzner';
  name: string;
  created_by: string;
  created_at: string;
}

export interface HetznerLocation {
  name: string;
  city: string;
  country: string;
  network_zone: string;
}

export interface HetznerServerType {
  name: string;
  description: string;
  architecture: 'x86' | 'arm';
  cores: number;
  memory_gb: number;
  disk_gb: number;
  /** Net monthly price in the cheapest location; 0 when Hetzner lists none. */
  monthly_eur: number;
  locations: string[];
  deprecated: boolean;
}

export interface HetznerServerOptions {
  locations: HetznerLocation[];
  server_types: HetznerServerType[];
  /** The cheapest type with 4 cores and 8 GB; empty when none is offered. */
  recommended_server_type: string;
  recommended_location: string;
}

export interface ServerCloudInstance {
  server_id: string;
  provider: 'hetzner';
  provider_server_id: number;
  token_id?: string;
  location: string;
  server_type: string;
  public_ip?: string;
  created_by: string;
  created_at: string;
  deleted_at?: string;
}

export interface CreateHetznerServerInput {
  name: string;
  token_id: string;
  location: string;
  server_type: string;
  ssh_public_key?: string;
}

export interface ServerDetailResponse {
  server: Server;
  latest_audit?: ServerReadinessAudit | null;
}

// Magento Onboarding Planner Types
export type DeploymentStrategy = 'commerceops_docker' | 'atomic_native' | 'existing_native' | 'existing_docker';
export type PlanActionType = 'reuse' | 'create' | 'configure' | 'integrate' | 'leave_unchanged' | 'requires_user_input' | 'blocked';
export type ComponentType =
  | 'reverse_proxy'
  | 'php_runtime'
  | 'magento_app'
  | 'database'
  | 'redis'
  | 'catalog_search'
  | 'opensearch'
  | 'rabbitmq'
  | 'http_cache'
  | 'cron'
  | 'queue_consumers'
  | 'filesystem_media'
  | 'frontend'
  | 'tls'
  | 'domain_routing'
  | 'docker_runtime'
  | 'persistent_volumes'
  | 'backups'
  | 'monitoring';

export interface FrontendComponentAnalysis {
  framework: string;
  root_directory: string;
  package_name?: string;
  node_version?: string;
  build_script?: string;
  start_script?: string;
  port?: number;
  dependencies?: string[];
}

export interface DockerServiceAnalysis {
  name: string;
  image?: string;
  build_path?: string;
  ports?: string[];
  volumes?: string[];
  environment?: string[];
  /** Compose profiles the service is gated behind (does not start by default). */
  profiles?: string[];
}

// Application capabilities (CO-47): what the analysed code needs or
// provides, discovered from evidence, never assumed from "it is Magento".
export type ApplicationCapabilityKey = 'catalog_search' | string;
export type CapabilityConfidence = 'confirmed' | 'high' | 'medium' | 'low' | 'unknown';
export type CapabilityDetectionStatus = 'detected' | 'unresolved' | 'conflict';
export type CapabilityAnalysisSource = 'static_analysis' | 'runtime' | 'operator';

export interface CapabilityEvidence {
  source: string;
  path?: string;
  detail: string;
}

export interface CapabilityRuntimeObservation {
  observed_at: string;
  engine_identifier?: string;
  modules?: string[];
  enabled_core_search_modules?: string[];
  magento_version?: string;
  search_tables?: string[];
  agrees_with_static: boolean;
  warnings?: string[];
}

export interface ApplicationCapability {
  key: ApplicationCapabilityKey;
  status: CapabilityDetectionStatus;
  confidence: CapabilityConfidence;
  source: CapabilityAnalysisSource;
  /** opensearch | elasticsearch | mysql | custom | unknown */
  provider: string;
  provider_name: string;
  /** magento_core | third_party_module | application_module | unknown */
  implementation: string;
  engine_identifier?: string;
  module?: string;
  package?: string;
  external_service_required: boolean;
  external_service_kind?: 'opensearch' | 'elasticsearch' | '';
  version_constraint?: string;
  /** yes | deprecated | no | unknown */
  magento_native_support: string;
  /** detected | not_found | unknown */
  repository_support: string;
  /** compatible | deprecated | unsupported | unknown */
  compatibility: string;
  compatibility_note?: string;
  magento_version?: string;
  evidence: CapabilityEvidence[];
  warnings?: string[];
  runtime?: CapabilityRuntimeObservation;
  detected_at: string;
  commit_sha?: string;
}

export interface CapabilityDrift {
  key: ApplicationCapabilityKey;
  previous: ApplicationCapability;
  current: ApplicationCapability;
  detected_at: string;
}

export interface StoredApplicationCapability {
  organization_id: string;
  application_id: string;
  key: ApplicationCapabilityKey;
  capability: ApplicationCapability;
  fingerprint: string;
  previous?: ApplicationCapability;
  changed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CatalogSearchRuntimeInspection {
  environment_id: string;
  application_id: string;
  observation: CapabilityRuntimeObservation;
  capability: ApplicationCapability;
  previous?: ApplicationCapability;
  configuration_updated: boolean;
}

/** Memory footprint of the planned stack, from its actual components. */
export interface PlanResourceEstimate {
  components: { component: ComponentType; label: string; memory_mb: number; note?: string }[];
  total_memory_mb: number;
  recommended_host_memory_gb: number;
  minimum_host_memory_gb: number;
  search_service_memory_mb: number;
  notes?: string[];
}

/** Where an analysis came from: which branch and commit were read, when,
 * how — or, for assumed defaults, exactly why the repository was not read. */
export interface RepositoryAnalysisProvenance {
  /** "provider_api" when Commerce Ops read the files through the Git
   * provider, "provided" when the caller supplied them. */
  fetch_method?: 'provider_api' | 'provided' | string;
  provider?: string;
  repository?: string;
  branch?: string;
  commit_sha?: string;
  analyzed_at?: string;
  files_read?: string[];
  files_absent?: string[];
  fallback_reason?:
    | 'repository_url_not_configured'
    | 'repository_host_unsupported'
    | 'repository_access_not_configured'
    | 'branch_not_found'
    | string;
  fallback_message?: string;
}

/** A fact the analysis derived rather than read (for example the database
 * version from the Magento release). Shown as inferred, never as detected. */
export interface InferredValue {
  value: string;
  source: string;
}

/** Magento storefront technology (docs/HYVA_THEMES.md). */
export type StorefrontType = 'luma' | 'hyva' | 'headless' | 'undetermined';
export type StorefrontMode = 'auto' | 'luma' | 'hyva' | 'headless';

export interface StorefrontBuild {
  theme: string;
  path: string;
  package_manager: 'npm' | 'pnpm' | 'yarn' | string;
  package_manager_version?: string;
  lockfile?: string;
  script?: string;
  node_major?: number;
  node_requirement?: string;
  output: string;
  tailwind?: string;
  problems?: string[];
}

export interface StorefrontTheme {
  code: string;
  path: string;
  location: 'app' | 'package' | 'vendor' | string;
  /** The Composer package of a packaged theme. */
  package?: string;
  /** A theme package whose files are not in the repository. */
  unresolved?: boolean;
  parent?: string;
  lineage?: string[];
  hyva: boolean;
  build?: StorefrontBuild;
  node_tooling?: string;
}

export interface StorefrontDetection {
  storefront: StorefrontType;
  confidence: 'confirmed' | 'high' | 'low' | string;
  hyva_packages?: { name: string; version: string; source: string }[];
  hyva_modules?: string[];
  themes?: StorefrontTheme[];
  active_themes?: string[];
  hyva_config_committed?: boolean;
  static_signing_disabled?: boolean;
  evidence?: string[];
  ambiguities?: string[];
  warnings?: string[];
  unresolved_theme_packages?: string[];
  resolve_at_build?: boolean;
}

export interface EffectiveStorefront {
  type: StorefrontType;
  source: 'configured' | 'project' | 'detected' | 'release' | 'default';
  confidence?: string;
  /** The theme comes from a Composer package only the built release shows. */
  resolve_at_build?: boolean;
  build_themes?: string[];
  ambiguities?: string[];
  problems?: string[];
}

export interface MagentoStorefront {
  organization_id: string;
  application_id: string;
  mode: StorefrontMode;
  detection?: StorefrontDetection;
  detected_from?: 'repository' | 'deployment' | 'release' | string;
  detected_commit?: string;
  detected_at?: string;
  updated_by?: string;
  updated_at: string;
  effective: EffectiveStorefront;
  label: string;
}

export interface RepositoryAnalysis {
  /** "repository" when real repository files were analysed, "assumed_defaults"
   * when the repository could not be read for a documented, non-credential
   * reason (see provenance.fallback_reason) and generic Magento 2.4 defaults
   * were used instead. */
  source?: 'repository' | 'assumed_defaults' | string;
  provenance?: RepositoryAnalysisProvenance;
  is_magento: boolean;
  magento_edition?: string;
  /** The exact release when the repository pins one (composer.json exact
   * requirement or composer.lock); empty when only a constraint is known. */
  magento_version?: string;
  /** The composer requirement as written, e.g. "^2.4.7". */
  magento_version_constraint?: string;
  magento_version_source?: string;
  /** The composer "php" constraint as written, never narrowed. */
  required_php_version?: string;
  required_php_extensions?: string[];
  required_composer_version?: string;
  /** Pre-CO-47 summary of the catalog search verdict; `capabilities` is
   * the source of truth. */
  search_engine_required?: string;
  search_engine_inferred?: InferredValue;
  /** Discovered application capabilities (catalog_search first). */
  capabilities?: ApplicationCapability[];
  database_requirement?: InferredValue;
  config_php_committed?: boolean;
  enabled_modules?: number;
  disabled_modules?: number;
  env_php_committed?: boolean;
  auth_json_committed?: boolean;
  magento_cloud_config_detected?: boolean;
  dockerfiles?: string[];
  uses_redis: boolean;
  uses_rabbitmq: boolean;
  headless: boolean;
  frontend_component?: FrontendComponentAnalysis;
  docker_compose_detected: boolean;
  docker_services?: DockerServiceAnalysis[];
  deployment_scripts?: string[];
  modules_count: number;
  identified_files?: string[];
  raw_warnings?: string[];
  /** The storefront technology the repository shows (Luma, Hyvä). */
  storefront?: StorefrontDetection;
}

export interface StrategyEvaluation {
  strategy: DeploymentStrategy;
  status: CapabilityStatus | 'recommended' | 'available';
  reason: string;
  pros: string[];
  cons: string[];
}

export interface ComponentPlan {
  component: ComponentType;
  current_state: string;
  required: boolean;
  proposed_action: PlanActionType;
  future_ownership: ResourceOwnership;
  reason: string;
  details?: Record<string, any>;
}

export interface PlanStep {
  id: string;
  title: string;
  description: string;
  status: string;
  risk: string;
  requires_approval: boolean;
  dependencies?: string[];
  action_type: string;
  resource_type: string;
  resource_id: string;
  rollback_implications?: string;
  configuration?: Record<string, any>;
}

export interface PlanRisk {
  level: string;
  category: string;
  title: string;
  description: string;
}

export interface RequiredInput {
  id: string;
  required: boolean;
  question: string;
  description?: string;
  options?: string[];
  default_value?: string;
}

export interface OnboardingPlan {
  id: string;
  organization_id: string;
  project_id: string;
  environment_id: string;
  application_id?: string | null;
  server_id: string;
  version: number;
  planner_version: number;
  status: string; // 'draft' | 'needs_input' | 'blocked' | 'ready_for_review' | 'approved' | 'superseded'
  strategy: DeploymentStrategy;
  strategy_evaluations: StrategyEvaluation[];
  repository_analysis: RepositoryAnalysis;
  components: ComponentPlan[];
  steps: PlanStep[];
  risks: PlanRisk[];
  required_inputs: RequiredInput[];
  user_inputs: Record<string, string>;
  server_usage: string;
  resource_estimate?: PlanResourceEstimate;
  capability_drift?: CapabilityDrift[];
  approved_at?: string | null;
  approved_by?: string | null;
  created_at: string;
  updated_at: string;
  /** Derived by the API from the runs created from this plan (CO-73); never stored with the plan. */
  lifecycle?: PlanLifecycle;
  /** The latest live setup run created from this plan. */
  execution?: PlanExecutionSummary | null;
  /** What can be done next with the plan's run: create it, open it, or run the plan again. */
  run_action?: PlanRunAction;
  /** When a live setup run of this environment last completed, from any plan version. */
  environment_setup_completed_at?: string | null;
}

export type PlanLifecycle =
  | 'draft'
  | 'needs_input'
  | 'blocked'
  | 'awaiting_approval'
  | 'approved'
  | 'run_pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'superseded';

export type PlanRunAction = 'run' | 'open' | 'retry';

export interface PlanExecutionSummary {
  id: string;
  status: string;
  created_at: string;
  started_at?: string | null;
  completed_at?: string | null;
  error_code?: string | null;
  error_message?: string | null;
}

/** What deleting a project removes, keeps and is blocked by (CO-74). */
export interface ProjectDeletionPreview {
  project_id: string;
  name: string;
  slug: string;
  environments: number;
  components: number;
  deployments: number;
  setup_plans: number;
  runs: number;
  recovery_points: number;
  monitors: number;
  domains: number;
  /** Servers the project runs on; they and everything on them stay. */
  server_names: string[];
  /** Stored backup files; they stay in storage, recorded in the audit log. */
  backup_files: number;
  /** Operations in flight; deletion is refused until there are none. */
  blockers: ProjectDeletionBlocker[];
  /** Set when this installation cannot delete projects safely yet. */
  unavailable_reason?: string;
}

export interface ProjectDeletionBlocker {
  kind: 'run' | 'deployment' | 'coordinated_deployment' | 'legacy_deployment' | 'component_removal' | string;
  id: string;
  label: string;
  status: string;
}

/** The repositories a project's own repository access can read (CO-73). */
export interface ProjectRepositories {
  /** The credential the list was read with; 'none' when the project has no repository access. */
  source: 'project_connection' | 'github_app' | 'none';
  provider?: GitProvider;
  account?: string;
  repositories: RemoteRepository[];
}

export interface GenerateOnboardingPlanInput {
  environment_id?: string;
  server_id: string;
  user_inputs?: Record<string, string>;
  repo_files?: Record<string, string>;
}

export type ExecutionMode = 'live' | 'rehearsal';

export interface ExecutionRun {
  id: string;
  organization_id: string;
  project_id: string;
  environment_id: string;
  server_id: string;
  /** "rehearsal" executions record what would run and touch nothing. */
  mode?: ExecutionMode;
  application_ids?: string[];
  plan_id: string;
  plan_version: number;
  planner_version: number;
  execution_schema_version: number;
  workflow_type: string;
  status: string; // 'pending' | 'preflighting' | 'running' | 'paused' | 'waiting_for_input' (needs the operator) | 'paused_waiting_for_agent' | 'failed' | 'completed' | 'cancelled'
  current_step_key?: string;
  locked_by_worker_id?: string | null;
  lease_expires_at?: string | null;
  plan_snapshot: OnboardingPlan;
  created_by: string;
  created_at: string;
  started_at?: string | null;
  completed_at?: string | null;
  paused_at?: string | null;
  error_code?: string | null;
  error_message?: string | null;
}

export interface ExecutionStep {
  id: string;
  execution_id: string;
  step_key: string;
  title: string;
  description: string;
  sequence: number;
  action_type: string;
  resource_type: string;
  resource_id: string;
  status: string; // 'pending' | 'ready' | 'running' | 'succeeded' | 'failed' | 'skipped' | 'blocked' | 'cancelled'
  risk: string;
  reversibility: string;
  requires_approval: boolean;
  depends_on: string[];
  attempt_count: number;
  max_attempts: number;
  timeout_seconds: number;
  retryable: boolean;
  command_name: string;
  command_payload?: Record<string, any>;
  result?: Record<string, any>;
  /** What the engine dispatches to undo this step when a later step fails before `until` succeeded. */
  compensation?: StepCompensation;
  rollback_notes?: string;
  started_at?: string | null;
  completed_at?: string | null;
  error_code?: string | null;
  error_message?: string | null;
}

export interface StepCompensation {
  title: string;
  command: string;
  until: string;
  guard?: string;
}

/** Outcome of a step's compensation, recorded under `result.compensation` after a failure. */
export interface StepCompensationOutcome {
  title?: string;
  command?: string;
  status: 'succeeded' | 'failed' | 'skipped';
  error?: string;
  startedAt?: string;
  completedAt?: string;
}

export interface ExecutionLog {
  id: string;
  execution_id: string;
  step_id?: string | null;
  timestamp: string;
  source: string;
  level: string;
  message: string;
}

export interface ExecutionCheckpoint {
  id: string;
  execution_id: string;
  step_id: string;
  name: string;
  metadata: Record<string, any>;
  created_at: string;
}

export interface ExecutionControls {
  can_start: boolean;
  can_pause: boolean;
  can_resume: boolean;
  can_retry: boolean;
  can_cancel: boolean;
  /** The run needs the operator to settle a step whose outcome is unknown. */
  can_resolve?: boolean;
  retryable_step_id?: string | null;
  reason?: string;
}

export interface ExecutionDetailResponse {
  execution: ExecutionRun;
  steps: ExecutionStep[];
  /** The steps grouped into at most 14 stages (absent from older servers). */
  stages?: DeploymentStage[];
  checkpoints: ExecutionCheckpoint[];
  controls: ExecutionControls;
  /** What a failed setup, backup or restore run means (CO-97). */
  interpretation?: FailureInterpretation;
}

/** Starts the setup run of an approved plan. When the plan already has a run
 * in flight the API answers with that run (`created: false`) instead of
 * queueing a second one. */
export async function createExecution(projectId: string, planId: string): Promise<{ execution: ExecutionRun; steps: ExecutionStep[]; created?: boolean }> {
  return request<{ execution: ExecutionRun; steps: ExecutionStep[]; created?: boolean }>(`/api/v1/projects/${projectId}/executions`, {
    method: 'POST',
    body: JSON.stringify({ planId }),
  });
}

export async function listProjectExecutions(projectId: string): Promise<{ executions: ExecutionRun[] }> {
  return request<{ executions: ExecutionRun[] }>(`/api/v1/projects/${projectId}/executions`);
}

export async function getExecution(executionId: string): Promise<ExecutionDetailResponse> {
  return request<ExecutionDetailResponse>(`/api/v1/executions/${executionId}`);
}

export async function startExecution(executionId: string): Promise<{ success: boolean }> {
  return request<{ success: boolean }>(`/api/v1/executions/${executionId}/start`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function pauseExecution(executionId: string): Promise<{ success: boolean }> {
  return request<{ success: boolean }>(`/api/v1/executions/${executionId}/pause`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function resumeExecution(executionId: string): Promise<{ success: boolean }> {
  return request<{ success: boolean }>(`/api/v1/executions/${executionId}/resume`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function cancelExecution(executionId: string): Promise<{ success: boolean }> {
  return request<{ success: boolean }>(`/api/v1/executions/${executionId}/cancel`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/** How the operator settles a step whose outcome is unknown (RES-001). */
export type ExecutionResolution = 'succeeded' | 'failed' | 'rerun';

export async function resolveExecution(executionId: string, resolution: ExecutionResolution, note: string): Promise<{ success: boolean }> {
  return request<{ success: boolean }>(`/api/v1/executions/${executionId}/resolve`, {
    method: 'POST',
    body: JSON.stringify({ resolution, note }),
  });
}

export async function retryExecutionStep(executionId: string, stepId: string): Promise<{ success: boolean }> {
  return request<{ success: boolean }>(`/api/v1/executions/${executionId}/steps/${stepId}/retry`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function getExecutionLogs(executionId: string, limit = 500): Promise<{ logs: ExecutionLog[] }> {
  return request<{ logs: ExecutionLog[] }>(`/api/v1/executions/${executionId}/logs?limit=${limit}`);
}

export type DeploymentWorkflowStatus =
  | 'pending'
  | 'preflighting'
  | 'building'
  | 'deploying'
  | 'verifying'
  | 'activating'
  | 'post_deploy'
  | 'completed'
  | 'completed_with_failures'
  | 'failed'
  | 'rolled_back'
  | 'cancelled';

export type DBChangeClassification =
  | 'no_db_change'
  | 'db_change_backward_compatible'
  | 'db_change_detected'
  | 'db_change_unknown'
  | 'db_change_high_risk';

export interface DeploymentRelease {
  id: string;
  organization_id: string;
  project_id: string;
  application_id?: string | null;
  commit_sha: string;
  commit_ref: string;
  commit_message: string;
  commit_author: string;
  release_tag: string;
  deployment_strategy: string;
  status: 'pending' | 'building' | 'active' | 'previous' | 'inactive' | 'failed';
  created_at: string;
  activated_at?: string | null;
}

export interface ProjectDeployment {
  id: string;
  organization_id: string;
  project_id: string;
  environment_id: string;
  server_id: string;
  application_id?: string | null;
  execution_id: string;
  scope?: DeploymentScope;
  repository_id: string;
  source_branch: string;
  source_commit_sha: string;
  source_commit_message: string;
  source_commit_author: string;
  release_id: string;
  previous_release_id?: string | null;
  target_release_id?: string | null;
  frontend_application_id?: string | null;
  frontend_release_id?: string | null;
  frontend_commit_sha?: string | null;
  frontend_commit_ref?: string | null;
  frontend_commit_message?: string | null;
  components?: DeploymentComponentRelease[];
  deployment_strategy: string;
  status: DeploymentWorkflowStatus;
  db_change_classification: DBChangeClassification;
  rollback_details?: Record<string, any> | null;
  verification_results?: Record<string, any> | null;
  /** Blast radius computed when the deployment was created (CO-46). */
  impact?: DeploymentImpact | null;
  /** Coordinated deployment this one belongs to, if any (CO-48). */
  group_id?: string | null;
  group_sequence?: number;
  component_name?: string;
  failure_reason?: string;
  created_by: string;
  created_at: string;
  started_at?: string | null;
  completed_at?: string | null;
}

export interface DeploymentVerification {
  id: string;
  organization_id: string;
  deployment_id: string;
  verification_type: string;
  target_url: string;
  /** Which part of the topology the check covered (CO-46). */
  scope?: VerificationScope | null;
  status: 'passed' | 'failed' | 'warning';
  latency_ms: number;
  details: Record<string, any>;
  created_at: string;
}

export interface DeploymentRollback {
  id: string;
  organization_id: string;
  deployment_id: string;
  execution_id: string;
  from_release_id: string;
  to_release_id: string;
  code_rollback_status: string;
  db_rollback_status: string;
  reason: string;
  initiated_by: string;
  created_at: string;
  completed_at?: string | null;
}

/** What the recorded database classification means for a code-only rollback. */
export type DatabaseRollbackSafety = 'safe' | 'unsafe' | 'unknown';

/**
 * The one rollback decision the server makes for every entry point
 * (deployment detail, component history, incident suggestions, guarded
 * remediation): domain.EvaluateRollback.
 */
export interface RollbackEligibility {
  allowed: boolean;
  block?: 'no_previous_release' | 'rehearsal' | 'not_completed' | 'not_live' | 'database_unsafe' | 'platform_unsupported';
  reason?: string;
  db_classification: DBChangeClassification;
  database_safety: DatabaseRollbackSafety;
  from_release_id: string;
  to_release_id?: string;
}

/** One validator of Magento's setup:db:status (observation 1). */
export interface MagentoDBValidator {
  kind: 'declarative_schema' | 'schema_patches' | 'data_patches' | 'module_versions' | 'other';
  class: string;
  up_to_date: boolean;
}

export interface MagentoPatch {
  patch: string;
  module?: string;
}

export interface MagentoDBStatus {
  validators: MagentoDBValidator[];
  unapplied_schema_patches: MagentoPatch[];
  unapplied_data_patches: MagentoPatch[];
  module_version_changes: { module: string; type: 'schema' | 'data'; current: string; required: string }[];
  modules: string[];
  messages?: string[];
  checked_at?: string;
  recognised: boolean;
}

/** What a Magento deployment knows about its database change, from its own steps. */
export interface DeploymentDatabaseChanges {
  state: 'analysed' | 'unrecognised' | 'not_installed' | 'pending' | 'not_analysed';
  classification?: DBChangeClassification;
  analysed_at?: string;
  status?: MagentoDBStatus;
  raw?: string;
  applied: 'applied' | 'not_needed' | 'failed' | 'running' | 'pending' | 'not_run';
  applied_action?: string;
  applied_at?: string;
  not_determinable: string[];
  /** The exact structural change: live release's declarative schema vs this release's. */
  schema?: SchemaAnalysis;
  /** The exact code: every database-relevant file this release changes, with its diff. */
  code?: DatabaseCodeChanges;
  /** What Commerce Ops' own comparison of the two releases found (blue-green runs); absent when it found nothing. */
  detected?: DetectedChanges;
  /** flagged: Magento reports nothing pending, but the release comparison found changes. */
  overall?: 'no_changes' | 'data_only' | 'schema' | 'schema_and_data' | 'flagged' | 'undetermined' | 'pending';
  /** What a code rollback leaves behind: guidance, never a promise that it is safe. */
  rollback_impact?: 'none' | 'data' | 'additive' | 'modifying' | 'destructive' | 'unknown';
}

/** One change the release comparison detected; `known` is false when it could not be determined. */
export interface DetectedOperation {
  kind: string;
  subject: string;
  detail?: string;
  known: boolean;
}

export interface DetectedChanges {
  operations: DetectedOperation[];
  /** What the comparison could not read, and why. */
  unknowns?: string[];
}

export type DatabaseCodeKind = 'schema' | 'schema_whitelist' | 'schema_patch' | 'data_patch' | 'setup_script' | 'module_declaration' | 'setup_other';

/** One database-relevant file that differs from the live release. */
export interface DatabaseCodeFile {
  path: string;
  module?: string;
  kind: DatabaseCodeKind;
  change: 'added' | 'removed' | 'modified';
  origin: 'app_code' | 'vendor';
  /** Unified diff, 3 lines of context, at most 64 KiB. */
  diff?: string;
  lines_added: number;
  lines_removed: number;
  diff_truncated?: boolean;
  /** size_budget: the result's size bound was spent before this file. */
  diff_omitted?: 'size_budget';
}

/** The exact code of a release's database changes, read from files only. */
export interface DatabaseCodeChanges {
  state: 'compared' | 'no_live_release' | 'unreadable' | 'pending' | 'not_run' | 'not_available';
  reason?: string;
  source_release?: string;
  target_release?: string;
  files: DatabaseCodeFile[];
  file_count: number;
  files_truncated?: boolean;
  summary?: Partial<Record<DatabaseCodeKind, number>>;
  modules?: { added: string[]; removed: string[]; enabled: string[]; disabled: string[] };
  duration_ms?: number;
}

/** A commit's database changes read against the live release before deploying it. */
export interface DatabaseChangePreview {
  id: string;
  environment_id: string;
  application_id: string;
  execution_id: string;
  commit_sha: string;
  commit_ref: string;
  /** The release it is compared with ("" when nothing was deployed yet). */
  live_release_tag: string;
  created_by: string;
  created_at: string;
  status: string;
  finished: boolean;
  current_step?: string;
  error_message?: string;
  schema?: SchemaAnalysis;
  code?: DatabaseCodeChanges;
}

export type SchemaImpact = 'none' | 'additive' | 'modifying' | 'destructive';
export type SchemaOperation = 'create' | 'drop' | 'keep' | 'modify';

export interface SchemaAnalysis {
  state: 'compared' | 'no_live_release' | 'unreadable' | 'pending' | 'not_run' | 'not_available';
  reason?: string;
  source_release?: string;
  target_release?: string;
  live_matches_record?: boolean;
  diff?: SchemaDiff;
}

export interface SchemaDiff {
  impact: SchemaImpact;
  summary: {
    tables_created: number;
    tables_dropped: number;
    tables_modified: number;
    columns_added: number;
    columns_dropped: number;
    columns_modified: number;
    indexes_changed: number;
    constraints_changed: number;
    kept: number;
  };
  tables: SchemaTableChange[];
  modules_enabled: string[];
  modules_disabled: string[];
  notes: string[];
}

export interface SchemaKeyChange {
  name: string;
  kind: string;
  operation: SchemaOperation;
  impact: string;
  before?: string;
  after?: string;
}

export interface SchemaTableChange {
  table: string;
  operation: SchemaOperation;
  impact: SchemaImpact;
  modules: string[];
  columns: { name: string; operation: SchemaOperation; impact: string; before?: string; after?: string; changed?: string[]; data_from?: string }[];
  indexes: SchemaKeyChange[];
  constraints: SchemaKeyChange[];
  attributes: { name: string; before: string; after: string }[];
  data_from?: string;
}

/** A run's steps grouped into a stage (at most 14 per run). */
export interface DeploymentStage {
  key: string;
  title: string;
  status: 'pending' | 'running' | 'partial' | 'succeeded' | 'warning' | 'failed' | 'skipped' | 'cancelled' | 'not_run';
  steps: number;
  done: number;
  started_at?: string;
  completed_at?: string;
  current_step?: string;
  failed_step?: string;
  step_keys: string[];
}

export interface ProjectDeploymentDetailResponse {
  /** Magento deployments only (absent for WordPress and frontends). */
  database_changes?: DeploymentDatabaseChanges;
  /** Whether the storefront answers slower since this release went live (CO-198). */
  latency?: DeploymentLatency;
  deployment: ProjectDeployment;
  release: DeploymentRelease;
  execution: ExecutionRun;
  steps: ExecutionStep[];
  /** The steps grouped into at most 14 stages (absent from older servers). */
  stages?: DeploymentStage[];
  verifications: DeploymentVerification[];
  rollback?: DeploymentRollback | null;
  controls: ExecutionControls;
  /** Absent only when the server could not decide (treated as not allowed). */
  rollback_eligibility?: RollbackEligibility;
  /** The release a rollback returns to. */
  rollback_target_release?: DeploymentRelease;
  /** What a failed, rolled-back, partial or rehearsed deployment means (CO-97). */
  interpretation?: FailureInterpretation;
  /** The component's release live in the environment now (CO-99). */
  live_release?: DeploymentRelease;
  /** The backup taken before this deployment, when there is one (CO-99). */
  checkpoint?: RecoveryPoint;
  /** What happened and whether its release serves now (CO-150). */
  serving?: DeploymentServingState;
}

export interface CreateProjectDeploymentPayload {
  /** The environment to deploy into; the server comes from its placement. */
  environmentId?: string;
  mode?: ExecutionMode;
  scope?: DeploymentScope;
  backendApplicationId?: string;
  frontendApplicationId?: string;
  branch?: string;
  commitSha?: string;
  commitMessage?: string;
  commitAuthor?: string;
  frontendBranch?: string;
  frontendCommitSha?: string;
  frontendCommitMessage?: string;
  frontendCommitAuthor?: string;
  /** Coordinated deployment (scope selected|all): one deployment per component (CO-48). */
  components?: CoordinatedComponentInput[];
  start?: boolean;
  options?: {
    locales?: string[];
    hasNextJs?: boolean;
    packageManager?: string;
    nodeVersion?: string;
  };
}

/** The Idempotency-Key header for one user intent (RES-073; see idempotency.ts). */
function idempotencyHeaders(key: string | undefined): Record<string, string> {
  return key ? { 'Idempotency-Key': key } : {};
}

export async function createCoordinatedDeployment(
  projectId: string,
  payload: CreateProjectDeploymentPayload,
  idempotencyKey?: string,
): Promise<{ group: DeploymentGroup; deployments: ProjectDeployment[] }> {
  return request<{ group: DeploymentGroup; deployments: ProjectDeployment[] }>(`/api/v1/projects/${projectId}/project-deployments`, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: idempotencyHeaders(idempotencyKey),
  });
}

export async function getDeploymentGroup(groupId: string): Promise<{ group: DeploymentGroup; members: DeploymentGroupMember[] }> {
  return request<{ group: DeploymentGroup; members: DeploymentGroupMember[] }>(`/api/v1/deployment-groups/${groupId}`);
}

export async function startDeploymentGroup(groupId: string): Promise<{ group: DeploymentGroup }> {
  return request<{ group: DeploymentGroup }>(`/api/v1/deployment-groups/${groupId}/start`, { method: 'POST', body: JSON.stringify({}) });
}

export async function listDeploymentGroups(environmentId: string): Promise<{ groups: DeploymentGroup[] }> {
  return request<{ groups: DeploymentGroup[] }>(`/api/v1/environments/${environmentId}/deployment-groups`);
}

export async function createProjectDeployment(
  projectId: string,
  payload: CreateProjectDeploymentPayload,
  idempotencyKey?: string,
): Promise<{ deployment: ProjectDeployment; release: DeploymentRelease; steps: ExecutionStep[]; start_error?: string }> {
  return request<{ deployment: ProjectDeployment; release: DeploymentRelease; steps: ExecutionStep[]; start_error?: string }>(
    `/api/v1/projects/${projectId}/project-deployments`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: idempotencyHeaders(idempotencyKey),
    }
  );
}

export async function listProjectDeployments(
  projectId: string,
  environmentId?: string,
): Promise<{ deployments: ProjectDeployment[]; serving?: Record<string, DeploymentServingState> }> {
  return request<{ deployments: ProjectDeployment[]; serving?: Record<string, DeploymentServingState> }>(
    `/api/v1/projects/${projectId}/project-deployments${environmentId ? `?environment_id=${encodeURIComponent(environmentId)}` : ''}`,
  );
}

export async function getProjectDeployment(deploymentId: string): Promise<ProjectDeploymentDetailResponse> {
  return request<ProjectDeploymentDetailResponse>(`/api/v1/project-deployments/${deploymentId}`);
}

export async function startProjectDeployment(deploymentId: string): Promise<{ success: boolean }> {
  return request<{ success: boolean }>(`/api/v1/project-deployments/${deploymentId}/start`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function rollbackProjectDeployment(deploymentId: string, reason: string): Promise<{ success: boolean }> {
  return request<{ success: boolean }>(`/api/v1/project-deployments/${deploymentId}/rollback`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

// Backup, Verified Restore & Disaster Recovery Types and Endpoints
export type RecoveryPointType = 'scheduled' | 'manual' | 'pre_deployment' | 'pre_upgrade' | 'pre_restore';
export type RecoveryPointStatus = 'creating' | 'uploading' | 'verifying' | 'ready' | 'verification_failed' | 'failed' | 'expired' | 'deleting' | 'deleted';
export type VerificationStatus = 'unverified' | 'checksum_verified' | 'verification_failed';
export type RestoreTestStatus = 'not_tested' | 'testing' | 'restore_verified' | 'restore_failed';
export type ArtifactType = 'database_dump' | 'media_archive' | 'recovery_metadata';
export type ArtifactStatus = 'pending' | 'uploading' | 'uploaded' | 'verified' | 'failed';
export type RestoreMode = 'full_restore' | 'database_only' | 'media_only';
export type RestoreRunStatus = 'pending' | 'preflighting' | 'protecting_current' | 'restoring_db' | 'restoring_media' | 'verifying' | 'completed' | 'failed';

export interface BackupArtifact {
  id: string;
  recovery_point_id: string;
  organization_id: string;
  project_id: string;
  artifact_type: ArtifactType;
  storage_target_id?: string;
  storage_key: string;
  compression: string;
  size_bytes: number;
  checksum_algo: string;
  checksum_value: string;
  status: ArtifactStatus;
  metadata_json?: Record<string, any>;
  created_at: string;
  verified_at?: string;
  /** Where the agent wrote it on the server that captured it. */
  local_path?: string;
  /** When it was stored off-host; absent: the only copy is on the server. */
  uploaded_at?: string;
  stored_size_bytes?: number;
  /** When the off-host copy was last read back, decrypted and checked. */
  remote_verified_at?: string;
  upload_attempts?: number;
  last_upload_error?: string;
}

export interface RecoveryPoint {
  id: string;
  organization_id: string;
  workspace_id: string;
  project_id: string;
  environment_id: string;
  server_id: string;
  type: RecoveryPointType;
  deployment_id?: string;
  release_id?: string;
  commit_sha?: string;
  status: RecoveryPointStatus;
  verification_status: VerificationStatus;
  restore_test_status: RestoreTestStatus;
  pinned: boolean;
  total_size_bytes: number;
  execution_id?: string;
  artifacts?: BackupArtifact[];
  metadata_json?: Record<string, any>;
  created_at: string;
  completed_at?: string;
  expires_at?: string;
}

export interface RestoreVerification {
  id: string;
  restore_run_id: string;
  recovery_point_id: string;
  organization_id: string;
  verification_type: string;
  target: string;
  status: string;
  latency_ms: number;
  details?: Record<string, any>;
  created_at: string;
}

export interface RestoreRun {
  id: string;
  organization_id: string;
  workspace_id: string;
  project_id: string;
  server_id: string;
  recovery_point_id: string;
  execution_id: string;
  pre_restore_recovery_point_id?: string;
  mode: RestoreMode;
  status: RestoreRunStatus;
  initiated_by: string;
  confirmed_by: string;
  failure_reason?: string;
  /** What the ended restore did to the live store. */
  outcome?: RestoreOutcome;
  /** The store was left in maintenance mode. */
  maintenance_left_on?: boolean;
  verifications?: RestoreVerification[];
  created_at: string;
  started_at?: string;
  completed_at?: string;
}

export type RestoreOutcome = 'restored' | 'untouched' | 'database_partially_replaced' | 'media_partially_extracted' | 'restored_with_errors';

export interface BackupPolicy {
  id: string;
  organization_id: string;
  workspace_id: string;
  project_id: string;
  frequency: string;
  retention_days: number;
  retain_daily_count: number;
  retain_weekly_count: number;
  pre_deploy_checkpoint_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateRecoveryPointPayload {
  environmentId?: string;
  type?: RecoveryPointType;
  includeMedia?: boolean;
  compression?: string;
}

export interface RestoreRecoveryPointPayload {
  /** Only the database can be restored for now (CO-169); the server refuses the others. */
  mode: 'database_only';
  confirmedBy: string;
}

export async function createRecoveryPoint(
  projectId: string,
  payload: CreateRecoveryPointPayload,
  idempotencyKey?: string,
): Promise<{ recovery_point: RecoveryPoint; execution: ExecutionRun }> {
  return request<{ recovery_point: RecoveryPoint; execution: ExecutionRun }>(
    `/api/v1/projects/${projectId}/recovery-points`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: idempotencyHeaders(idempotencyKey),
    }
  );
}

export async function listRecoveryPoints(
  projectId: string,
  environmentId?: string,
): Promise<{ recovery_points: RecoveryPoint[]; incomplete_restore?: RestoreRun }> {
  return request<{ recovery_points: RecoveryPoint[]; incomplete_restore?: RestoreRun }>(
    `/api/v1/projects/${projectId}/recovery-points${environmentId ? `?environment_id=${encodeURIComponent(environmentId)}` : ''}`,
  );
}

export async function getRecoveryPoint(recoveryPointId: string): Promise<{ recovery_point: RecoveryPoint }> {
  return request<{ recovery_point: RecoveryPoint }>(`/api/v1/recovery-points/${recoveryPointId}`);
}

export async function pinRecoveryPoint(recoveryPointId: string): Promise<{ success: boolean; pinned: boolean }> {
  return request<{ success: boolean; pinned: boolean }>(`/api/v1/recovery-points/${recoveryPointId}/pin`, {
    method: 'POST',
  });
}

export async function unpinRecoveryPoint(recoveryPointId: string): Promise<{ success: boolean; pinned: boolean }> {
  return request<{ success: boolean; pinned: boolean }>(`/api/v1/recovery-points/${recoveryPointId}/unpin`, {
    method: 'POST',
  });
}

export async function deleteRecoveryPoint(recoveryPointId: string): Promise<{ success: boolean }> {
  return request<{ success: boolean }>(`/api/v1/recovery-points/${recoveryPointId}`, {
    method: 'DELETE',
  });
}

/** Starts a restore test; `execution` is its run, whose log shows the steps. */
export async function testRestoreRecoveryPoint(recoveryPointId: string): Promise<{ success: boolean; status: string; execution: ExecutionRun }> {
  return request<{ success: boolean; status: string; execution: ExecutionRun }>(`/api/v1/recovery-points/${recoveryPointId}/test-restore`, {
    method: 'POST',
  });
}

export async function restoreRecoveryPoint(
  recoveryPointId: string,
  payload: RestoreRecoveryPointPayload,
  idempotencyKey?: string,
): Promise<{ restore_run: RestoreRun; execution: ExecutionRun }> {
  return request<{ restore_run: RestoreRun; execution: ExecutionRun }>(
    `/api/v1/recovery-points/${recoveryPointId}/restore`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: idempotencyHeaders(idempotencyKey),
    }
  );
}

export async function getBackupPolicy(projectId: string): Promise<{ backup_policy: BackupPolicy }> {
  return request<{ backup_policy: BackupPolicy }>(`/api/v1/projects/${projectId}/backup-policy`);
}

export async function upsertBackupPolicy(
  projectId: string,
  policy: Partial<BackupPolicy>
): Promise<{ backup_policy: BackupPolicy }> {
  return request<{ backup_policy: BackupPolicy }>(`/api/v1/projects/${projectId}/backup-policy`, {
    method: 'PUT',
    body: JSON.stringify(policy),
  });
}

// Continuous Monitoring & Incident Detection Subsystem Types & API

export type ContinuousMonitorType =
  | 'agent'
  | 'host'
  | 'service'
  | 'http'
  | 'graphql'
  | 'database'
  | 'redis'
  | 'opensearch'
  | 'catalog_search'
  | 'rabbitmq'
  | 'cron'
  | 'queue_consumer'
  | 'log'
  | 'backup_health'
  | 'deployment_health'
  | 'tls_certificate'
  | 'file_watch'
  | 'database_table_watch'
  | 'process_watch'
  | 'docker_engine'
  | 'varnish'
  // The store's slow database queries (CO-156)
  | 'database_slow_queries'
  // The store's Magento indexers (GAP-24)
  | 'magento_indexers'
  // WordPress / WooCommerce (CO-60)
  | 'wordpress_page'
  | 'wordpress_health'
  | 'wp_cron'
  | 'action_scheduler'
  | 'wordpress_errors'
  | 'wordpress_software';

export type ContinuousMonitorStatus = 'healthy' | 'warning' | 'critical' | 'unknown' | 'disabled';
export type IncidentLifecycleStatus = 'open' | 'acknowledged' | 'resolved' | 'suppressed';
export type OperationalSeverity = 'info' | 'warning' | 'critical';
export type OperationalCorrelationConfidence = 'related' | 'likely_related' | 'possibly_related';

export interface ContinuousMonitor {
  id: string;
  organization_id: string;
  workspace_id: string;
  project_id: string;
  server_id?: string;
  environment_id?: string;
  name: string;
  type: ContinuousMonitorType;
  status: ContinuousMonitorStatus;
  scope: 'system_generated' | 'user_created';
  target_config: Record<string, any>;
  interval_seconds: number;
  timeout_seconds: number;
  consecutive_failures: number;
  consecutive_successes: number;
  last_run_at?: string;
  next_run_at: string;
  last_status_changed_at: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  /** The newest recorded result: what the check found and why (CO-114). */
  last_result?: ContinuousMonitorLastResult;
}

export interface ContinuousMonitorLastResult {
  status: ContinuousMonitorStatus;
  message: string;
  metrics?: Record<string, unknown>;
  /** Host checks only: the limits the readings are classified against. */
  limits?: HostLimits;
  checked_at: string;
}

/** A host check's limits in percent: above a warn limit it warns; disk or
 * inodes above critical_percent fail it. */
export interface HostLimits {
  cpu_warn_percent: number;
  ram_warn_percent: number;
  disk_warn_percent: number;
  critical_percent: number;
}

export interface ContinuousMonitorResult {
  id: string;
  monitor_id: string;
  project_id: string;
  status: ContinuousMonitorStatus;
  latency_ms: number;
  message: string;
  metrics_json?: Record<string, any>;
  raw_output?: string;
  checked_at: string;
}

export interface IncidentSignal {
  id: string;
  incident_id: string;
  signal_type: string;
  status: ContinuousMonitorStatus;
  details_json?: Record<string, any>;
  attached_at: string;
}

export interface IncidentTimelineEvent {
  id: string;
  incident_id: string;
  event_type: string;
  description: string;
  actor?: string;
  metadata_json?: Record<string, any>;
  occurred_at: string;
}

export interface IncidentCorrelation {
  id: string;
  incident_id: string;
  correlated_type: string;
  correlated_id?: string;
  title: string;
  reason: string;
  confidence: OperationalCorrelationConfidence;
  metadata_json?: Record<string, any>;
  created_at: string;
}

export interface SuggestedOperationalAction {
  id: string;
  incident_id: string;
  action_type: 'rollback_deployment' | 'view_deployment' | 'view_logs' | 'trigger_backup' | 'test_restore' | 'run_readiness_audit';
  title: string;
  description: string;
  available: boolean;
  availability_reason: string;
  action_payload_json?: Record<string, any>;
  created_at: string;
}

export interface Incident {
  id: string;
  organization_id: string;
  workspace_id: string;
  project_id: string;
  fingerprint: string;
  title: string;
  description: string;
  severity: OperationalSeverity;
  status: IncidentLifecycleStatus;
  root_cause_category?: string;
  correlated_entity_id?: string;
  correlated_entity_type?: string;
  correlation_confidence?: OperationalCorrelationConfidence;
  /** The environment it was raised in, when known (Go: Incident.EnvironmentID). */
  environment_id?: string;
  primary_resource: string;
  opened_at: string;
  acknowledged_at?: string;
  acknowledged_by?: string;
  resolved_at?: string;
  resolved_by?: string;
  resolution_reason?: string;
  created_at: string;
  updated_at: string;
  signals?: IncidentSignal[];
  timeline?: IncidentTimelineEvent[];
  correlations?: IncidentCorrelation[];
  /** The check this incident was raised from, when it can be named (CO-100). */
  source_monitor_id?: string;
  suggested_actions?: SuggestedOperationalAction[];
}

export interface ContinuousMonitoringPolicy {
  id: string;
  organization_id: string;
  workspace_id: string;
  project_id: string;
  enabled: boolean;
  default_interval_seconds: number;
  host_cpu_threshold: number;
  host_ram_threshold: number;
  host_disk_warning_threshold: number;
  host_disk_critical_threshold: number;
  http_timeout_seconds: number;
  tls_expiry_warning_days: number;
  tls_expiry_critical_days: number;
  backup_max_age_hours: number;
  created_at: string;
  updated_at: string;
}

export async function listContinuousMonitors(projectId: string): Promise<{ monitors: ContinuousMonitor[] }> {
  return request<{ monitors: ContinuousMonitor[] }>(`/api/v1/projects/${projectId}/monitors`);
}

export async function createContinuousMonitor(projectId: string, payload: Partial<ContinuousMonitor>): Promise<ContinuousMonitor> {
  return request<ContinuousMonitor>(`/api/v1/projects/${projectId}/monitors`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function getContinuousMonitor(monitorId: string): Promise<ContinuousMonitor> {
  return request<ContinuousMonitor>(`/api/v1/monitors/${monitorId}`);
}

export async function updateContinuousMonitor(monitorId: string, payload: Partial<ContinuousMonitor>): Promise<ContinuousMonitor> {
  return request<ContinuousMonitor>(`/api/v1/monitors/${monitorId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function deleteContinuousMonitor(monitorId: string): Promise<void> {
  return request<void>(`/api/v1/monitors/${monitorId}`, {
    method: 'DELETE',
  });
}

export async function runContinuousMonitorNow(monitorId: string): Promise<{ result: ContinuousMonitorResult; incident?: Incident | null }> {
  return request<{ result: ContinuousMonitorResult; incident?: Incident | null }>(`/api/v1/monitors/${monitorId}/run`, {
    method: 'POST',
  });
}

export async function listContinuousIncidents(projectId: string, status: 'active' | 'all' = 'active'): Promise<{ incidents: Incident[] }> {
  return request<{ incidents: Incident[] }>(`/api/v1/projects/${projectId}/incidents?status=${status}`);
}

export async function getContinuousIncident(incidentId: string): Promise<Incident> {
  return request<Incident>(`/api/v1/incidents/${incidentId}`);
}

export async function acknowledgeContinuousIncident(incidentId: string, actor?: string): Promise<{ status: string }> {
  return request<{ status: string }>(`/api/v1/incidents/${incidentId}/acknowledge`, {
    method: 'POST',
    body: JSON.stringify({ actor }),
  });
}

export async function resolveContinuousIncident(incidentId: string, reason?: string, actor?: string): Promise<{ status: string }> {
  return request<{ status: string }>(`/api/v1/incidents/${incidentId}/resolve`, {
    method: 'POST',
    body: JSON.stringify({ actor, reason }),
  });
}

export async function getContinuousMonitoringPolicy(projectId: string): Promise<ContinuousMonitoringPolicy> {
  return request<ContinuousMonitoringPolicy>(`/api/v1/projects/${projectId}/monitoring-policy`);
}

export async function updateContinuousMonitoringPolicy(projectId: string, policy: Partial<ContinuousMonitoringPolicy>): Promise<ContinuousMonitoringPolicy> {
  return request<ContinuousMonitoringPolicy>(`/api/v1/projects/${projectId}/monitoring-policy`, {
    method: 'PUT',
    body: JSON.stringify(policy),
  });
}

// ----------------------------------------------------------------------------
// Guarded Remediation Subsystem Types & API
// ----------------------------------------------------------------------------

export type RemediationMode = 'observe_only' | 'approval_required' | 'auto_safe';

export type RemediationActionType =
  | 'health_check.rerun'
  | 'queue_consumer.restart'
  | 'managed_service.restart'
  | 'backup.retry'
  | 'deployment.rollback'
  | 'magento.cache_clean'
  | 'magento.cache_flush'
  | 'varnish.vcl_reload';

export type RemediationRisk = 'low' | 'moderate' | 'high' | 'prohibited_auto';

export type RemediationActionState =
  | 'proposed'
  | 'waiting_for_approval'
  | 'approved'
  | 'executing'
  | 'verifying'
  | 'succeeded'
  | 'failed'
  | 'suppressed'
  | 'blocked'
  | 'escalated'
  | 'cancelled';

export interface RemediationPolicy {
  id: string;
  organization_id: string;
  workspace_id: string;
  project_id: string;
  mode: RemediationMode;
  auto_restart_consumers: boolean;
  auto_restart_app: boolean;
  auto_rollback_deployment: boolean;
  auto_retry_backup: boolean;
  auto_clean_cache: boolean;
  max_attempts: number;
  cooldown_minutes: number;
  circuit_breaker_window_min: number;
  created_at: string;
  updated_at: string;
}

export interface RemediationAction {
  id: string;
  organization_id: string;
  workspace_id: string;
  project_id: string;
  incident_id: string;
  server_id?: string | null;
  action_type: RemediationActionType;
  risk: RemediationRisk;
  state: RemediationActionState;
  eligibility_code: string;
  eligibility_reason: string;
  target_resource_id: string;
  target_resource_type: string;
  target_resource_ownership: string;
  execution_id?: string | null;
  approved_by?: string | null;
  approved_at?: string | null;
  executed_at?: string | null;
  completed_at?: string | null;
  failure_reason?: string | null;
  verification_status: string;
  action_payload_json?: Record<string, any>;
  fingerprint: string;
  created_at: string;
  updated_at: string;
}

export interface RemediationAttempt {
  id: string;
  action_id: string;
  attempt_number: number;
  execution_id: string;
  status: string;
  failure_message?: string;
  started_at: string;
  completed_at?: string;
}

export interface RemediationVerification {
  id: string;
  action_id: string;
  strategy: string;
  status: 'passed' | 'failed';
  details_json?: Record<string, any>;
  verified_at: string;
}

export interface RemediationCircuitBreaker {
  id: string;
  organization_id: string;
  workspace_id: string;
  project_id: string;
  resource_id: string;
  action_type: RemediationActionType;
  attempt_count: number;
  window_started_at: string;
  last_attempt_at: string;
  open_until?: string | null;
  state: 'closed' | 'open' | 'half_open';
  created_at: string;
  updated_at: string;
}

export async function getRemediationPolicy(projectId: string): Promise<{ policy: RemediationPolicy }> {
  return request<{ policy: RemediationPolicy }>(`/api/v1/projects/${projectId}/remediation-policy`);
}

export async function updateRemediationPolicy(
  projectId: string,
  policy: Partial<RemediationPolicy>
): Promise<{ policy: RemediationPolicy }> {
  return request<{ policy: RemediationPolicy }>(`/api/v1/projects/${projectId}/remediation-policy`, {
    method: 'PUT',
    body: JSON.stringify(policy),
  });
}

export async function listProjectRemediations(
  projectId: string,
  incidentId?: string
): Promise<{ actions: RemediationAction[] }> {
  const query = incidentId ? `?incident_id=${incidentId}` : '';
  return request<{ actions: RemediationAction[] }>(`/api/v1/projects/${projectId}/remediations${query}`);
}

export async function listIncidentRemediations(
  incidentId: string
): Promise<{ actions: RemediationAction[] }> {
  return request<{ actions: RemediationAction[] }>(`/api/v1/incidents/${incidentId}/remediations`);
}

export async function getRemediationAction(
  actionId: string
): Promise<{ action: RemediationAction }> {
  return request<{ action: RemediationAction }>(`/api/v1/remediations/${actionId}`);
}

export async function approveRemediationAction(
  actionId: string,
  actor?: string
): Promise<{ action: RemediationAction }> {
  return request<{ action: RemediationAction }>(`/api/v1/remediations/${actionId}/approve`, {
    method: 'POST',
    body: JSON.stringify({ actor }),
  });
}

export async function executeRemediationAction(
  actionId: string,
  actor?: string
): Promise<{ action: RemediationAction }> {
  return request<{ action: RemediationAction }>(`/api/v1/remediations/${actionId}/execute`, {
    method: 'POST',
    body: JSON.stringify({ actor }),
  });
}

export async function cancelRemediationAction(
  actionId: string,
  reason?: string
): Promise<{ action: RemediationAction }> {
  return request<{ action: RemediationAction }>(`/api/v1/remediations/${actionId}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function resetCircuitBreaker(
  breakerId: string
): Promise<{ status: string; circuit_breaker_id: string }> {
  return request<{ status: string; circuit_breaker_id: string }>(
    `/api/v1/remediations/circuit-breakers/${breakerId}/reset`,
    {
      method: 'POST',
    }
  );
}






// ---------------------------------------------------------------------------
// Product Distribution Hub (CO-54)
//
// Magento owns what a product is. Commerce Ops owns where it has been
// distributed, which version, whether it was accepted and why it was not.
// Every shape here mirrors internal/httpapi/distribution.go.
// ---------------------------------------------------------------------------

export type DistributionChannelType = 'google_merchant' | 'openai_acp' | 'compari' | 'meta_commerce';
export type ChannelDeliveryMode = 'per_product' | 'snapshot' | 'feed';
export type ChannelHealth =
  | 'connected'
  | 'degraded'
  | 'auth_error'
  | 'disabled'
  | 'unconfigured'
  | 'catalog_error'
  | 'unknown';
export type PublicationState =
  | 'ineligible'
  | 'ready'
  | 'queued'
  | 'publishing'
  | 'submitted'
  | 'published'
  | 'warning'
  | 'rejected'
  | 'stale'
  | 'delete_pending'
  | 'deleted'
  | 'failed';
export type IssueSeverity = 'info' | 'warning' | 'blocking';
export type FeedHealth = 'healthy' | 'degraded' | 'unavailable' | 'stale';

/**
 * What the channel did with the feed, which is a different question from
 * whether we produced it. Only a channel that reports back leaves
 * 'not_reported'.
 */
export type FeedIngestionState = 'not_reported' | 'pending' | 'accepted' | 'partial' | 'rejected';
export type VariantPublication = 'variants' | 'parents' | 'both';

export interface DistributionIssue {
  code: string;
  severity: IssueSeverity;
  origin: 'policy' | 'source' | 'validation' | 'channel' | 'remote';
  field?: string;
  source_field?: string;
  message: string;
  advice?: string;
  channel?: DistributionChannelType;
  documentation?: string;
}

export interface ChannelSettings {
  merchant_account_id?: string;
  data_source_id?: string;
  content_language?: string;
  feed_label?: string;
  sftp_host?: string;
  sftp_port?: number;
  sftp_username?: string;
  sftp_directory?: string;
  sftp_file_name?: string;
  seller_name?: string;
  seller_privacy_policy_url?: string;
  seller_tos?: string;
  enable_checkout?: boolean;
  accepts_returns?: boolean;
  return_window_days?: number;
  default_delivery_days_max?: number;
  default_delivery_cost_set?: boolean;
  default_delivery_cost_micros?: number;
  include_attributes?: boolean;
  meta_catalog_id?: string;
  meta_business_id?: string;
  meta_feed_id?: string;
  meta_graph_api_version?: string;
  meta_use_facebook_category?: boolean;
  meta_brand_fallback_to_seller?: boolean;
  meta_seller_name?: string;
  target_countries?: string[];
}

export interface ChannelPolicy {
  exclude_disabled: boolean;
  exclude_without_image: boolean;
  exclude_not_visible_individually: boolean;
  allow_without_price: boolean;
  include_out_of_stock: boolean;
  include_backorders: boolean;
  minimum_stock: number;
  allowed_category_paths?: string[];
  excluded_category_paths?: string[];
  excluded_skus?: string[];
  variant_publication: VariantPublication;
  target_country?: string;
  currency?: string;
  language?: string;
}

export interface FeedStatus {
  health: FeedHealth;
  format: string;
  url?: string;
  current_version: number;
  generated_at?: string;
  delivered_at?: string;
  product_count: number;
  eligible_count: number;
  excluded_count: number;
  validation_failures: number;
  byte_size: number;
  content_hash?: string;
  last_failure_at?: string;
  last_failure_error?: string;
  ingestion: FeedIngestionState;
  ingestion_detail?: string;
  ingested_at?: string;
  ingested_items: number;
  ingestion_deleted: number;
  ingestion_errors: number;
  ingestion_warnings: number;
  detail?: string;
}

/** One thing a channel connection still needs, from its channel's own rules (CO-193). */
export interface ChannelSetupItem {
  field: string;
  message: string;
  advice?: string;
  /** Publishing is blocked without it; otherwise it is recommended. */
  required: boolean;
}

export interface DistributionChannel {
  id: string;
  project_id: string;
  channel_type: DistributionChannelType;
  channel_display_name: string;
  name: string;
  website_code: string;
  store_view_code?: string;
  scope: string;
  enabled: boolean;
  health: ChannelHealth;
  health_detail?: string;
  last_health_check_at?: string;
  credential_configured: boolean;
  delivery_mode: ChannelDeliveryMode;
  settings: ChannelSettings;
  policy: ChannelPolicy;
  /** What the connection still needs, required first; empty once complete. Absent from older servers. */
  setup?: ChannelSetupItem[];
  feed_url?: string;
  feed?: FeedStatus;
  created_at: string;
  updated_at: string;
}

export interface DistributionChannelSummary {
  connection_id: string;
  channel_type: DistributionChannelType;
  name: string;
  scope: string;
  enabled: boolean;
  health: ChannelHealth;
  health_detail?: string;
  in_scope: number;
  published: number;
  warnings: number;
  rejected: number;
  ineligible: number;
  pending: number;
  failed: number;
  stale: number;
  last_success_at?: string;
  feed?: FeedStatus;
}

export interface DistributionMarket {
  website_code: string;
  website_name: string;
  store_view_code: string;
  store_view_name: string;
  locale?: string;
  currency?: string;
  base_url?: string;
  is_default: boolean;
}

export interface DistributionRunStats {
  inspected: number;
  created: number;
  updated: number;
  unchanged: number;
  in_sync: number;
  repaired: number;
  queued: number;
  warnings: number;
  rejected: number;
  ineligible: number;
  withdrawn: number;
  deleted: number;
  failed: number;
  unsupported: number;
  feeds_built: number;
  drift_found: number;
}

export interface DistributionRunFinding {
  kind: string;
  subject: string;
  detail: string;
  action?: string;
  channel?: DistributionChannelType;
}

export interface DistributionRun {
  id: string;
  project_id: string;
  kind: 'initial_sync' | 'incremental_sync' | 'reconciliation' | 'revalidation';
  state: 'running' | 'completed' | 'failed' | 'cancelled' | 'partial';
  stats: DistributionRunStats;
  findings?: DistributionRunFinding[];
  started_at: string;
  completed_at?: string;
  error?: string;
  created_by: string;
}

export interface DistributionOverview {
  enabled: boolean;
  source?: {
    id: string;
    platform: string;
    status: string;
    last_full_sync_at?: string;
    last_event_at?: string;
  };
  summary: {
    products: number;
    healthy: number;
    warnings: number;
    rejected: number;
    not_eligible: number;
    pending: number;
    failed: number;
    stale: number;
    unsupported: number;
    withdrawn: number;
    last_synced_at?: string;
  };
  channels: DistributionChannelSummary[];
  markets: DistributionMarket[];
  runs: DistributionRun[];
  pending_events: number;
  dead_jobs: number;
  warnings?: string[];
}

export interface DistributionProductRow {
  projection_id: string;
  sku: string;
  title: string;
  website_code: string;
  store_view_code: string;
  product_type: string;
  supported: boolean;
  state: string;
  channels: Record<string, { connection_id: string; state: PublicationState; warnings: number; errors: number }>;
  overall: PublicationState;
  last_changed: string;
}

export interface DistributionProductPage {
  rows: DistributionProductRow[];
  total: number;
}

export interface ProductProjection {
  id: string;
  source_sku: string;
  source_product_id: string;
  website_code: string;
  store_view_code: string;
  type: string;
  supported: boolean;
  version: number;
  state: 'active' | 'withdrawn';
  source_updated_at?: string;
  updated_at: string;
  canonical: {
    identity: { sku: string; gtin?: string; mpn?: string; brand?: string; condition: string };
    content: {
      title: string;
      short_description?: string;
      description?: string;
      categories?: { path: string[]; primary: boolean }[];
      attributes?: { code: string; label: string; value: string; unit?: string }[];
      media?: { url: string; role: string }[];
    };
    commerce: {
      regular_price: { amount_micros: number; currency: string };
      final_price: { amount_micros: number; currency: string };
      sale_price: { amount_micros: number; currency: string };
      availability: string;
      quantity: number;
      quantity_known: boolean;
      buyable: boolean;
      price_visible: boolean;
    };
    urls: { canonical: string };
    market: { website_code: string; store_view_code: string; locale?: string; currency?: string };
    compatibility?: { kind: string; value: string; source_field?: string }[];
    provenance?: { field: string; source: string; source_field?: string; note?: string }[];
  };
}

export interface ChannelPublication {
  id: string;
  projection_id: string;
  connection_id: string;
  channel_type: DistributionChannelType;
  external_id?: string;
  state: PublicationState;
  desired_version: number;
  published_version: number;
  last_attempt_at?: string;
  last_success_at?: string;
  last_remote_check_at?: string;
  remote_status: string;
  remote_metadata?: Record<string, unknown>;
  issues: DistributionIssue[];
  error_code?: string;
  error_message?: string;
}

export interface DistributionProductDetail {
  projection: ProductProjection;
  publications: { publication: ChannelPublication; channel_name: string; scope: string }[];
}

export interface MappingProvenance {
  external_field: string;
  canonical_field: string;
  source_field?: string;
  origin: string;
  value?: string;
  note?: string;
}

export interface PayloadPreview {
  projection: ProductProjection;
  channel: DistributionChannelType;
  body: string;
  content_type: string;
  summary: Record<string, string>;
  provenance: MappingProvenance[];
  issues: DistributionIssue[];
  eligible: boolean;
  external_id?: string;
}

export interface CategoryMapping {
  id: string;
  connection_id: string;
  source_path: string;
  external_category: string;
  external_category_id?: string;
  auto: boolean;
}

export interface AttributeMapping {
  id: string;
  source_id: string;
  canonical_field: string;
  source_attribute: string;
  label?: string;
  unit?: string;
  auto: boolean;
}

export interface DistributionIngestEndpoint {
  url: string;
  source_id: string;
  signature_header: string;
  timestamp_header: string;
  source_header: string;
  key_configured: boolean;
  max_batch_size: number;
  skew_seconds: number;
}

export function getDistributionOverview(projectId: string): Promise<DistributionOverview> {
  return request<DistributionOverview>(`/api/v1/projects/${projectId}/distribution`);
}

export function enableDistribution(
  projectId: string,
  environmentId: string,
): Promise<{ setup: { markets: DistributionMarket[]; warnings?: string[] }; ingest: DistributionIngestEndpoint }> {
  return post(`/api/v1/projects/${projectId}/distribution/enable`, { environment_id: environmentId });
}

export function getDistributionIngest(projectId: string): Promise<DistributionIngestEndpoint> {
  return request<DistributionIngestEndpoint>(`/api/v1/projects/${projectId}/distribution/ingest`);
}

export function rotateDistributionIngestKey(
  projectId: string,
): Promise<{ ingest: DistributionIngestEndpoint; key: string; notice: string }> {
  return post(`/api/v1/projects/${projectId}/distribution/ingest/rotate-key`, {});
}

export function listDistributionChannels(
  projectId: string,
): Promise<{ channels: DistributionChannel[]; available: { channel_type: DistributionChannelType; display_name: string; delivery_mode: ChannelDeliveryMode }[] }> {
  return request(`/api/v1/projects/${projectId}/distribution/channels`);
}

export function createDistributionChannel(
  projectId: string,
  input: { channel_type: DistributionChannelType; name?: string; website_code: string; store_view_code?: string },
): Promise<DistributionChannel> {
  return post(`/api/v1/projects/${projectId}/distribution/channels`, input);
}

export function updateDistributionChannel(
  connectionId: string,
  input: { name?: string; enabled?: boolean; settings?: ChannelSettings; policy?: ChannelPolicy },
): Promise<DistributionChannel> {
  return request<DistributionChannel>(`/api/v1/distribution/channels/${connectionId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function setDistributionChannelCredential(connectionId: string, value: string): Promise<DistributionChannel> {
  return put(`/api/v1/distribution/channels/${connectionId}/credential`, { value });
}

export function deleteDistributionChannel(connectionId: string): Promise<{ deleted: boolean }> {
  return del(`/api/v1/distribution/channels/${connectionId}`);
}

export function checkDistributionChannelHealth(connectionId: string): Promise<DistributionChannel> {
  return post(`/api/v1/distribution/channels/${connectionId}/health`, {});
}

export function regenerateDistributionFeed(connectionId: string): Promise<{ queued: boolean }> {
  return post(`/api/v1/distribution/channels/${connectionId}/feed/regenerate`, {});
}

export function rotateDistributionFeedToken(connectionId: string): Promise<{ feed_url: string }> {
  return post(`/api/v1/distribution/channels/${connectionId}/feed/rotate-token`, {});
}

export function listDistributionProducts(
  projectId: string,
  filter: { search?: string; website?: string; channel?: string; state?: string; limit?: number; offset?: number } = {},
): Promise<DistributionProductPage> {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(filter)) {
    if (value !== undefined && value !== '' && value !== null) query.set(key, String(value));
  }
  const suffix = query.toString() ? `?${query.toString()}` : '';
  return request<DistributionProductPage>(`/api/v1/projects/${projectId}/distribution/products${suffix}`);
}

export function getDistributionProduct(projectionId: string): Promise<DistributionProductDetail> {
  return request<DistributionProductDetail>(`/api/v1/distribution/products/${projectionId}`);
}

export function previewDistributionPayload(projectionId: string, connectionId: string): Promise<PayloadPreview> {
  return request<PayloadPreview>(`/api/v1/distribution/products/${projectionId}/preview/${connectionId}`);
}

export function forceSyncDistributionProduct(projectionId: string): Promise<{ projection: ProductProjection }> {
  return post(`/api/v1/distribution/products/${projectionId}/sync`, {});
}

export function retryDistributionPublication(publicationId: string): Promise<{ publication: ChannelPublication }> {
  return post(`/api/v1/distribution/publications/${publicationId}/retry`, {});
}

export function listDistributionCategoryMappings(
  connectionId: string,
): Promise<{ mappings: CategoryMapping[]; unmapped: string[] }> {
  return request(`/api/v1/distribution/channels/${connectionId}/category-mappings`);
}

export function setDistributionCategoryMapping(
  connectionId: string,
  input: { source_path: string; external_category: string; external_category_id?: string },
): Promise<CategoryMapping> {
  return put(`/api/v1/distribution/channels/${connectionId}/category-mappings`, input);
}

export function deleteDistributionCategoryMapping(mappingId: string): Promise<{ deleted: boolean }> {
  return del(`/api/v1/distribution/category-mappings/${mappingId}`);
}

export function listDistributionAttributeMappings(
  projectId: string,
): Promise<{ mappings: AttributeMapping[]; canonical_fields: string[] }> {
  return request(`/api/v1/projects/${projectId}/distribution/attribute-mappings`);
}

export function setDistributionAttributeMapping(
  projectId: string,
  input: { canonical_field: string; source_attribute: string; label?: string; unit?: string },
): Promise<AttributeMapping> {
  return put(`/api/v1/projects/${projectId}/distribution/attribute-mappings`, input);
}

export function deleteDistributionAttributeMapping(mappingId: string): Promise<{ deleted: boolean }> {
  return del(`/api/v1/distribution/attribute-mappings/${mappingId}`);
}

export function listDistributionSourceAttributes(
  projectId: string,
): Promise<{ attributes: string[]; discovered: boolean }> {
  return request(`/api/v1/projects/${projectId}/distribution/source-attributes`);
}

export function listDistributionRuns(projectId: string): Promise<{ runs: DistributionRun[] }> {
  return request(`/api/v1/projects/${projectId}/distribution/runs`);
}

export function startDistributionRun(
  projectId: string,
  kind: DistributionRun['kind'],
  connectionId?: string,
): Promise<{ run: DistributionRun }> {
  return post(`/api/v1/projects/${projectId}/distribution/runs`, { kind, connection_id: connectionId });
}

// ---------------------------------------------------------------------------
// WordPress / WooCommerce (CO-60). Shapes mirror internal/wordpress and
// internal/domain/wordpress_site.go.

export interface WordPressCapabilities {
  ssh: boolean;
  agent: boolean;
  wp_cli: boolean;
  composer: boolean;
  node: boolean;
  git: boolean;
  woocommerce: boolean;
  multisite: boolean;
  bedrock: boolean;
  object_cache: string;
  redis: boolean;
  page_cache: string;
  cdn?: string;
  cron_mode: string;
  system_cron: 'yes' | 'no' | 'unknown' | string;
  atomic_symlink: boolean;
  filesystem_deploy: boolean;
  logs: 'full' | 'partial' | 'none' | string;
  action_scheduler: boolean;
}

export interface WordPressPackage {
  slug: string;
  name?: string;
  kind: string;
  status: string;
  version?: string;
  update_version?: string;
  update_available: boolean;
  update_state?: string;
  auto_update?: string;
}

export interface WooCommerceFacts {
  version: string;
  db_version?: string;
  currency?: string;
  default_country?: string;
  pages?: Record<string, { id: number; status?: string; url?: string }>;
  hpos?: boolean | null;
  weight_unit?: string;
  dimension_unit?: string;
}

export interface WordPressInspection {
  host: {
    user?: string;
    wp_cli_version?: string;
    php_cli_version?: string;
    tools: string[] | null;
    crontab_readable: boolean;
    cron_lines?: string[];
    symlinks: boolean;
    disk_free_kb?: number;
  };
  site: {
    bootstrapped: boolean;
    bootstrap_error?: string;
    wp_version?: string;
    php_version?: string;
    multisite: boolean;
    home?: string;
    siteurl?: string;
    environment_type?: string;
    locale?: string;
    blogname?: string;
    active_theme?: string;
    template?: string;
    active_plugins?: string[];
    permalink_structure?: string;
    woocommerce?: WooCommerceFacts;
    action_scheduler?: string;
    ext_object_cache: boolean;
    memory_limit?: string;
  };
  inventory: {
    core_version?: string;
    core_updates?: { version: string; update_type?: string }[];
    core_updates_known: boolean;
    plugins: WordPressPackage[] | null;
    themes: WordPressPackage[] | null;
    plugins_known: boolean;
    themes_known: boolean;
  };
  capabilities: WordPressCapabilities;
  warnings?: string[];
}

export interface WordPressVulnerability {
  id: string;
  provider: string;
  title: string;
  cve?: string[];
  cvss_score?: number;
  cvss_rating?: string;
  patched_in?: string[];
  references?: string[];
}

export interface WordPressSoftwareItem {
  kind: 'core' | 'plugin' | 'theme' | string;
  slug: string;
  name?: string;
  status?: string;
  version: string;
  target_version?: string;
  /** security, major, minor, bugfix, unknown — never invented. */
  category?: string;
  vulnerabilities?: WordPressVulnerability[];
  /** checked, not_configured, unavailable — whether a feed was consulted. */
  vulnerability_data: string;
  compatibility: {
    requires_php: string;
    requires_wp: string;
    tested_up_to_wp: string;
    wc_requires_at_least: string;
    wc_tested_up_to: string;
    verdict: 'compatible' | 'incompatible' | 'unknown' | string;
    reasons?: string[];
  };
  source?: string;
}

export interface WordPressSoftwareReport {
  items: WordPressSoftwareItem[] | null;
  vulnerability_source?: string;
  vulnerability_state: string;
  vulnerability_error?: string;
  checked_at: string;
  summary: {
    plugins_installed: number;
    plugins_active: number;
    updates_available: number;
    security_updates: number;
    vulnerable: number;
    major_updates: number;
  };
}

export interface WordPressSecurityFinding {
  id: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info' | string;
  title: string;
  description: string;
  remediation: string;
  affected_path?: string;
  evidence?: string;
  confirmed: boolean;
}

export interface WordPressSecurityReport {
  security_score: number;
  overall_rating: string;
  findings: WordPressSecurityFinding[] | null;
  checked_at: string;
  errors?: string[];
}

export type WordPressPatchMode = 'manual_approval' | 'security_only' | 'minor_and_security' | 'allowlist';

export interface WordPressSiteSettings {
  product_url?: string;
  category_url?: string;
  shop_url?: string;
  checkout_url?: string;
  run_builds?: boolean;
  cron: { grace_seconds: number; warn_late_seconds: number; critical_late_seconds: number; duplicate_warning_count: number };
  action_scheduler: {
    past_due_warning: number;
    past_due_critical: number;
    oldest_pending_warning_seconds: number;
    oldest_pending_critical_seconds: number;
    failed_recent_warning: number;
    growth_warning_percent: number;
    growth_minimum_pending: number;
  };
  latency_warning_ms?: number;
  latency_critical_ms?: number;
  patch_policy: { mode: WordPressPatchMode | ''; allowlist?: string[]; allowlist_only?: boolean; allow_core?: boolean };
  keep_releases?: number;
}

export interface WordPressRepositoryDetection {
  is_wordpress: boolean;
  kind?: string;
  units?: { kind: string; slug: string; source_path: string; target_path: string; name?: string }[];
  woocommerce_evidence?: string[];
  warnings?: string[];
}

export interface WordPressSite {
  id: string;
  project_id: string;
  environment_id: string;
  application_id: string;
  server_id: string;
  root_path: string;
  releases_root: string;
  site_url?: string;
  settings: WordPressSiteSettings;
  repository_detection?: WordPressRepositoryDetection;
  inspection?: WordPressInspection;
  inspection_error?: string;
  inspected_at?: string;
  health_baseline: { products_published: number; gateways_enabled: number; shipping_methods: number };
  software?: WordPressSoftwareReport;
  software_checked_at?: string;
  security?: WordPressSecurityReport;
  security_checked_at?: string;
  updated_at: string;
}

export interface WordPressDiscovery {
  user?: string;
  home?: string;
  wp_cli?: string;
  sites: { root: string; has_config: boolean }[] | null;
}

export interface WordPressPatchItem {
  kind: 'core' | 'plugin' | 'theme' | string;
  slug: string;
  target_version: string;
}

export interface WordPressPatchDecision {
  item: WordPressPatchItem;
  decision: { decision: 'allowed_by_policy' | 'approval_required' | 'blocked' | string; reason: string };
  software?: WordPressSoftwareItem;
}

export interface WordPressDeploymentPlanResponse {
  possible: boolean;
  reason?: string;
  site_id: string;
  plan?: { mode: string; database_checkpoint: boolean; database_update: boolean; maintenance: boolean; notes?: string[] };
  steps?: { key: string; title: string; description: string; risk: string }[] | null;
}

export type ErrorGroupStatus = 'open' | 'resolved' | 'muted';

export interface ApplicationErrorGroup {
  id: string;
  environment_id: string;
  application_id: string;
  fingerprint: string;
  level: string;
  category: string;
  component: string;
  route?: string;
  message: string;
  file?: string;
  line?: number;
  source?: string;
  occurrences: number;
  first_seen_at: string;
  last_seen_at: string;
  correlated_execution_id?: string;
  correlated_kind?: string;
  correlation_note?: string;
  status: ErrorGroupStatus;
  incident_id?: string;
}

export const wordpressApi = {
  connectSSHServer: (input: {
    name: string;
    host: string;
    port: number;
    username: string;
    auth_kind: 'ssh_password' | 'ssh_private_key';
    secret: string;
  }) => post<Server>('/api/v1/servers/ssh', input),
  discover: (serverId: string, searchRoot?: string) =>
    post<WordPressDiscovery>(`/api/v1/servers/${serverId}/wordpress/discover`, searchRoot ? { search_root: searchRoot } : {}),
  listSites: (projectId: string) => request<{ sites: WordPressSite[] | null }>(`/api/v1/projects/${projectId}/wordpress/sites`),
  configureSite: (
    projectId: string,
    environmentId: string,
    input: { server_id: string; root_path: string; releases_root?: string; site_url?: string },
  ) => put<WordPressSite>(`/api/v1/projects/${projectId}/environments/${environmentId}/wordpress/site`, input),
  deploymentPlan: (projectId: string, environmentId: string, commit: string) =>
    request<WordPressDeploymentPlanResponse>(
      `/api/v1/projects/${projectId}/environments/${environmentId}/wordpress/deployment-plan?commit=${encodeURIComponent(commit)}`,
    ),
  getSite: (siteId: string) => request<WordPressSite>(`/api/v1/wordpress/sites/${siteId}`),
  inspect: (siteId: string) => post<WordPressSite>(`/api/v1/wordpress/sites/${siteId}/inspect`, {}),
  updateSettings: (siteId: string, settings: WordPressSiteSettings) =>
    put<WordPressSite>(`/api/v1/wordpress/sites/${siteId}/settings`, settings),
  refreshSoftware: (siteId: string) => post<WordPressSoftwareReport>(`/api/v1/wordpress/sites/${siteId}/software/refresh`, {}),
  evaluatePatch: (siteId: string, items: WordPressPatchItem[]) =>
    post<{ decisions: WordPressPatchDecision[] | null }>(`/api/v1/wordpress/sites/${siteId}/patch-runs/evaluate`, { items, approved: false }),
  startPatch: (siteId: string, items: WordPressPatchItem[], approved: boolean) =>
    post<{ execution: ExecutionRun; decisions: WordPressPatchDecision[] | null }>(`/api/v1/wordpress/sites/${siteId}/patch-runs`, { items, approved }),
  flushCaches: (siteId: string) => post<{ flushed: boolean }>(`/api/v1/wordpress/sites/${siteId}/actions/flush-caches`, {}),
  disableMaintenance: (siteId: string) =>
    post<{ maintenance: boolean }>(`/api/v1/wordpress/sites/${siteId}/actions/disable-maintenance`, {}),
  retryScheduledActions: (siteId: string, hook: string) =>
    post<{ requeued: number }>(`/api/v1/wordpress/sites/${siteId}/actions/retry-scheduled-actions`, { hook }),
  listErrorGroups: (projectId: string, environmentId?: string) =>
    request<{ groups: ApplicationErrorGroup[] | null }>(
      `/api/v1/projects/${projectId}/error-groups${environmentId ? `?environment_id=${encodeURIComponent(environmentId)}` : ''}`,
    ),
  setErrorGroupStatus: (groupId: string, status: ErrorGroupStatus) =>
    put<{ status: ErrorGroupStatus }>(`/api/v1/error-groups/${groupId}/status`, { status }),
  /** Creates the product webhooks on the store (or keeps existing ones);
   * each list holds WooCommerce topics such as product.updated. */
  configureWooCommerceWebhooks: (projectId: string) =>
    post<{ created: string[] | null; kept: string[] | null; reactivated?: string[] | null; removed: string[] | null }>(
      `/api/v1/projects/${projectId}/distribution/woocommerce/webhooks`,
      {},
    ),
};

export type StoreMigrationStatus = 'assessing' | 'assessed' | 'blocked' | 'approved' | 'importing' | 'imported' | 'failed';

/** One risk the assessment found; `blocking` stops the migration. */
export interface StoreMigrationRisk {
  level: 'info' | 'warning' | 'blocking' | string;
  code: string;
  message: string;
}

/** One assessment and import of an existing Magento store into an
 * environment. `assessment` and `plan` are the server's free-form records
 * (null until the inspection has run); the fields read from them are
 * optional and checked where used. */
export interface StoreMigration {
  id: string;
  environment_id: string;
  status: StoreMigrationStatus;
  source_path?: string;
  dump_path?: string;
  media_path?: string;
  config_path?: string;
  assessment: StoreMigrationAssessment | null;
  plan: StoreMigrationPlan | null;
  risks: StoreMigrationRisk[] | null;
  replace_existing: boolean;
  approved_by?: string;
  approved_at?: string;
  assessment_execution_id?: string;
  import_execution_id?: string;
  last_error?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface StoreMigrationAssessment {
  source?: { edition?: string; version?: string };
  database?: { sizeBytes?: number; tableCount?: number; isMagento?: boolean; hasSalesOrderTable?: boolean; topology?: { websites?: unknown[] } };
  media?: { files?: number; bytes?: number; archive?: boolean; stripPrefix?: string };
  configPhp?: { provided?: boolean; modules?: number };
}

export interface StoreMigrationPlan {
  steps?: { key: string; title: string }[];
  missing_inputs?: string[];
  required_confirmations?: string[];
  replaces_existing_database?: boolean;
  downtime?: string;
  base_urls?: { unsecure?: string; secure?: string };
}

export interface AssessStoreMigrationInput {
  dump_path?: string;
  media_path?: string;
  config_path?: string;
  source_path?: string;
}

// ---------------------------------------------------------------------------
// ERP integrations (CO-77). Mirrors internal/httpapi/erp.go. No response ever
// carries an ERP credential; a store key appears only in the one response
// that mints it.
// ---------------------------------------------------------------------------

export type ErpSupport = 'supported' | 'limited' | 'unverified' | 'manual' | 'unsupported';
export type ErpConnectionStatus = 'draft' | 'connected' | 'auth_failed' | 'unavailable';
export type ErpStockQuantity = 'on_hand' | 'available';
export type ErpMappingState = 'matched' | 'no_code' | 'unmatched' | 'conflict';
export type ErpIssueSeverity = 'info' | 'warning' | 'blocking';
export type ErpIssueCategory = 'mapping' | 'conflict' | 'configuration' | 'delivery';
export type ErpIssueStatus = 'open' | 'resolved' | 'ignored';

export interface ErpCapability {
  operation: string;
  support: ErpSupport;
  implemented: boolean;
  note: string;
}

export interface ErpProviderInfo {
  provider: string;
  display_name: string;
  company_identifier_label: string;
  company_identifier_hint: string;
  credential_label: string;
  credential_hint: string;
  api_version: string;
  capabilities: ErpCapability[];
  /** The places this ERP can give sale prices from; null from servers before CO-185. */
  price_sources?: ErpPriceSource[] | null;
  settings: ErpProviderSettingField[];
}

export interface ErpProviderSettingField {
  key: string;
  label: string;
  help: string;
  placeholder: string;
  default: string;
}

// How products match, which ERP warehouses feed which store source, and
// whether the ERP's prices include VAT are each store's own settings
// (StoreErpSettings, CO-79), not the connection's.
/**
 * Invoices are sent to the ERP automatically or not at all (CO-179). Preview
 * builds and keeps the documents so they can be read, and sends none.
 */
export type ErpDocumentMode = 'off' | 'preview' | 'live';
export type ErpImportMode = 'staged' | 'automatic';

/** What Commerce Ops does with the store's invoices and their payments (CO-147). */
export interface ErpDocumentSettings {
  mode: ErpDocumentMode;
  /** The first day whose invoices are taken. */
  since?: string;
  company_name: string;
  shipping_article: string;
  default_unit: string;
  due_days: number;
  import_mode: ErpImportMode;
  /** A store payment method code to the ERP treasury account its money goes to; "*" covers the rest. */
  /** null when nothing was ever mapped, from servers before CO-174. Read it through accountsOf. */
  payment_accounts: Record<string, string> | null;
  receipts_automatic: boolean;
  /** Who confirmed, by typing it, the company the documents are made out by. Set only by the server. */
  company_confirmed?: { tax_id: string; confirmed_by: string; confirmed_at: string };
}

/** The payment-method accounts of document settings, {} when none were ever mapped. */
export function accountsOf(settings: Pick<ErpDocumentSettings, 'payment_accounts'>): Record<string, string> {
  return settings.payment_accounts ?? {};
}

/** Where an ERP's sale prices are read from (CO-185). */
export type ErpPriceSource = 'stock_report' | 'article_card';

export interface ErpConnectionSettings {
  stock_quantity: ErpStockQuantity;
  price_currency: string;
  /** The default, the stock report, is what was always done; the article record is opt-in. */
  price_source: ErpPriceSource;
  price_list: string;
  price_change_hold_percent: number;
  documents: ErpDocumentSettings;
  /** The company is a test company of the ERP. */
  test_company?: boolean;
}

export interface ErpSyncStats {
  items_reported: number;
  positions: number;
  warehouses: number;
  items_changed: number;
  prices_changed: number;
  items_missing: number;
  mapping_problems: number;
  stores_updated: number;
  quantities_sent: number;
  prices_sent: number;
  /** Article records read after the stock read, when prices come from them (CO-185). */
  cards_read?: number;
  cards_failed?: number;
  prices_held: number;
  products_sent: number;
  imports_requested: number;
  duration_ms: number;
  credential_rotated: boolean;
}

export interface ErpSyncRun {
  id: string;
  connection_id: string;
  kind: 'stock' | 'test';
  trigger: 'schedule' | 'manual';
  state: 'running' | 'succeeded' | 'failed';
  correlation_id: string;
  stats: ErpSyncStats;
  error_class?: string;
  error_message?: string;
  started_at: string;
  finished_at?: string;
}

export interface StoreLocationMapping {
  erp_warehouse: string;
  location: string;
}

// What the store's extension reports: the store is the only place these
// are set, and Commerce Ops only shows them.
export interface StoreErpSettings {
  enabled: boolean;
  stock_sync: boolean;
  price_sync: boolean;
  price_includes_tax: boolean;
  price_scope: string;
  match_attribute: string;
  inventory_mode: 'multi_source' | 'single';
  locations: StoreLocationMapping[] | null;
  erp_prices_include_tax: boolean | null;
  price_update_mode: 'overwrite' | 'fill';
  create_products: boolean;
}

export interface StoreInstallationView {
  id: string;
  project_id: string;
  environment_id: string;
  name: string;
  platform: string;
  status: 'active' | 'disabled';
  webhook_url: string;
  reported_base_urls: string[];
  platform_version: string;
  module_version: string;
  settings: {
    /** What the store's extension says it can do beyond its first release, such as erp_code_write (CO-155). */
    capabilities?: string[] | null;
    erp: StoreErpSettings;
    websites?: StoreWebsite[];
    /** The WooCommerce plugin's change outbox and read API (CO-137). */
    distribution?: { outbox?: boolean; read_api?: boolean; read_url?: string };
  };
  settings_reported_at?: string;
  last_seen_at?: string;
  acknowledged_sequence: number;
  key_rotated_at?: string;
  created_at: string;
  key_configured: boolean;
  /** A new connection code was issued and the store has not used it yet. */
  previous_key_active: boolean;
  environment_name: string;
  environment_type: string;
  deliveries: { pending: number; dead: number; last_delivered_at?: string };
  latest_sequence: number;
  catalog_products: number;
  /**
   * The store's websites with their ERP links (CO-134). A store that reports
   * no websites (modules before 1.4.0, or one not checked in yet) is one
   * website with code "".
   */
  websites: StoreWebsiteView[];
}

/** One website of a store, as the store reported it. */
export interface StoreWebsite {
  code: string;
  name: string;
  base_urls: string[];
  base_currency: string;
  erp: StoreErpSettings;
}

export interface StoreWebsiteView extends StoreWebsite {
  /** False for a linked website the store no longer reports. */
  reported: boolean;
  connection_id?: string;
  link_id?: string;
  catalog_products: number;
}

export interface ErpConnectionView {
  id: string;
  project_id: string;
  provider: string;
  name: string;
  company_identifier: string;
  settings: ErpConnectionSettings;
  provider_settings: Record<string, unknown>;
  enabled: boolean;
  status: ErpConnectionStatus;
  status_detail: string;
  last_tested_at?: string;
  verified_capabilities: Record<string, string>;
  stock_interval_minutes: number;
  next_stock_sync_at?: string;
  last_stock_sync_at?: string;
  last_stock_sync_success_at?: string;
  last_stock_sync_status: '' | 'running' | 'succeeded' | 'failed';
  last_stock_sync_detail: string;
  consecutive_sync_failures: number;
  created_at: string;
  credential_configured: boolean;
  provider_info: ErpProviderInfo;
  stores: StoreInstallationView[];
  issues: { blocking: number; warning: number; info: number };
  stale: boolean;
  positions: number;
  missing_positions: number;
  warehouses: ErpWarehouse[];
  last_run?: ErpSyncRun;
  /** Whether the operator lets Commerce Ops send documents to this company. */
  documents_live_allowed: boolean;
  /** Whether this Commerce Ops takes the invoices and orders documents are made from. */
  store_data_enabled: boolean;
  /** Documents that need a person or are not moving (the Invoices tab's badge). */
  documents_attention: ErpDocumentAttention;
  /** What sending documents needs and which of it is true now. */
  documents_readiness?: ErpReadinessStep[];
}

export type ErpReadinessId = 'key' | 'test' | 'company' | 'store_data' | 'store' | 'accounts' | 'documents' | 'allowed' | 'sending';

/** done, to do, needs a person, blocked on the operator, or not readable (never done). */
export type ErpReadinessState = 'done' | 'todo' | 'attention' | 'blocked' | 'unknown';

export interface ErpReadinessStep {
  id: ErpReadinessId;
  state: ErpReadinessState;
  /** One short line of evidence. */
  detail?: string;
  at?: string;
}

export interface ErpOverview {
  providers: ErpProviderInfo[];
  connections: ErpConnectionView[];
  stores: StoreInstallationView[];
}

export interface ErpWarehouse {
  code: string;
  name: string;
  articles: number;
}

export interface ErpConnectionTest {
  ok: boolean;
  status: string;
  message: string;
  detail?: string;
  report?: { verified: string[]; warehouses: ErpWarehouse[]; articles: number; message: string };
  capabilities: ErpCapability[];
}

export interface ErpInventoryProduct {
  installation_id: string;
  website_code: string;
  sku: string;
  location: string;
  quantity_sent?: string;
  price_sent?: string;
  held_price?: string;
}

export interface ErpInventoryRow {
  connection_id: string;
  warehouse_code: string;
  warehouse_name: string;
  article_code: string;
  barcode: string;
  name: string;
  unit: string;
  quantity: string;
  sale_price?: string;
  vat_rate?: string;
  sellable?: boolean;
  weight?: string;
  reported_at: string;
  changed_at: string;
  missing_since?: string;
  products: ErpInventoryProduct[];
  /** The price read from the article's own record, when the connection takes prices from it. */
  card_price?: ErpCardPrice;
}

export interface ErpCardPrice {
  state: 'read' | 'not_read' | 'not_found' | 'failed';
  message?: string;
  net?: string;
  gross?: string;
  /** When a read last succeeded. */
  read_at?: string;
}

export interface ErpInventoryView {
  total: number;
  counts: Record<string, number>;
  warehouses: ErpWarehouse[];
  items: ErpInventoryRow[];
}

export interface ErpProductMapping {
  installation_id: string;
  website_code: string;
  sku: string;
  name: string;
  erp_code: string;
  stock_managed: boolean;
  state: ErpMappingState;
  article_code?: string;
  conflicts_with?: string[];
  /** Other ERP-fed websites a conflicting product is also assigned to. */
  other_websites?: string[];
}

/**
 * Why an article is suggested for a product, as facts (CO-170). There is no
 * "sure": an equal code is evidence, and `same_code_loose` says it is equal
 * only once leading zeros and separators are ignored.
 */
export type ErpMatchReason = 'same_code' | 'same_code_loose' | 'barcode_is_sku' | 'sku_in_name' | 'similar_name';
/** Whether the product's name and the article's name describe the same thing. */
export type ErpNameCheck = 'agree' | 'partial' | 'differ' | 'unknown';
/**
 * Whether a pair may be ticked in a batch: selected (ticked), optional
 * (unticked), blocked (cannot be ticked). Absent: reviewed one at a time.
 */
export type ErpBulkState = 'selected' | 'optional' | 'blocked';

/** A product that already carries an ERP article's code. */
export interface ErpArticleHolder {
  sku: string;
  name: string;
}

/** One ERP article as one store website sees it: its quantity is the sum over the warehouses that website maps. */
export interface ErpArticle {
  code: string;
  name: string;
  barcode?: string;
  unit?: string;
  /** Absent when none of the website's warehouses reports the article. */
  quantity?: string;
  price?: string;
  vat_rate?: string;
  sellable?: boolean;
  in_warehouses: boolean;
  /** The ERP no longer reports it in any warehouse the website maps. */
  missing: boolean;
  reported_at: string;
  held_by?: ErpArticleHolder[];
}

export interface ErpArticleView {
  installation_id: string;
  website_code: string;
  /** null: the website has not said whether the ERP's prices include VAT. */
  prices_include_tax: boolean | null;
  total: number;
  items: ErpArticle[];
}

export interface ErpSuggestion {
  article: ErpArticle;
  reasons: ErpMatchReason[];
  name_check: ErpNameCheck;
  bulk?: ErpBulkState;
  /** Another article has the same code or barcode. */
  ambiguous?: boolean;
}

export interface ErpSuggestionView {
  installation_id: string;
  website_code: string;
  sku: string;
  prices_include_tax: boolean | null;
  /** A start for the ERP search box: the product's rarest words the ERP also has. */
  search_hint?: string;
  items: ErpSuggestion[];
}

/** The best suggestion for a product without a match, as a list row shows it. */
export interface ErpSuggestionBrief {
  article_code: string;
  article_name: string;
  reasons: ErpMatchReason[];
  name_check: ErpNameCheck;
  bulk?: ErpBulkState;
}

/** Where a request to save a match in the store stands (CO-155). */
export type ErpPendingState = 'pending' | 'confirmed' | 'rejected' | 'expired';

/** A person's request to write an ERP code into a store product, and what became of it. */
export interface ErpPendingMatch {
  id: string;
  installation_id: string;
  connection_id: string;
  website_code: string;
  sku: string;
  /** Empty asks the store to clear the product's code. */
  requested_code: string;
  previous_code: string;
  state: ErpPendingState;
  /** Why the store refused, as a short code such as value_in_use, or no_answer. */
  reason?: string;
  requested_by: string;
  requested_at: string;
  resolved_at?: string;
}

export interface ErpMatchOutcome {
  /** "requested": the store was asked. "unchanged": already so, or already asked. */
  status: 'requested' | 'unchanged';
  pending?: ErpPendingMatch;
  /** For a move (CO-163): the request to clear the product the article was taken from. */
  released?: ErpPendingMatch;
}

export interface ErpMappingRow extends ErpProductMapping {
  /** The matched article. */
  article?: ErpArticle;
  suggestion?: ErpSuggestionBrief;
  /** A request to save a match that is open, or ended badly recently. */
  pending?: ErpPendingMatch;
}

/** A row of an imported file exactly as it was uploaded; only the two codes decide anything. */
export interface ErpUploadedRow {
  code_ecom: string;
  name_ecom: string;
  code_erp: string;
  name_erp: string;
}

/** One pair in a batch review or an imported file (CO-170). */
export interface ErpMatchRow {
  /** The row's line in the imported file; absent for suggestions. */
  line?: number;
  uploaded?: ErpUploadedRow;
  sku: string;
  /** The store's own name for the product (authoritative, unlike the uploaded one). */
  product_name?: string;
  installation_id: string;
  website_code: string;
  article_code: string;
  /** The ERP's own name for the article. */
  article_name?: string;
  article_barcode?: string;
  /** The product's value in the store now, and the name of that article when the ERP has it. */
  current_code?: string;
  current_article_name?: string;
  /** ready: will be asked. unchanged: already so. replace: would replace a match. error: cannot be saved. */
  status: 'ready' | 'unchanged' | 'replace' | 'error';
  /** Why an error row cannot be saved, as a short code. */
  reason?: string;
  message?: string;
  /** Not problems: already_asked, zero_variants_sku, zero_variants_code, number_sku, number_code. */
  notes?: string[];
  /** The signals behind a suggested article. */
  why?: ErpMatchReason[];
  name_check?: ErpNameCheck;
  bulk?: ErpBulkState;
  ambiguous?: boolean;
}

export interface ErpMatchCounts {
  total: number;
  ready: number;
  unchanged: number;
  replace: number;
  errors: number;
}

/** The pairs proposed for a batch, ticked or not by what the evidence supports. */
export interface ErpMatchReview {
  counts: ErpMatchCounts;
  rows: ErpMatchRow[];
  truncated: boolean;
  /** Products without a match that have no code or barcode behind a suggestion. */
  other_products: number;
}

export interface ErpMatchImportPreview {
  website: { installation_id: string; website_code: string; name: string };
  file: { name: string; format: 'csv' | 'xlsx'; rows: number; extra_columns: string[] };
  counts: ErpMatchCounts;
  /** In file order, each with what saving it would do. */
  rows: ErpMatchRow[];
}

/** A pair a person selected. */
export interface ErpMatchPair {
  sku: string;
  article_code: string;
  /** The person chose to replace the match this product has now. */
  replace?: boolean;
}

export interface ErpMatchApplyRow {
  sku: string;
  article_code: string;
  /** requested: the store was asked. unchanged: already so, or already asked. excluded: a replacement not chosen. failed: not valid now. */
  outcome: 'requested' | 'unchanged' | 'excluded' | 'failed';
  reason?: string;
  message?: string;
  pending_id?: string;
}

export interface ErpMatchApplyResult {
  requested: number;
  unchanged: number;
  excluded: number;
  failed: number;
  events: number;
  /** The requests, for progress and for undoing the batch. */
  pending_ids: string[];
  rows: ErpMatchApplyRow[];
}

export interface ErpUndoRow {
  request_id: string;
  sku: string;
  /** requested: the store was asked to put the previous value back. unchanged: already so. skipped: left alone, with a reason. */
  outcome: 'requested' | 'unchanged' | 'skipped';
  reason?: string;
  message?: string;
  /** The value the store was asked to put back; empty means the code is cleared. */
  restored_code: string;
  pending_id?: string;
}

export interface ErpUndoResult {
  requested: number;
  unchanged: number;
  skipped: number;
  pending_ids: string[];
  rows: ErpUndoRow[];
}

export interface ErpMatchProgress {
  total: number;
  confirmed: number;
  waiting: number;
  rejected: number;
  expired: number;
  done: boolean;
  failures: { sku: string; requested_code: string; reason: string }[];
}

/** Whether a match can be saved in the store from the dashboard, and if not, why. */
export interface ErpSaveState {
  available: boolean;
  reason?: 'extension_cannot' | 'match_field_not_set' | 'matches_by_sku' | 'field_not_writable';
  /** For extension_cannot: the first version that can save a match, and the one the store runs (CO-170). */
  required_version?: string;
  installed_version?: string;
}

export interface ErpMappingView {
  installation_id: string;
  website_code: string;
  match_attribute: string;
  prices_include_tax: boolean | null;
  counts: Record<string, number>;
  /**
   * Products without a match that have a suggestion, by the evidence behind it;
   * absent when there are too many to propose for. with_code have a code or
   * barcode equal to an article's; names_agree are those offered ticked;
   * name_conflicts are not offered for ticking.
   */
  suggestions?: { with_code: number; names_agree: number; name_conflicts: number; name_only: number };
  save: ErpSaveState;
  total: number;
  items: ErpMappingRow[];
}

export interface ErpIssue {
  id: string;
  connection_id: string;
  installation_id?: string;
  website_code: string;
  category: ErpIssueCategory;
  code: string;
  subject_key: string;
  severity: ErpIssueSeverity;
  message: string;
  details: Record<string, unknown>;
  status: ErpIssueStatus;
  occurrences: number;
  first_seen_at: string;
  last_seen_at: string;
  resolved_at?: string;
  resolved_by?: string;
}

export interface StoreSetup {
  installation: StoreInstallationView;
  /** URL, installation id and key in the one value the store takes (CO-82). */
  connection_code: string;
  url: string;
  key: string;
  notice: string;
}

/** What installing the store plugin on a site Commerce Ops reaches did (CO-137). */
export interface StorePluginInstallResult {
  installation: { id: string; name: string };
  version: string;
  /** The plugin signed a request with the new code. */
  connected: boolean;
  /** Why it has not, when it has not. */
  message?: string;
}

export interface StoreWebhookDelivery {
  id: string;
  event_id: string;
  event_type: string;
  sequence: number;
  state: 'pending' | 'leased' | 'delivered' | 'dead';
  attempts: number;
  next_attempt_at: string;
  last_status?: number;
  last_error?: string;
  last_duration_ms?: number;
  delivered_via?: 'webhook' | 'pull';
  delivered_at?: string;
  created_at: string;
}

export interface ErpConnectionPatch {
  name?: string;
  enabled?: boolean;
  stock_interval_minutes?: number;
  stock_quantity?: ErpStockQuantity;
  price_currency?: string;
  price_change_hold_percent?: number;
  price_source?: ErpPriceSource;
  price_list?: string;
  provider_settings?: Record<string, string>;
  documents?: ErpDocumentSettings;
  /** The fiscal code a person types to confirm the company; must be the connection's own. */
  confirm_company?: string;
  test_company?: boolean;
}

export type ErpDocumentKind = 'invoice' | 'receipt';
export type ErpDocumentState =
  | 'blocked'
  | 'waiting'
  | 'ready'
  | 'sent'
  | 'failed'
  | 'refused'
  | 'duplicate'
  | 'skipped'
  /** A send is in flight, claimed by one worker. */
  | 'submitting'
  /** A send may have succeeded and no usable answer came back; a person decides. */
  | 'outcome_unknown';
export type ErpDocumentAction = 'send' | 'skip' | 'rebuild' | 'confirm_sent' | 'resend';

export interface ErpDocumentProblem {
  code: string;
  message: string;
  subject?: string;
}

/** An invoice built from a store's invoice, or the receipt of its payment (CO-147). */
export interface ErpDocument {
  id: string;
  connection_id: string;
  installation_id: string;
  website_code: string;
  kind: ErpDocumentKind;
  record_native_id: string;
  document_number: string;
  document_date?: string;
  currency: string;
  net_total?: string;
  vat_total?: string;
  gross_total?: string;
  state: ErpDocumentState;
  problems: ErpDocumentProblem[];
  waiting_reason?: string;
  message?: string;
  attempts: number;
  next_attempt_at?: string;
  last_attempt_at?: string;
  sent_at?: string;
  decided_by?: string;
  created_at: string;
  updated_at: string;
}

export interface ErpDocumentList {
  documents: ErpDocument[];
  total: number;
  /** "kind/state" to a count, for example "invoice/ready". */
  counts: Record<string, number>;
  live_allowed: boolean;
}

/** One attempt to send a document, newest first in the detail (CO-201). */
export interface ErpDocumentSendAttempt {
  number: number;
  state: ErpDocumentState;
  started_at: string;
  finished_at?: string;
  message?: string;
  /** How a person settled an unknown outcome. */
  resolution?: 'confirmed_sent' | 'not_sent';
  resolved_by?: string;
  resolved_at?: string;
}

export interface ErpDocumentDetail extends ErpDocument {
  /** The ERP's own report on a refused file. */
  report?: string;
  history: ErpDocumentSendAttempt[];
}

/** Documents that need a person or are not moving, per condition (CO-201). */
export interface ErpDocumentAttention {
  blocked: number;
  failed: number;
  stuck: number;
  unknown: number;
  /** Each document once, whatever conditions it is in. */
  documents: number;
  oldest_unknown?: string;
}

export interface ErpDocumentPreviewFile {
  name: string;
  kind: string;
  description: string;
  content: string;
}

/** The file(s) a document would be sent as. Nothing in it was sent or stored (CO-201). */
export interface ErpDocumentPreview {
  kind: ErpDocumentKind;
  record_id: string;
  website_code: string;
  document_number: string;
  external_id: string;
  company_tax_id: string;
  document_date?: string;
  currency?: string;
  net_total?: string;
  vat_total?: string;
  gross_total?: string;
  outcome: 'ready' | 'blocked' | 'waiting' | 'skipped';
  problems: ErpDocumentProblem[];
  waiting_reason?: string;
  skip_reason?: string;
  before_start_day: boolean;
  import_automatic: boolean;
  /** The state of the document a pass already staged for it, if any. */
  staged_state?: ErpDocumentState;
  documents_mode: ErpDocumentMode;
  live_allowed: boolean;
  files: ErpDocumentPreviewFile[];
}

function query(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

export const erpApi = {
  overview: (projectId: string) => request<ErpOverview>(`/api/v1/projects/${projectId}/erp`),
  createConnection: (projectId: string, input: { provider: string; name: string; company_identifier: string }) =>
    post<ErpConnectionView>(`/api/v1/projects/${projectId}/erp/connections`, input),
  connection: (connectionId: string) => request<ErpConnectionView>(`/api/v1/erp/connections/${connectionId}`),
  updateConnection: (connectionId: string, patch: ErpConnectionPatch) =>
    request<ErpConnectionView>(`/api/v1/erp/connections/${connectionId}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteConnection: (connectionId: string) => del<null>(`/api/v1/erp/connections/${connectionId}`),
  setCredential: (connectionId: string, value: string) =>
    put<ErpConnectionView>(`/api/v1/erp/connections/${connectionId}/credential`, { value }),
  testConnection: (connectionId: string) => post<ErpConnectionTest>(`/api/v1/erp/connections/${connectionId}/test`, {}),
  requestSync: (connectionId: string) => post<ErpConnectionView>(`/api/v1/erp/connections/${connectionId}/sync`, {}),
  documents: (
    connectionId: string,
    params: { kind?: ErpDocumentKind; state?: string; q?: string; limit?: number; offset?: number },
  ) => request<ErpDocumentList>(`/api/v1/erp/connections/${connectionId}/documents${query(params)}`),
  document: (connectionId: string, documentId: string) =>
    request<ErpDocumentDetail>(`/api/v1/erp/connections/${connectionId}/documents/${documentId}`),
  actOnDocument: (connectionId: string, documentId: string, action: ErpDocumentAction) =>
    post<ErpDocumentDetail>(`/api/v1/erp/connections/${connectionId}/documents/${documentId}/actions`, { action }),
  /** Builds the file an invoice or receipt would be sent as. Sends and stores nothing. */
  previewDocument: (connectionId: string, input: { kind: ErpDocumentKind; reference: string }) =>
    post<ErpDocumentPreview>(`/api/v1/erp/connections/${connectionId}/documents/preview`, input),
  runs: (connectionId: string, limit = 25) =>
    request<{ runs: ErpSyncRun[] }>(`/api/v1/erp/connections/${connectionId}/runs${query({ limit })}`),
  inventory: (connectionId: string, params: { filter?: string; warehouse?: string; q?: string; limit?: number; offset?: number }) =>
    request<ErpInventoryView>(`/api/v1/erp/connections/${connectionId}/inventory${query(params)}`),
  mappings: (
    connectionId: string,
    params: { installation_id: string; website_code?: string; state?: string; q?: string; limit?: number; offset?: number },
  ) => request<ErpMappingView>(`/api/v1/erp/connections/${connectionId}/mappings${query(params)}`),
  /** Searches the connection's ERP articles as one website sees them (CO-155). */
  articles: (
    connectionId: string,
    params: {
      installation_id: string;
      website_code?: string;
      q?: string;
      availability?: 'free' | 'held';
      for_sku?: string;
      limit?: number;
      offset?: number;
    },
  ) => request<ErpArticleView>(`/api/v1/erp/connections/${connectionId}/articles${query(params)}`),
  /**
   * Asks the store to give a product the code of an ERP article, or to clear
   * it (an empty article_code). The store's attribute stays the source of
   * truth: the answer says the store was asked, and the match shows once the
   * store confirms (CO-155).
   */
  setMatch: (
    connectionId: string,
    body: { installation_id: string; website_code: string; sku: string; article_code: string; move?: boolean },
    idempotencyKey?: string,
  ) =>
    request<ErpMatchOutcome>(`/api/v1/erp/connections/${connectionId}/matches`, {
      method: 'PUT',
      body: JSON.stringify(body),
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
    }),
  /** The pairs proposed for a batch: a code or barcode equal to an article's, with the evidence (CO-170). */
  reviewMatches: (connectionId: string, params: { installation_id: string; website_code?: string }) =>
    request<ErpMatchReview>(`/api/v1/erp/connections/${connectionId}/matches/review${query(params)}`),
  /** Saves the pairs a person selected; a pair that is no longer valid is reported and the rest is sent. */
  applyMatches: (
    connectionId: string,
    body: { installation_id: string; website_code: string; source: 'import' | 'review'; pairs: ErpMatchPair[] },
    idempotencyKey?: string,
  ) =>
    request<ErpMatchApplyResult>(`/api/v1/erp/connections/${connectionId}/matches/apply`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
    }),
  /** Asks the store to put back what the given requests replaced. */
  undoMatches: (connectionId: string, requestIds: string[], idempotencyKey?: string) =>
    request<ErpUndoResult>(`/api/v1/erp/connections/${connectionId}/matches/undo`, {
      method: 'POST',
      body: JSON.stringify({ request_ids: requestIds }),
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
    }),
  /** Where the given requests stand: confirmed, still with the store, refused or unanswered. */
  matchProgress: (connectionId: string, requestIds: string[]) =>
    request<ErpMatchProgress>(`/api/v1/erp/connections/${connectionId}/matches/progress`, {
      method: 'POST',
      body: JSON.stringify({ request_ids: requestIds }),
    }),
  /** Checks a file (CSV or XLSX, code_ecom / name_ecom / code_erp / name_erp) against one website. Nothing is sent. */
  previewMatchImport: (
    connectionId: string,
    body: { installation_id: string; website_code: string; file_name: string; content_base64: string },
  ) =>
    request<ErpMatchImportPreview>(`/api/v1/erp/connections/${connectionId}/matches/import/preview`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** Where one website's products are downloaded from, in the format the import reads: those that need a match (the default) or all of them. */
  matchExportUrl: (connectionId: string, params: { installation_id: string; website_code: string; scope?: 'all'; format: 'xlsx' | 'csv'; delimiter?: 'comma' | 'semicolon' }) =>
    `/api/v1/erp/connections/${connectionId}/matches/export${query(params)}`,
  /** Where the example file is downloaded from. */
  matchExampleUrl: (connectionId: string, params: { format: 'xlsx' | 'csv'; delimiter?: 'comma' | 'semicolon' }) =>
    `/api/v1/erp/connections/${connectionId}/matches/example${query(params)}`,
  /** Writes the rows an import left out, with the reason, as a file to correct and import again. */
  rejectedMatchesFile: (
    connectionId: string,
    body: { format: 'xlsx' | 'csv'; delimiter?: 'comma' | 'semicolon'; rows: (ErpUploadedRow & { reason: string })[] },
  ) => requestFile(`/api/v1/erp/connections/${connectionId}/matches/rejected`, body),
  /** Proposes ERP articles for one product of a website. */
  suggestions: (connectionId: string, params: { installation_id: string; website_code?: string; sku: string }) =>
    request<ErpSuggestionView>(`/api/v1/erp/connections/${connectionId}/mappings/suggestions${query(params)}`),
  issues: (connectionId: string, status = 'open') =>
    request<{ issues: ErpIssue[] }>(`/api/v1/erp/connections/${connectionId}/issues${query({ status })}`),
  actOnIssue: (issueId: string, action: 'accept' | 'ignore' | 'resolve') =>
    post<ErpIssue>(`/api/v1/erp/issues/${issueId}/actions`, { action }),
  /** replace moves a website that takes stock from another connection to this one, in one step (CO-134). */
  linkStore: (connectionId: string, installationId: string, websiteCode: string, replace = false) =>
    post<{ id: string }>(`/api/v1/erp/connections/${connectionId}/links`, {
      installation_id: installationId,
      website_code: websiteCode,
      replace,
    }),
  unlinkStore: (linkId: string) => del<null>(`/api/v1/erp/links/${linkId}`),
  resend: (linkId: string) =>
    post<{ quantities_sent: number; prices_sent: number; prices_held: number }>(`/api/v1/erp/links/${linkId}/resend`, {}),
  organizationStores: () => request<{ stores: StoreInstallationView[] }>(`/api/v1/stores`),
  projectStores: (projectId: string) => request<{ stores: StoreInstallationView[] }>(`/api/v1/projects/${projectId}/stores`),
  /** The store plugin for the project's platform (WooCommerce), as a plain download link (CO-137). */
  storePluginUrl: (projectId: string) => `/api/v1/projects/${projectId}/store-plugin`,
  /** Commerce Ops installs and connects the plugin on the environment's WordPress site (CO-137). */
  installStorePlugin: (projectId: string, input: { environment_id: string; name?: string }) =>
    post<StorePluginInstallResult>(`/api/v1/projects/${projectId}/store-plugin/install`, input),
  createStore: (projectId: string, input: { environment_id: string; name: string }) =>
    post<StoreSetup>(`/api/v1/projects/${projectId}/stores`, input),
  rotateStoreKey: (installationId: string) => post<StoreSetup>(`/api/v1/stores/${installationId}/rotate-key`, {}),
  setStoreEnabled: (installationId: string, enabled: boolean) =>
    request<StoreInstallationView>(`/api/v1/stores/${installationId}`, { method: 'PATCH', body: JSON.stringify({ enabled }) }),
  deleteStore: (installationId: string) => del<null>(`/api/v1/stores/${installationId}`),
  deliveries: (installationId: string, limit = 25) =>
    request<{ deliveries: StoreWebhookDelivery[] }>(`/api/v1/stores/${installationId}/deliveries${query({ limit })}`),
};

// ---- Operational health (CO-95) -------------------------------------------
// One server-side derivation (domain.DeriveEnvironmentHealth) that every
// surface renders; nothing here is recomputed in the browser.

export type HealthLevel = 'healthy' | 'degraded' | 'critical' | 'unknown';
export type ReadinessLevel = 'ok' | 'warning' | 'critical' | 'unknown';

/** Where an operator acts: a project section (with params) or a server page. */
export interface OperationalTarget {
  section: string;
  params?: Record<string, string>;
  fragment?: string;
  server_id?: string;
}

export interface OperationalAction {
  label: string;
  target: OperationalTarget;
}

export interface AttentionSymptom {
  kind: string;
  title: string;
  detail?: string;
  since?: string;
}

export interface OperationalAttentionItem {
  id: string;
  project_id: string;
  environment_id: string;
  kind: string;
  /** Which question it answers: the site, a deployment, the infrastructure… (CO-150). */
  category?: AttentionCategory;
  severity: 'critical' | 'warning';
  title: string;
  summary?: string;
  since?: string;
  since_label?: string;
  cause?: string;
  symptoms?: AttentionSymptom[];
  action?: OperationalAction;
  blocks_deploy?: boolean;
  /** An advisory item someone may dismiss (observation 5). */
  dismissible?: boolean;
  /** The evidence a dismissal is recorded against. */
  fingerprint?: string;
}

/** An advisory item hidden by a dismissal that still matches its evidence. */
export interface DismissedAttentionItem extends OperationalAttentionItem {
  dismissed_by: string;
  dismissed_at: string;
}

export interface AttentionDismissResult {
  dismissed: string[];
  refused: { id: string; reason: 'not_found' | 'not_dismissible' | 'changed'; message: string }[];
}

export interface OperationalReadinessRow {
  key: string;
  label: string;
  level: ReadinessLevel;
  fact: string;
  action?: OperationalAction;
  /** Read from the server for this request, not from the record. */
  live?: boolean;
}

export interface AgentHealth {
  state: 'connected' | 'stale' | 'offline' | 'waiting' | 'ssh' | 'none';
  level: ReadinessLevel;
  server_id?: string;
  server_name?: string;
  last_contact_at?: string;
  fact: string;
}

export interface SetupHealth {
  state: 'not_started' | 'planned' | 'running' | 'failed' | 'complete';
  complete: boolean;
  run_id?: string;
  failed_at?: string;
  error?: string;
  error_code?: string;
  plan_status?: string;
}

export interface BackupHealth {
  state: 'healthy' | 'unverified' | 'failing' | 'stuck' | 'stale' | 'unscheduled' | 'none' | 'running' | 'local_only';
  level: ReadinessLevel;
  headline: string;
  detail: string;
  scheduled: boolean;
  frequency?: string;
  last_success_at?: string;
  last_success_size_bytes?: number;
  last_attempt_at?: string;
  last_attempt_status?: string;
  failures_since_success: number;
  failure_reason?: string;
  failure_cause?: FailureCause;
  failure_cause_title?: string;
  failure_fix?: string;
  in_progress_since?: string;
  restore_test_at?: string;
  restore_test_result?: string;
  /** The last passed restore test is older than 30 days. */
  restore_test_stale?: boolean;
  /** The last successful backup has a copy off its server. */
  last_success_off_host: boolean;
  /** The newest backup that does. */
  last_off_host_at?: string;
  /** Why the last backup could not be stored off-host. */
  upload_error?: string;
}

export interface CertificateHealth {
  domain_id: string;
  hostname: string;
  application_id?: string;
  canonical: boolean;
  state: 'valid' | 'expiring' | 'expired' | 'failed' | 'pending' | 'staging' | 'external' | 'none' | 'unavailable';
  level: ReadinessLevel;
  not_after?: string;
  issuer?: string;
  staging?: boolean;
  detail?: string;
}

export interface DeploymentBrief {
  id: string;
  component_name?: string;
  commit_sha: string;
  branch?: string;
  message?: string;
  status: string;
  failure_reason?: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  /** What happened to the attempt, apart from what serves (CO-150). */
  outcome?: DeploymentOutcome;
  /** Whether its release is the one serving now, when the server knew. */
  serving?: ServingRelation;
}

/** What happened to an attempted release (domain.DeploymentOutcome, CO-150). */
export type DeploymentOutcome =
  | 'queued'
  | 'deploying'
  | 'succeeded'
  | 'succeeded_with_failures'
  | 'failed'
  | 'rolled_back'
  | 'cancelled'
  | 'rehearsal';

/** Whether an attempt's release is what visitors get now (domain.ServingRelation). */
export type ServingRelation = 'serving' | 'superseded' | 'never_activated' | 'rolled_back' | 'pending' | 'unknown';

/** A deployment's outcome and serving relation, as the list and detail endpoints send them. */
export interface DeploymentServingState {
  outcome: DeploymentOutcome;
  serving: ServingRelation;
}

/** The newest attempt of an environment and what it means for what serves. */
export interface DeploymentAttempt {
  deployment: DeploymentBrief;
  outcome: DeploymentOutcome;
  serving: ServingRelation;
  /** A failed or cancelled attempt left the release that served before in place. */
  previous_active: boolean;
  failed_in_a_row?: number;
}

/** The live site's one word (domain.SiteState). */
export type SiteState = 'operational' | 'degraded' | 'unavailable' | 'unknown';

/**
 * The condition of what serves visitors, from the site checks only — never
 * from how the last deployment went (domain.LiveSiteHealth, CO-150).
 */
export interface LiveSiteHealth {
  state: SiteState;
  reason: 'not_set_up' | 'nothing_live' | 'not_monitored' | 'not_reported' | 'stale' | 'checks_passing' | 'checks_warning' | 'checks_failing' | 'maintenance';
  headline: string;
  evidence?: string;
  stale: boolean;
  /** Every site check reported recently and passed: only then may a surface say visitors are unaffected. */
  verified: boolean;
  checks: number;
  passing: number;
  failing: number;
  warning: number;
  not_reported: number;
  delayed: number;
  last_checked_at?: string;
  since?: string;
  failing_checks?: string[];
}

/** What is underneath the site: agent, host, services, the checker. */
export interface InfrastructureHealth {
  level: ReadinessLevel;
  headline: string;
  detail?: string;
  issues: number;
}

/** Which question an attention item answers (domain.AttentionCategory). */
export type AttentionCategory = 'site' | 'deployment' | 'infrastructure' | 'recovery' | 'security' | 'setup' | 'operations';

export interface ServicesHealth {
  total: number;
  reachable: number;
  unreachable: number;
  unknown: number;
  level: ReadinessLevel;
  headline: string;
  detail: string;
}

export interface EnvironmentHealth {
  project_id: string;
  environment_id: string;
  environment_name: string;
  environment_slug: string;
  environment_kind: string;
  level: HealthLevel;
  headline: string;
  readiness: OperationalReadinessRow[];
  attention: OperationalAttentionItem[];
  agent: AgentHealth;
  setup: SetupHealth;
  backups: BackupHealth;
  certificates: CertificateHealth[] | null;
  deployments: { latest?: DeploymentBrief; last_success?: DeploymentBrief; running: number; latest_attempt?: DeploymentAttempt };
  monitoring: { checks: number; failing: number; warning: number; not_reported: number; delayed: number; open_incidents: number };
  services: ServicesHealth;
  /** The live site, from its checks only (CO-150). Absent from servers older than it. */
  site?: LiveSiteHealth;
  infrastructure?: InfrastructureHealth;
  /** What each component serves now, apart from its deployment history (observation 2). */
  components?: ComponentState[];
  /** Advisory items dismissed by someone; they count toward nothing. */
  dismissed?: DismissedAttentionItem[];
  checked_at: string;
}

export type ComponentOutcome = 'live' | 'failed_previous_live' | 'failed_not_live' | 'rolled_back' | 'partial' | 'never_deployed';

/** One component's position in one environment (domain.ComponentState). */
export interface ComponentState {
  application_id: string;
  name: string;
  type: ApplicationType;
  disabled: boolean;
  health: string;
  live?: { release_id: string; since: string; deployment_id?: string; deployment?: DeploymentBrief };
  deploying?: DeploymentBrief;
  latest?: DeploymentBrief;
  outcome: ComponentOutcome;
  /** This component's own site checks (CO-150); "unknown" when none watches it. */
  site?: SiteState;
  failed_in_a_row?: number;
}

export interface LiveInspection {
  checked_at?: string;
  findings: EnvironmentDrift['findings'];
  reclaimable_bytes: number;
  agent_version?: string;
  latest_agent_version?: string;
}

export interface EnvironmentHealthView extends EnvironmentHealth {
  /** Present when the request asked for a live inspection of the server. */
  live?: LiveInspection;
}

export interface ProjectHealthSummary {
  project_id: string;
  name: string;
  slug: string;
  platform: string;
  architecture?: string;
  level: HealthLevel;
  headline: string;
  primary?: EnvironmentHealth;
  environments: { id: string; name: string; slug: string; kind: string; level: HealthLevel }[];
  attention: OperationalAttentionItem[];
  critical: number;
  warnings: number;
}

/** An environment with a component on a server, and its derived level. */
export interface HostedEnvironment {
  project_id: string;
  project_name: string;
  environment_id: string;
  environment_name: string;
  environment_slug: string;
  environment_kind: string;
  /** The attention level (not the site's health). */
  level: HealthLevel;
  /** The environment's live-site state (CO-150). */
  site?: SiteState;
  components: string[];
}

/** A server as the portfolio sees it: its derived agent state and what it hosts. */
export interface ServerHosting {
  server_id: string;
  name: string;
  agent: AgentHealth;
  environments: HostedEnvironment[];
}

export interface OrganizationHealth {
  generated_at: string;
  projects: ProjectHealthSummary[];
  servers: ServerHosting[];
  totals: {
    projects: number;
    critical: number;
    degraded: number;
    healthy: number;
    unknown: number;
    attention_critical: number;
    attention_warning: number;
  };
  queries: number;
}

// ---- Failure interpretation (CO-97) --------------------------------------------

export type FailureCause = 'code' | 'dependency' | 'database' | 'server_permission' | 'infrastructure' | 'integration' | 'commerce_ops' | 'unknown';
export type RetryAdvice = 'helps' | 'after_fix' | 'wont_help' | 'unknown' | 'not_applicable';

export interface FailureAction {
  label: string;
  detail?: string;
  target?: OperationalTarget;
  /** A command to run on the server, shown to copy — never run by the UI. */
  command?: string;
}

/** What a failed run means: live now, what else changed, cause, what to do. */
export interface FailureInterpretation {
  kind: string;
  headline: string;
  live_now: string;
  also_changed?: string;
  cause: FailureCause;
  cause_title: string;
  cause_detail?: string;
  retry: RetryAdvice;
  retry_why?: string;
  actions: FailureAction[];
  failed_step?: { key: string; title: string; sequence: number };
  /** "likely": a known error pattern matched — an inference, not a proven root cause (CO-150). */
  confidence?: 'likely' | 'unknown';
  /** The output lines the inference rests on. */
  evidence?: string[];
  checkpoint_id?: string;
  checkpoint_at?: string;
  raw_error?: string;
  /** Kind "rollback": a completed deployment a person rolled back later. */
  outcome?: string;
  rollback?: RollbackSummary;
}

/** A rollback a person asked for: who, when, why and what serves after it. */
export interface RollbackSummary {
  initiated_by: string;
  at: string;
  reason?: string;
  /** What serves after the rollback, in words ("release … (commit …)"). */
  to: string;
  to_release?: { id: string; tag?: string; commit_sha?: string };
  execution_id: string;
}

// ---- Deployment confidence (CO-98) ---------------------------------------------

/** Which credential reads the project's repository, by the rules a checkout uses. */
export interface RepositoryAccess {
  source: 'project_connection' | 'github_app' | 'none' | 'no_repository';
  provider?: string;
  repository_url?: string;
  account?: string;
  summary: string;
  /** A credential that exists but does not apply to this repository. */
  other?: string;
  /** Every component repository, resolved by the same rules. */
  components?: ComponentRepositoryAccess[];
  /** A credential reads every repository the project deploys from; a project token is then optional. */
  readable?: boolean;
}

export interface ComponentRepositoryAccess {
  application_id: string;
  application_name: string;
  repository_url: string;
  source: RepositoryAccess['source'];
  summary: string;
}

/** One running (or just finished) deployment for the progress toast (observation 3). */
export interface ActiveDeployment {
  deployment_id: string;
  project_id: string;
  project_name: string;
  environment_id: string;
  environment_name: string;
  environment_slug: string;
  component_name: string;
  repository_url?: string;
  commit_sha: string;
  commit_message?: string;
  branch?: string;
  status: DeploymentWorkflowStatus;
  finished: boolean;
  failure_reason?: string;
  rehearsal: boolean;
  created_by: string;
  started_at?: string;
  completed_at?: string;
  progress: {
    percent: number;
    basis: 'steps' | 'previous_run';
    done: number;
    total: number;
    current_step_key?: string;
    current_step_title?: string;
    current_step_index?: number;
    /** The stage the run is in (at most 14 per run). */
    current_stage_title?: string;
    current_stage_index?: number;
    stage_total?: number;
  };
}

export interface CommitSummary {
  sha: string;
  message: string;
  author?: string;
  date?: string;
}

/** What deploying a commit changes compared with the live release. */
export interface ChangesSinceLive {
  application_id: string;
  state: 'compared' | 'same_commit' | 'no_live_release' | 'not_available';
  live_commit?: string;
  live_release_tag?: string;
  target_commit: string;
  comparison?: { status: 'ahead' | 'identical' | 'behind' | 'diverged'; ahead_by: number; behind_by: number; commits: CommitSummary[] };
  reason?: string;
}

// ---- WordPress recovery (CO-99) --------------------------------------------------

/** A verified database export taken before a WordPress update or deployment. */
export interface WordPressCheckpoint {
  execution_id: string;
  reason: string;
  taken_at: string;
  path: string;
  bytes: number;
  tables: number;
  sha256: string;
  complete: boolean;
  /** The manual restore, in order, run on the server. */
  restore_commands: string[];
}

export interface WordPressRecoveryView {
  checkpoints: WordPressCheckpoint[];
  restore_is_manual: boolean;
  explanation: string;
  site_root?: string;
}

// ---- Magento operations, managed path (CO-101) ----------------------------------

export interface MagentoOperationCheck {
  monitor_id: string;
  name: string;
  status: string;
  last_run_at?: string;
  failing_since?: string;
  consecutive_failures: number;
}

export interface MagentoOperationsView {
  /** False for Magento reached over SSH; `reason` says where its operations are. */
  available: boolean;
  reason?: string;
  indexers?: {
    checked_at: string;
    known: boolean;
    valid: boolean;
    invalid: string[];
    statuses?: Record<string, string>;
    reason?: string;
  };
  cron: MagentoOperationCheck[];
  consumers: MagentoOperationCheck[];
}

/**
 * The Commerce Ops edge of a server: one OpenResty serving ports 80 and 443
 * for every Commerce Ops environment placed there. What it should route is
 * rendered from Commerce Ops' state; `live` is what the agent read on the
 * server (null when it could not, `live_error` says why).
 */
export type EdgeMode = 'edge' | 'per_environment' | 'unused';

export interface EdgeEnvironment {
  environment_id: string;
  project_id: string;
  project: string;
  environment: string;
  compose_project: string;
  fragment: string;
  routes: number;
}

export interface EdgeRoute {
  hostname: string;
  environment_id: string;
  project_id: string;
  environment: string;
  project: string;
  destination: 'magento' | 'frontend' | 'redirect';
  component?: string;
  redirect_to?: string;
  tls: boolean;
}

export interface EdgeLive {
  installed: boolean;
  running: boolean;
  fragments: string[];
  legacy_gates: string[];
  stale: string[];
  missing: string[];
  checked_at: string;
}

export interface ServerEdge {
  server_id: string;
  mode: EdgeMode;
  agent_supports_edge: boolean;
  container: string;
  environments: EdgeEnvironment[];
  routes: EdgeRoute[];
  unread_fragments: string[];
  live: EdgeLive | null;
  live_error?: string;
}

export interface EdgeApplyResult {
  action: 'reloaded' | 'started' | 'removed' | 'absent';
  fragments: string[];
  removed_fragments: string[];
  took_over: string[];
}

// Varnish full page cache (CO-127). The VCL always comes from the
// release's own Magento (bin/magento varnish:vcl:generate).
export type VarnishState =
  | 'not_applicable'
  | 'not_planned'
  | 'planned'
  | 'not_provisioned_here'
  | 'waiting_for_agent'
  | 'awaiting_first_release'
  | 'active';

export type VarnishOperation = 'reload' | 'restart' | 'verify';

export interface VarnishVCLEvent {
  execution_id: string;
  workflow_type: string;
  step_key: string;
  status: string;
  at?: string;
  message?: string;
  result?: Record<string, unknown>;
}

/** The Varnish reading (monitor last result or a live reading). */
export interface VarnishReading {
  status?: string;
  message?: string;
  latencyMs?: number;
  metrics?: {
    running?: boolean;
    cache_hits?: number;
    cache_misses?: number;
    hit_ratio?: number;
    objects?: number;
    lru_nuked?: number;
    storage_used_bytes?: number;
    storage_free_bytes?: number;
    storage_used_ratio?: number;
    backend_failures?: number;
    backend_healthy?: boolean;
    vcl_failures?: number;
    active_vcl?: string;
    vcl_matches_release?: boolean;
    origin_reachable?: boolean;
    latency_ms?: number;
    uptime_seconds?: number;
    [key: string]: unknown;
  };
}

export interface EnvironmentVarnish {
  environment_id: string;
  applies: boolean;
  state: VarnishState;
  reason?: string;
  resource_id?: string;
  image?: string;
  version?: string;
  version_basis?: string;
  storage_mb?: number;
  backend?: string;
  service?: string;
  listen_port?: number;
  grace_seconds?: number;
  vcl_source?: string;
  active_release?: string;
  magento_version?: string;
  last_vcl?: VarnishVCLEvent;
  last_vcl_failure?: VarnishVCLEvent;
  last_verification?: VarnishVCLEvent;
  monitor?: { id: string; name: string; status: string; last_run_at?: string; last_result?: { status?: string; message?: string; metrics?: VarnishReading['metrics']; latency_ms?: number; checked_at?: string } };
  live?: VarnishReading;
}

// ---------------------------------------------------------------------------
// Blue-green runtimes (docs/BLUE_GREEN_RUNTIME.md)
// ---------------------------------------------------------------------------

export type RuntimeStrategy = 'recreate' | 'blue_green';
export type RuntimeServing = 'serving' | 'degraded' | 'unavailable' | 'protected' | 'manual_recovery' | 'not_routed' | 'none';

/** A component's current serving health: its own checks and the active runtime's state, never the route record alone. */
export interface RuntimeServingHealth {
  status: 'healthy' | 'warning' | 'critical' | 'unknown';
  reasons: string[];
  checked_at?: string | null;
  monitors: number;
}

export interface RuntimeContainer {
  role: string;
  name: string;
  id?: string;
  image?: string;
  image_digest?: string;
  restart_policy?: string;
}

export interface ComponentRuntime {
  id: string;
  application_id: string;
  kind: 'magento' | 'frontend' | 'wordpress';
  slot: 'a' | 'b';
  role: 'candidate' | 'active' | 'previous' | 'retired' | 'rejected';
  state: string;
  release_id?: string | null;
  release_tag: string;
  commit_sha: string;
  artifact_path: string;
  image?: string;
  image_digest?: string;
  containers: RuntimeContainer[];
  endpoint: { host: string; port: number; network?: string };
  route_generation: number;
  worker_owner: boolean;
  shared_state?: { class: string; operations: { kind: string; subject: string; detail?: string }[]; unknowns?: string[]; reasons: string[] } | null;
  last_error?: string;
  created_at: string;
  ready_at?: string | null;
  promoted_at?: string | null;
  drain_started_at?: string | null;
  drained_at?: string | null;
}

export interface RuntimePhaseEvent {
  id: string;
  phase: string;
  outcome: string;
  started_at: string;
  completed_at?: string | null;
  duration_ms?: number | null;
  detail?: Record<string, unknown>;
}

/** A lifecycle phase measured from the boundaries of the steps that ran in it. */
export interface RuntimePhaseSummary {
  phase: string;
  started_at: string;
  completed_at?: string | null;
  duration_seconds: number;
  outcome: 'succeeded' | 'warning' | 'failed' | string;
  steps: RuntimePhaseEvent[];
}

export interface ComponentRuntimeView {
  application_id: string;
  name: string;
  type: string;
  kind: 'magento' | 'frontend' | 'wordpress';
  serving: RuntimeServing;
  active?: ComponentRuntime | null;
  candidate?: ComponentRuntime | null;
  previous?: ComponentRuntime | null;
  recent: ComponentRuntime[];
  route: { desired_generation: number; observed_generation: number; pending_generation?: number; pending_runtime_id?: string | null };
  protection: { state: '' | 'active' | 'retained'; reason?: string; since?: string | null; duration_seconds?: number };
  workers: { state: 'granted' | 'fenced'; owner_runtime_id?: string | null; epoch: number; reason?: string };
  rollback: { allowed: boolean; reasons?: string[]; start_previous: boolean };
  /** Whether a failed deployment of this component is switched back by itself: the environment's policy and a routed runtime. */
  automatic_rollback?: { applies: boolean; reasons: string[] };
  timings: { execution_id?: string; total_seconds?: number; protected_seconds: number; phases: RuntimePhaseEvent[]; step_phases?: RuntimePhaseSummary[]; observed_request_failures_note: string };
  health?: RuntimeServingHealth;
  warnings: string[];
  /** A WordPress serving release's owed object-cache settlement (its old runtime was not confirmed stopped, or the flush failed). */
  cache_settlement?: { pending: boolean; reason: string } | null;
}

export type CatalogSearchPolicy = 'warn' | 'block';

export interface EnvironmentRuntimeSettings {
  strategy: RuntimeStrategy;
  drain_bound_seconds: number;
  drain_policy: 'keep_running' | 'stop_after_bound';
  asset_retention_hours: number;
  wordpress_managed_immutable: boolean;
  /** Magento releases that declare an additive schema change backward compatible are applied without the protected window. */
  allow_declared_compatible: boolean;
  /** The Magento runtime mounts the release read-only (CO-195); blue-green only. */
  read_only_code?: boolean;
  /** Storefront installs, builds and Node runtimes run as the runtime user (CO-195); blue-green only. */
  non_root_storefronts?: boolean;
  /** What a failed candidate catalog-search check does; warn when absent. */
  catalog_search_policy?: CatalogSearchPolicy;
  stored: boolean;
  updated_by?: string;
  updated_at?: string;
}

/** A new environment's runtime strategy decision (blue-green when its server can run it). */
export interface RuntimeDefault {
  state: 'pending' | 'applied' | 'declined' | 'declined_by_operator';
  reason?: string;
  trigger?: 'environment_setup' | 'first_deployment';
  marked_at?: string;
  decided_at?: string;
  agent_version?: string;
  missing?: string[];
}

/**
 * What stops blue-green runtimes on an environment, as data: a code and the
 * items it concerns (agent commands, the ingress in use, storefront names).
 * The eligibility is the one every blue-green deployment plan judges.
 */
export type EligibilityBlockerCode =
  | 'agent_not_connected'
  | 'agent_commands_missing'
  | 'ingress_not_commerce_ops'
  | 'frontend_backend_probe_missing'
  | 'stack_unreadable';

export interface EligibilityBlocker {
  code: EligibilityBlockerCode | string;
  items?: string[];
}

export interface EnvironmentRuntimeView {
  settings: EnvironmentRuntimeSettings;
  /** What a failed deployment does in this environment, from its next plan on. */
  rollback_policy?: RollbackPolicy;
  capability: { supported: boolean; missing?: string[]; reason?: string; blockers?: EligibilityBlocker[] };
  components: ComponentRuntimeView[];
  /** Present for an environment created with the new-environment default. */
  default?: RuntimeDefault;
}

export interface RuntimeAdoptionPreview {
  components: { application_id: string; name: string; kind: string; active_release?: string; replaced_services: string[]; steps?: string[]; supported: boolean; reason?: string; blockers?: EligibilityBlocker[] }[];
  interruption: string[];
  notes: string[];
}

export interface RuntimeReconciliation {
  observed_at: string;
  components: { application_id: string; recorded_generation: number; serving_generation: number; serving_runtime_id?: string; drift: string[] }[];
}

export interface SetEnvironmentRuntimeInput {
  strategy?: RuntimeStrategy;
  drain_bound_seconds?: number;
  drain_policy?: 'keep_running' | 'stop_after_bound';
  asset_retention_hours?: number;
  wordpress_managed_immutable?: boolean;
  confirm_adoption?: boolean;
  /** Only an owner can change it; omitted leaves it unchanged. */
  allow_declared_compatible?: boolean;
  /** The runtime hardening switches (CO-195): owner only, omitted leaves them unchanged. */
  read_only_code?: boolean;
  non_root_storefronts?: boolean;
  /** Owner only; omitted leaves it unchanged. */
  catalog_search_policy?: CatalogSearchPolicy;
}

export async function getEnvironmentRuntime(environmentId: string): Promise<{ runtime: EnvironmentRuntimeView }> {
  return request<{ runtime: EnvironmentRuntimeView }>(`/api/v1/environments/${environmentId}/runtime`);
}

export async function setEnvironmentRuntime(environmentId: string, input: SetEnvironmentRuntimeInput): Promise<{ runtime: EnvironmentRuntimeView }> {
  return put<{ runtime: EnvironmentRuntimeView }>(`/api/v1/environments/${environmentId}/runtime`, input);
}

export async function setEnvironmentRollbackPolicy(environmentId: string, rollbackPolicy: RollbackPolicy): Promise<{ rollback_policy: RollbackPolicy; runtime?: EnvironmentRuntimeView }> {
  return put<{ rollback_policy: RollbackPolicy; runtime?: EnvironmentRuntimeView }>(`/api/v1/environments/${environmentId}/rollback-policy`, { rollback_policy: rollbackPolicy });
}

export async function previewRuntimeAdoption(environmentId: string): Promise<{ preview: RuntimeAdoptionPreview }> {
  return request<{ preview: RuntimeAdoptionPreview }>(`/api/v1/environments/${environmentId}/runtime/adoption-preview`);
}

export async function reconcileEnvironmentRuntime(environmentId: string): Promise<{ reconciliation: RuntimeReconciliation }> {
  return post<{ reconciliation: RuntimeReconciliation }>(`/api/v1/environments/${environmentId}/runtime/reconcile`, {});
}

export interface RecoverComponentInput {
  resolution: 'checkpoint_restored' | 'database_verified';
  note?: string;
  confirm: boolean;
}

/** Retries a pending WordPress object-cache settlement: old runtimes are stopped first; route, workers and database are untouched. */
export async function settleWordPressObjectCache(environmentId: string, applicationId: string): Promise<{ execution_id: string }> {
  return post<{ execution_id: string }>(`/api/v1/environments/${environmentId}/runtime/wordpress/${applicationId}/cache-settlement`, {});
}

export async function recoverComponentProtection(environmentId: string, applicationId: string, input: RecoverComponentInput): Promise<{ execution_id: string }> {
  return post<{ execution_id: string }>(`/api/v1/environments/${environmentId}/runtime/components/${applicationId}/recover`, input);
}
